import React, { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import * as LucideIcons from 'lucide-react';
import { DIMENSION_CONSTRAINTS, getColorById } from '../data/mockData';
import ColorPicker from './ColorPicker';
import { useTranslation } from '../context/TranslationContext';
import { getColorByRAL } from '../data/ralColors';
import { getDimensionInputWidth } from '../lib/utils';
import { ensurePartWidthsSum } from '../lib/partWidthDistribution';
import {
  buildPartBandSectionLayouts,
  clampPartBandSectionCount,
  getPartBandSectionConfig,
} from '../lib/partSectionConfig';
import {
  FRAME_EDGE_STROKE_DARK,
  FRAME_EDGE_STROKE_DEFAULT,
  isLightFrameColor,
  shouldUsePrintableFrameEdges,
} from '../lib/configuratorColors';

const Kozijnen = ({ 
  width, 
  height, 
  windowType, 
  color, 
  interiorFrameColor,
  glassTexture,
  glazingType,
  parts,
  partWindowTypes,
  woodJoint = 'joint-45',
  overhead = 'no-overhead',
  overheadParts = 1,
  overheadHeight = 300,
  overheadPartWindowTypes,
  overheadPartWidths,
  underlight = 'no-underlight',
  underlightParts = 1,
  underlightHeight = 400,
  underlightPartWindowTypes,
  underlightPartWidths,
  raamtypes,
  onPartWindowTypeChange,
  onOverheadPartWindowTypeChange,
  onUnderlightPartWindowTypeChange,
  partWidths,
  onPartWidthChange,
  onOverheadPartWidthChange,
  onUnderlightPartWidthChange,
  onOverheadHeightChange,
  onUnderlightHeightChange,
  onWidthChange,
  onHeightChange,
  panelColors: propPanelColors,
  panelOverheadColors: propPanelOverheadColors,
  panelUnderlightColors: propPanelUnderlightColors,
  panelLineTypes: propPanelLineTypes,
  panelOverheadLineTypes: propPanelOverheadLineTypes,
  panelUnderlightLineTypes: propPanelUnderlightLineTypes,
  onPanelColorChange,
  onPanelOverheadColorChange,
  onPanelUnderlightColorChange,
  onPanelLineTypeChange,
  onPanelOverheadLineTypeChange,
  onPanelUnderlightLineTypeChange,
  rampDorpel = 'no-ramp-dorpel', // 'no-ramp-dorpel', 'ramp-dorpel-30mm', 'ramp-dorpel-45mm', 'ramp-dorpel-60mm'
  onRampDorpelChange,
  ventilatorParts: ventilatorPartsProp,
  ventilatorOverheadParts: ventilatorOverheadPartsProp,
  ventilatorUnderlightParts: ventilatorUnderlightPartsProp,
  onVentilatorPartsChange,
  onVentilatorOverheadPartsChange,
  onVentilatorUnderlightPartsChange,
  overheadPerPart: overheadPerPartProp,
  onOverheadPerPartChange,
  underlightPerPart: underlightPerPartProp,
  onUnderlightPerPartChange,
  partUnderlightWindowTypes: partUnderlightWindowTypesProp,
  onPartUnderlightWindowTypeChange,
  partOverheadWindowTypes: partOverheadWindowTypesProp,
  onPartOverheadWindowTypeChange,
  partOverheadSections: partOverheadSectionsProp,
  partUnderlightSections: partUnderlightSectionsProp,
  partOverheadSectionWindowTypes: partOverheadSectionWindowTypesProp,
  partUnderlightSectionWindowTypes: partUnderlightSectionWindowTypesProp,
  partOverheadSectionWidths: partOverheadSectionWidthsProp,
  partUnderlightSectionWidths: partUnderlightSectionWidthsProp,
  onPartOverheadSectionCountChange,
  onPartUnderlightSectionCountChange,
  onPartOverheadSectionWindowTypeChange,
  onPartUnderlightSectionWindowTypeChange,
  onPartOverheadSectionWidthChange,
  onPartUnderlightSectionWidthChange,
  screenElectricallyOperated = false,
  rollerShutterElectricallyOperated = false,
  screenColor = '7016', // Default to RAL 7016
  rollerShutterColor = '7016' // Default to RAL 7016
}) => {
  const { t } = useTranslation();
  
  // Helper function to translate window type names
  const translateWindowTypeName = (windowTypeId) => {
    const translationMap = {
      'fixed-sash': t('options.fixedSash'),
      'fixed-glass': t('options.fixedGlass'),
      'tilt-turn-left': t('options.tiltTurnLeft'),
      'tilt-turn-right': t('options.tiltTurnRight'),
      'tilt-only': t('options.tiltOnly'),
      'fixed-panel': t('options.fixedPanel')
    };
    return translationMap[windowTypeId] || windowTypeId;
  };
  
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inputPartWidths, setInputPartWidths] = useState([]);
  const [inputOverheadPartWidths, setInputOverheadPartWidths] = useState([]);
  const [inputUnderlightPartWidths, setInputUnderlightPartWidths] = useState([]);
  const [hoveredPartIndex, setHoveredPartIndex] = useState(null);
  const [selectedPartIndex, setSelectedPartIndex] = useState(null);
  const [hoveredOverheadPartIndex, setHoveredOverheadPartIndex] = useState(null);
  const [selectedOverheadPartIndex, setSelectedOverheadPartIndex] = useState(null);
  const [hoveredUnderlightPartIndex, setHoveredUnderlightPartIndex] = useState(null);
  const [selectedUnderlightPartIndex, setSelectedUnderlightPartIndex] = useState(null);
  const [isOverheadPart, setIsOverheadPart] = useState(false);
  const [isUnderlightPart, setIsUnderlightPart] = useState(false);
  const [dialogType, setDialogType] = useState(null); // 'main' | 'overhead' | 'underlight' | 'part-overhead' | 'part-underlight'
  const [selectedPartOverheadIndex, setSelectedPartOverheadIndex] = useState(null);
  const [selectedPartOverheadSubIndex, setSelectedPartOverheadSubIndex] = useState(0);
  const [hoveredPartOverheadIndex, setHoveredPartOverheadIndex] = useState(null);
  const [hoveredPartOverheadSubIndex, setHoveredPartOverheadSubIndex] = useState(null);
  const [selectedPartUnderlightIndex, setSelectedPartUnderlightIndex] = useState(null);
  const [selectedPartUnderlightSubIndex, setSelectedPartUnderlightSubIndex] = useState(0);
  const [hoveredPartUnderlightIndex, setHoveredPartUnderlightIndex] = useState(null);
  const [hoveredPartUnderlightSubIndex, setHoveredPartUnderlightSubIndex] = useState(null);
  const [partOverheadWindowTypesLocal, setPartOverheadWindowTypesLocal] = useState([]);
  const [partUnderlightWindowTypesLocal, setPartUnderlightWindowTypesLocal] = useState([]);
  const [ventilatorPartOverheadLocal, setVentilatorPartOverheadLocal] = useState([]);
  const [ventilatorPartUnderlightLocal, setVentilatorPartUnderlightLocal] = useState([]);
  const [widthInput, setWidthInput] = useState(String(width));
  const [heightInput, setHeightInput] = useState(String(height));
  const [overheadHeightInput, setOverheadHeightInput] = useState(String(overheadHeight));
  const [ventilatorPartsLocal, setVentilatorPartsLocal] = useState([]);
  const [ventilatorOverheadPartsLocal, setVentilatorOverheadPartsLocal] = useState([]);
  const [ventilatorUnderlightPartsLocal, setVentilatorUnderlightPartsLocal] = useState([]);
  const [overheadPerPartLocal, setOverheadPerPartLocal] = useState([]);
  const [underlightPerPartLocal, setUnderlightPerPartLocal] = useState([]);
  const ventilatorParts = (ventilatorPartsProp != null && Array.isArray(ventilatorPartsProp)) ? ventilatorPartsProp : ventilatorPartsLocal;
  const ventilatorOverheadParts = (ventilatorOverheadPartsProp != null && Array.isArray(ventilatorOverheadPartsProp)) ? ventilatorOverheadPartsProp : ventilatorOverheadPartsLocal;
  const ventilatorUnderlightParts = (ventilatorUnderlightPartsProp != null && Array.isArray(ventilatorUnderlightPartsProp)) ? ventilatorUnderlightPartsProp : ventilatorUnderlightPartsLocal;
  const overheadPerPart = (overheadPerPartProp != null && Array.isArray(overheadPerPartProp)) ? overheadPerPartProp : overheadPerPartLocal;
  const underlightPerPart = (underlightPerPartProp != null && Array.isArray(underlightPerPartProp)) ? underlightPerPartProp : underlightPerPartLocal;
  const partOverheadWindowTypes = Array.from({ length: Math.max(parts, 0) }, (_, i) => {
    if (partOverheadWindowTypesProp != null && Array.isArray(partOverheadWindowTypesProp)) {
      return partOverheadWindowTypesProp[i] ?? 'fixed-sash';
    }
    return partOverheadWindowTypesLocal[i] ?? partWindowTypes?.[i] ?? 'fixed-sash';
  });
  const partUnderlightWindowTypes = Array.from({ length: Math.max(parts, 0) }, (_, i) => {
    if (partUnderlightWindowTypesProp != null && Array.isArray(partUnderlightWindowTypesProp)) {
      return partUnderlightWindowTypesProp[i] ?? 'fixed-sash';
    }
    return partUnderlightWindowTypesLocal[i] ?? partWindowTypes?.[i] ?? 'fixed-sash';
  });
  const ventilatorPartOverhead = Array.from({ length: Math.max(parts, 0) }, (_, i) =>
    ventilatorPartOverheadLocal[i] ?? ventilatorParts[i] ?? false
  );
  const ventilatorPartUnderlight = Array.from({ length: Math.max(parts, 0) }, (_, i) =>
    ventilatorPartUnderlightLocal[i] ?? ventilatorParts[i] ?? false
  );
  const setPartOverheadWindowType = (index, value) => {
    if (onPartOverheadWindowTypeChange) {
      onPartOverheadWindowTypeChange(index, value);
      return;
    }
    setPartOverheadWindowTypesLocal((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };
  const setPartUnderlightWindowType = (index, value) => {
    if (onPartUnderlightWindowTypeChange) {
      onPartUnderlightWindowTypeChange(index, value);
      return;
    }
    setPartUnderlightWindowTypesLocal((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };
  const setPartOverheadSectionWindowType = (partIndex, sectionIndex, value) => {
    if (onPartOverheadSectionWindowTypeChange) {
      onPartOverheadSectionWindowTypeChange(partIndex, sectionIndex, value);
      return;
    }
    if (sectionIndex === 0) setPartOverheadWindowType(partIndex, value);
  };
  const setPartUnderlightSectionWindowType = (partIndex, sectionIndex, value) => {
    if (onPartUnderlightSectionWindowTypeChange) {
      onPartUnderlightSectionWindowTypeChange(partIndex, sectionIndex, value);
      return;
    }
    if (sectionIndex === 0) setPartUnderlightWindowType(partIndex, value);
  };

  const getNormalizedUnderlightPartWindowTypes = (count) =>
    Array.from({ length: count }, (_, i) => underlightPartWindowTypes?.[i] ?? 'fixed-sash');
  const setVentilatorPartOverhead = (index, value) => {
    setVentilatorPartOverheadLocal((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };
  const setVentilatorPartUnderlight = (index, value) => {
    setVentilatorPartUnderlightLocal((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };
  const setVentilatorParts = (arg) => {
    const next = typeof arg === 'function' ? arg(ventilatorParts) : arg;
    if (onVentilatorPartsChange) onVentilatorPartsChange(Array.isArray(next) ? next : ventilatorParts);
    else setVentilatorPartsLocal(next);
  };
  const setVentilatorOverheadParts = (arg) => {
    const next = typeof arg === 'function' ? arg(ventilatorOverheadParts) : arg;
    if (onVentilatorOverheadPartsChange) onVentilatorOverheadPartsChange(Array.isArray(next) ? next : ventilatorOverheadParts);
    else setVentilatorOverheadPartsLocal(next);
  };
  const setVentilatorUnderlightParts = (arg) => {
    const next = typeof arg === 'function' ? arg(ventilatorUnderlightParts) : arg;
    if (onVentilatorUnderlightPartsChange) onVentilatorUnderlightPartsChange(Array.isArray(next) ? next : ventilatorUnderlightParts);
    else setVentilatorUnderlightPartsLocal(next);
  };
  const setOverheadPerPart = (arg) => {
    const next = typeof arg === 'function' ? arg(overheadPerPart) : arg;
    if (onOverheadPerPartChange) onOverheadPerPartChange(Array.isArray(next) ? next : overheadPerPart);
    else setOverheadPerPartLocal(next);
  };
  const setUnderlightPerPart = (arg) => {
    const next = typeof arg === 'function' ? arg(underlightPerPart) : arg;
    if (onUnderlightPerPartChange) onUnderlightPerPartChange(Array.isArray(next) ? next : underlightPerPart);
    else setUnderlightPerPartLocal(next);
  };
  const [underlightHeightInput, setUnderlightHeightInput] = useState(String(underlightHeight));
  // Panel colors for main parts, overhead parts, and underlight parts
  // Use props if provided, otherwise use local state
  const [localPanelColors, setLocalPanelColors] = useState([]);
  const [localPanelOverheadColors, setLocalPanelOverheadColors] = useState([]);
  const [localPanelUnderlightColors, setLocalPanelUnderlightColors] = useState([]);
  // Panel line types: 'none', 'horizontal', 'vertical'
  const [localPanelLineTypes, setLocalPanelLineTypes] = useState([]);
  const [localPanelOverheadLineTypes, setLocalPanelOverheadLineTypes] = useState([]);
  const [localPanelUnderlightLineTypes, setLocalPanelUnderlightLineTypes] = useState([]);
  
  // Use props if provided, otherwise fall back to local state
  const panelColors = propPanelColors !== undefined ? propPanelColors : localPanelColors;
  const panelOverheadColors = propPanelOverheadColors !== undefined ? propPanelOverheadColors : localPanelOverheadColors;
  const panelUnderlightColors = propPanelUnderlightColors !== undefined ? propPanelUnderlightColors : localPanelUnderlightColors;
  const panelLineTypes = propPanelLineTypes !== undefined ? propPanelLineTypes : localPanelLineTypes;
  const panelOverheadLineTypes = propPanelOverheadLineTypes !== undefined ? propPanelOverheadLineTypes : localPanelOverheadLineTypes;
  const panelUnderlightLineTypes = propPanelUnderlightLineTypes !== undefined ? propPanelUnderlightLineTypes : localPanelUnderlightLineTypes;
  
  // Helper to update panel colors (use callback if provided, otherwise local state)
  const updatePanelColor = (index, colorId) => {
    if (onPanelColorChange) {
      onPanelColorChange(index, colorId);
    } else {
      setLocalPanelColors((prev) => {
        const next = [...prev];
        next[index] = colorId;
        return next;
      });
    }
  };
  
  const updatePanelOverheadColor = (index, colorId) => {
    if (onPanelOverheadColorChange) {
      onPanelOverheadColorChange(index, colorId);
    } else {
      setLocalPanelOverheadColors((prev) => {
        const next = [...prev];
        next[index] = colorId;
        return next;
      });
    }
  };
  
  const updatePanelUnderlightColor = (index, colorId) => {
    if (onPanelUnderlightColorChange) {
      onPanelUnderlightColorChange(index, colorId);
    } else {
      setLocalPanelUnderlightColors((prev) => {
        const next = [...prev];
        next[index] = colorId;
        return next;
      });
    }
  };
  
  // Helper to update panel line types
  const updatePanelLineType = (index, lineType) => {
    if (onPanelLineTypeChange) {
      onPanelLineTypeChange(index, lineType);
    } else {
      setLocalPanelLineTypes((prev) => {
        const next = [...prev];
        next[index] = lineType;
        return next;
      });
    }
  };
  
  const updatePanelOverheadLineType = (index, lineType) => {
    if (onPanelOverheadLineTypeChange) {
      onPanelOverheadLineTypeChange(index, lineType);
    } else {
      setLocalPanelOverheadLineTypes((prev) => {
        const next = [...prev];
        next[index] = lineType;
        return next;
      });
    }
  };
  
  const updatePanelUnderlightLineType = (index, lineType) => {
    if (onPanelUnderlightLineTypeChange) {
      onPanelUnderlightLineTypeChange(index, lineType);
    } else {
      setLocalPanelUnderlightLineTypes((prev) => {
        const next = [...prev];
        next[index] = lineType;
        return next;
      });
    }
  };
  
  const getIcon = (iconName) => {
    if (!iconName) return LucideIcons.Square;
    const iconNameFormatted = iconName.split('-').map((word, i) => 
      i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
    ).join('').replace(/^(.)/, (m) => m.toUpperCase());
    return LucideIcons[iconNameFormatted] || LucideIcons.Square;
  };
  // Base SVG dimensions - match DoorDrawingBoard for consistency
  const baseSvgWidth = 900;
  const baseSvgHeight = 800;

  // Per-part overhead: when any main part has "With Overhead", show overhead band
  const hasPerPartOverhead =
    Array.isArray(overheadPerPart) && overheadPerPart.length >= parts && overheadPerPart.some(Boolean);
  const hasPerPartUnderlight =
    Array.isArray(underlightPerPart) && underlightPerPart.length >= parts && underlightPerPart.some(Boolean);
  const partHasUnderlight = (index) => {
    if (underlight === 'underlight') {
      if (hasPerPartUnderlight) return !!underlightPerPart[index];
      return true;
    }
    return !!(hasPerPartUnderlight && underlightPerPart[index]);
  };
  const effectiveOverhead = overhead === 'overhead' || hasPerPartOverhead;
  const effectiveUnderlight = underlight === 'underlight' || hasPerPartUnderlight;

  const hasSubHeightRulers = effectiveOverhead || effectiveUnderlight;
  const prelimDimOffset = 40;
  const prelimHeightDimLabelWidth = 160;
  const prelimRulerSpaceRight =
    prelimDimOffset +
    (hasSubHeightRulers ? 22 : 0) +
    20 +
    prelimHeightDimLabelWidth +
    24;
  const rulerSpaceBottomBase = 80;
  const svgPadding = 40;
  const drawingFitScale = 0.92;
  const hasUnderlight = effectiveUnderlight;

  // Normalize on-screen size: similar height across configs; wide multi-part windows scale by height
  const targetFrameHeight = baseSvgHeight * 0.82;
  const targetFrameWidth =
    (baseSvgWidth - prelimRulerSpaceRight - svgPadding) * drawingFitScale;
  const aspectRatio = width / Math.max(height, 1);
  const scaleFromHeight = targetFrameHeight / height;
  const scaleFromWidth = targetFrameWidth / width;
  const scaleCapHeight = (baseSvgHeight / height) * 0.98;
  const scaleCapWidth = (baseSvgWidth / width) * 0.98;
  const scale =
    aspectRatio > 1.25
      ? Math.min(scaleFromHeight, scaleCapHeight)
      : Math.min(scaleFromHeight, scaleFromWidth, scaleCapHeight, scaleCapWidth);
  const scaledWidth = width * scale;
  const scaledHeight = height * scale;

  // Scale dimension labels/offsets when viewBox is wider/taller than the base canvas
  const dimUiScale = Math.max(
    1,
    scaledWidth / (baseSvgWidth * 0.7),
    scaledHeight / (baseSvgHeight * 0.7)
  );
  const dimOffset = prelimDimOffset * dimUiScale;
  const dimTick = 8 * dimUiScale;
  const dimFontSize = 16 * dimUiScale;
  const dimFontSizeSmall = 12 * dimUiScale;
  const dimLabelHeight = 24 * dimUiScale;
  const dimLabelGap = 8 * dimUiScale;
  const dimLabelGapSmall = 4 * dimUiScale;
  const heightDimLabelWidth = prelimHeightDimLabelWidth * dimUiScale;
  const heightDimExtraOffset = hasSubHeightRulers ? 22 * dimUiScale : 0;
  const rulerSpaceRight =
    dimOffset + heightDimExtraOffset + 20 * dimUiScale + heightDimLabelWidth + 24 * dimUiScale;
  const rulerSpaceBottom = rulerSpaceBottomBase * dimUiScale;
  // Extra bottom clearance whenever width sub-rulers stack under the frame:
  // underlight part widths and/or main part widths (with underlight).
  // Previously this only ran for parts > 1, so underlight sub-rulers overlapped the
  // bottom frame when main parts === 1.
  const dimStackOffset =
    hasUnderlight && (underlightParts > 1 || parts > 1) ? dimOffset : 0;
  const dimStrokeMain = 2.5 * dimUiScale;
  const dimStrokeSub = 1 * dimUiScale;
  const dimTickSmall = 5 * dimUiScale;
  const widthDimLabelWidth = 130 * dimUiScale;
  const partDimLabelWidth = 50 * dimUiScale;

  // Frame thickness - scaled like DoorDrawingBoard for consistency
  const frameThickness = 54 * scale;

  // Inner sash (raamtype) — visible interior frame + thin bead around glass
  const innerFrameThickness = frameThickness * 0.7;
  const innerSashGlazingBeadThickness = frameThickness * 0.15;
  const innerGlassInset = innerFrameThickness + innerSashGlazingBeadThickness;
  // Mullion/dividers keep the stronger visual weight (decoupled from inner sash)
  const mullionThickness = frameThickness * 0.7;
  const mullionHalf = mullionThickness / 2; // Half width for visual calculations
  
  // Calculate ramp dorpel height if enabled
  const rampDorpelHeightPx = rampDorpel !== 'no-ramp-dorpel' 
    ? (parseInt(rampDorpel.replace('ramp-dorpel-', '').replace('mm', '')) || 30) * scale
    : 0;
  
  // Calculate roller shutter box height if enabled (extends above window)
  const rollerShutterBoxHeight = rollerShutterElectricallyOperated === true 
    ? frameThickness * 1.2 
    : 0;
  
  // Calculate total content dimensions (drawing + rulers + ramp dorpel + roller shutter)
  const bottomBand = rulerSpaceBottom + rampDorpelHeightPx + dimStackOffset;
  const svgWidth = Math.max(
    baseSvgWidth,
    scaledWidth + rulerSpaceRight * 2 + svgPadding
  );
  const svgHeight = Math.max(
    baseSvgHeight,
    scaledHeight + bottomBand + rollerShutterBoxHeight + svgPadding,
    scaledHeight + bottomBand * 2 + rollerShutterBoxHeight + svgPadding
  );

  const startX = (svgWidth - scaledWidth) / 2;
  const startY =
    rollerShutterBoxHeight +
    (svgHeight - scaledHeight - rollerShutterBoxHeight - bottomBand) / 2;
  
  // Get frame colors
  const externalFrameColor = color?.hex || '#3E4348';
  const interiorFrameColorValue = interiorFrameColor?.hex || '#555B61';
  const usePrintableFrameEdges = shouldUsePrintableFrameEdges(
    externalFrameColor,
    interiorFrameColorValue
  );
  const frameEdgeStroke = usePrintableFrameEdges
    ? FRAME_EDGE_STROKE_DARK
    : FRAME_EDGE_STROKE_DEFAULT;
  const mullionEdgeStrokeWidth = usePrintableFrameEdges ? 0.6 : 0.5;

  const renderVerticalMullionEdgeOutlines = (x, y1, y2, stroke = frameEdgeStroke) => (
    <>
      <line
        x1={x - mullionHalf}
        y1={y1}
        x2={x - mullionHalf}
        y2={y2}
        stroke={stroke}
        strokeWidth={mullionEdgeStrokeWidth}
        pointerEvents="none"
      />
      <line
        x1={x + mullionHalf}
        y1={y1}
        x2={x + mullionHalf}
        y2={y2}
        stroke={stroke}
        strokeWidth={mullionEdgeStrokeWidth}
        pointerEvents="none"
      />
    </>
  );

  const renderHorizontalMullionEdgeOutlines = (x1, x2, y, stroke = frameEdgeStroke) => (
    <>
      <line
        x1={x1}
        y1={y - mullionHalf}
        x2={x2}
        y2={y - mullionHalf}
        stroke={stroke}
        strokeWidth={mullionEdgeStrokeWidth}
        pointerEvents="none"
      />
      <line
        x1={x1}
        y1={y + mullionHalf}
        x2={x2}
        y2={y + mullionHalf}
        stroke={stroke}
        strokeWidth={mullionEdgeStrokeWidth}
        pointerEvents="none"
      />
    </>
  );

  const renderVerticalMullionPair = (x, y1, y2) => (
    <>
      <line
        x1={x}
        y1={y1}
        x2={x}
        y2={y2}
        stroke={externalFrameColor}
        strokeWidth={mullionThickness}
      />
      <line
        x1={x - (mullionHalf - 1)}
        y1={y1}
        x2={x - (mullionHalf - 1)}
        y2={y2}
        stroke={externalFrameColor}
        strokeWidth="1"
        opacity="0.7"
      />
      <line
        x1={x + (mullionHalf - 1)}
        y1={y1}
        x2={x + (mullionHalf - 1)}
        y2={y2}
        stroke={externalFrameColor}
        strokeWidth="1"
        opacity="0.6"
      />
      {renderVerticalMullionEdgeOutlines(x, y1, y2)}
    </>
  );

  const renderHorizontalMullionPair = (x1, x2, y) => (
    <>
      <line
        x1={x1}
        y1={y}
        x2={x2}
        y2={y}
        stroke={externalFrameColor}
        strokeWidth={mullionThickness}
      />
      {renderHorizontalMullionEdgeOutlines(x1, x2, y)}
    </>
  );

  const renderRaamtypeTiltLines = (windowType, innerGlassX, innerGlassY, innerGlassWidth, innerGlassHeight) => {
    const lineProps = {
      stroke: '#999999',
      strokeWidth: '3',
      strokeDasharray: '3',
      pointerEvents: 'none',
    };
    if (windowType === 'tilt-only') {
      return (
        <>
          <line x1={innerGlassX} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} {...lineProps} />
          <line x1={innerGlassX + innerGlassWidth} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} {...lineProps} />
        </>
      );
    }
    if (windowType === 'tilt-turn-left') {
      return (
        <>
          <line x1={innerGlassX} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} {...lineProps} />
          <line x1={innerGlassX + innerGlassWidth} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} {...lineProps} />
          <line x1={innerGlassX + innerGlassWidth} y1={innerGlassY} x2={innerGlassX} y2={innerGlassY + innerGlassHeight / 2} {...lineProps} />
          <line x1={innerGlassX + innerGlassWidth} y1={innerGlassY + innerGlassHeight} x2={innerGlassX} y2={innerGlassY + innerGlassHeight / 2} {...lineProps} />
        </>
      );
    }
    if (windowType === 'tilt-turn-right') {
      return (
        <>
          <line x1={innerGlassX} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} {...lineProps} />
          <line x1={innerGlassX + innerGlassWidth} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} {...lineProps} />
          <line x1={innerGlassX} y1={innerGlassY} x2={innerGlassX + innerGlassWidth} y2={innerGlassY + innerGlassHeight / 2} {...lineProps} />
          <line x1={innerGlassX} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth} y2={innerGlassY + innerGlassHeight / 2} {...lineProps} />
        </>
      );
    }
    return null;
  };

  const renderPerPartBandInnerSash = (windowType, innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight) => {
    const innerGlassX = innerFrameX + innerGlassInset;
    const innerGlassY = innerFrameY + innerGlassInset;
    const innerGlassWidth = innerFrameWidth - innerGlassInset * 2;
    const innerGlassHeight = Math.max(innerFrameHeight - innerGlassInset * 2, 2);
    return (
      <g pointerEvents="none">
        <rect x={innerFrameX} y={innerFrameY} width={innerFrameWidth} height={innerFrameHeight} fill={interiorFrameColorValue} />
        {renderInnerSashReveal({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerFrameThickness })}
        {renderInnerSashWoodJoints({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerGlassX, innerGlassWidth, innerFrameThickness })}
        <rect
          x={innerFrameX + innerFrameThickness}
          y={innerFrameY + innerFrameThickness}
          width={innerFrameWidth - innerFrameThickness * 2}
          height={innerFrameHeight - innerFrameThickness * 2}
          fill="#282C30"
        />
        <rect x={innerGlassX} y={innerGlassY} width={innerGlassWidth} height={innerGlassHeight} fill={glassFill} />
        {glassTexturePattern && (
          <rect x={innerGlassX} y={innerGlassY} width={innerGlassWidth} height={innerGlassHeight} fill={glassTexturePattern} opacity="1" />
        )}
        {renderRaamtypeTiltLines(windowType, innerGlassX, innerGlassY, innerGlassWidth, innerGlassHeight)}
      </g>
    );
  };

  const renderInnerSashReveal = ({
    innerFrameX,
    innerFrameY,
    innerFrameWidth,
    innerFrameHeight,
    innerFrameThickness,
  }) => (
    <rect
      x={innerFrameX + innerFrameThickness / 2}
      y={innerFrameY + innerFrameThickness / 2}
      width={innerFrameWidth - innerFrameThickness}
      height={innerFrameHeight - innerFrameThickness}
      fill={interiorFrameColorValue}
      pointerEvents="none"
    />
  );

  const renderInnerSashWoodJoints = ({
    innerFrameX,
    innerFrameY,
    innerFrameWidth,
    innerFrameHeight,
    innerGlassX,
    innerGlassWidth,
    innerFrameThickness,
    glassEdgeRightOffset = 2,
    glassEdgeLeftOffset = 1,
  }) => {
    if (woodJoint === 'joint-45') {
      return (
        <>
          <line
            x1={innerFrameX}
            y1={innerFrameY}
            x2={innerFrameX + innerFrameThickness}
            y2={innerFrameY + innerFrameThickness}
            stroke={frameEdgeStroke}
            strokeWidth="0.5"
            pointerEvents="none"
          />
          <line
            x1={innerFrameX + innerFrameWidth}
            y1={innerFrameY}
            x2={innerFrameX + innerFrameWidth - innerFrameThickness}
            y2={innerFrameY + innerFrameThickness}
            stroke={frameEdgeStroke}
            strokeWidth="0.5"
            pointerEvents="none"
          />
          <line
            x1={innerFrameX}
            y1={innerFrameY + innerFrameHeight}
            x2={innerFrameX + innerFrameThickness}
            y2={innerFrameY + innerFrameHeight - innerFrameThickness}
            stroke={frameEdgeStroke}
            strokeWidth="0.5"
            pointerEvents="none"
          />
          <line
            x1={innerFrameX + innerFrameWidth}
            y1={innerFrameY + innerFrameHeight}
            x2={innerFrameX + innerFrameWidth - innerFrameThickness}
            y2={innerFrameY + innerFrameHeight - innerFrameThickness}
            stroke={frameEdgeStroke}
            strokeWidth="0.5"
            pointerEvents="none"
          />
        </>
      );
    }

    const glassLeft = innerGlassX - glassEdgeLeftOffset;
    const glassRight = innerGlassX + innerGlassWidth + glassEdgeRightOffset;

    return (
      <>
        <line
          x1={glassLeft}
          y1={innerFrameY + innerFrameThickness}
          x2={glassLeft}
          y2={innerFrameY}
          stroke={frameEdgeStroke}
          strokeWidth="0.5"
          pointerEvents="none"
        />
        <line
          x1={glassRight}
          y1={innerFrameY + innerFrameThickness}
          x2={glassRight}
          y2={innerFrameY}
          stroke={frameEdgeStroke}
          strokeWidth="0.5"
          pointerEvents="none"
        />
        <line
          x1={glassLeft}
          y1={innerFrameY + innerFrameHeight - innerFrameThickness}
          x2={glassLeft}
          y2={innerFrameY + innerFrameHeight}
          stroke={frameEdgeStroke}
          strokeWidth="0.5"
          pointerEvents="none"
        />
        <line
          x1={glassRight}
          y1={innerFrameY + innerFrameHeight - innerFrameThickness}
          x2={glassRight}
          y2={innerFrameY + innerFrameHeight}
          stroke={frameEdgeStroke}
          strokeWidth="0.5"
          pointerEvents="none"
        />
      </>
    );
  };

  const renderHorizontalDividerEdges = (x1, x2, y) => renderHorizontalMullionPair(x1, x2, y);

  // Helper function to get panel fill color
  const getPanelFill = (windowTypeId, colorId) => {
    if (windowTypeId !== 'fixed-panel') {
      return glassFill;
    }
    if (colorId) {
      const colorData = getColorById(colorId);
      return colorData?.hex || '#5A6578'; // Default dark gray if color not found
    }
    return "url(#panel-gradient)"; // Default gradient if no color selected
  };

  // Helper function to render panel lines (matching Voordeur style-5 pattern)
  const renderPanelLines = (lineType, x, y, width, height, panelColor) => {
    if (lineType === 'none' || !lineType) {
      return null;
    }

    const lineColor = '#D0D4DA'; // Same color as style-5 lines
    const lineWidth = 0.5; // Same stroke width as style-5
    const numberOfLines = 8; // Same number of lines as style-5
    const lines = [];

    if (lineType === 'horizontal') {
      // Calculate spacing: divide height by (numberOfLines + 1) for even distribution
      const lineSpacing = height / (numberOfLines + 1);
      // Draw horizontal lines evenly distributed across the panel
      return (
        <g pointerEvents="none">
          {Array.from({ length: numberOfLines }, (_, i) => {
            const lineY = y + (lineSpacing * (i + 1));
            return (
              <line
                key={`h-line-${i}`}
                x1={x}
                y1={lineY}
                x2={x + width}
                y2={lineY}
                stroke={lineColor}
                strokeWidth={lineWidth}
              />
            );
          })}
        </g>
      );
    } else if (lineType === 'vertical') {
      // Calculate spacing: divide width by (numberOfLines + 1) for even distribution
      const lineSpacing = width / (numberOfLines + 1);
      // Draw vertical lines evenly distributed across the panel
      return (
        <g pointerEvents="none">
          {Array.from({ length: numberOfLines }, (_, i) => {
            const lineX = x + (lineSpacing * (i + 1));
            return (
              <line
                key={`v-line-${i}`}
                x1={lineX}
                y1={y}
                x2={lineX}
                y2={y + height}
                stroke={lineColor}
                strokeWidth={lineWidth}
              />
            );
          })}
        </g>
      );
    }

    return null;
  };

  // Calculate overhead / underlight ratios based on their heights
  const safeHeight = height || 1;
  const overheadRatio = effectiveOverhead && safeHeight > 0 ? overheadHeight / safeHeight : 0.20;
  const underlightRatio = hasUnderlight && safeHeight > 0 ? underlightHeight / safeHeight : overheadRatio;

  // When underlight width sub-rulers and/or multiple main parts are active, reserve
  // extra space under the frame (dimStackOffset) so rulers stack without overlapping
  // the bottom border — including the parts === 1 + underlightParts > 1 case.

  // Get glass fill and texture pattern based on glassTexture
  const getGlassFill = () => {
    return 'url(#glass-gradient)'; // Always use gradient as base
  };
  const getGlassTexturePattern = () => {
    if (!glassTexture) {
      return null;
    }
    // glassTexture can be an object with id property or just a string id
    const textureId = typeof glassTexture === 'object' ? glassTexture.id : glassTexture;
    if (!textureId || textureId === 'helder-glas') {
      return null; // No texture overlay for clear glass
    }
    const patternId = `glass-pattern-${textureId}`;
    return `url(#${patternId})`;
  };
  const glassFill = getGlassFill();
  const glassTexturePattern = getGlassTexturePattern();

  // Black glazing bead between exterior kozijn frame and opening (terrasdeur side-panel style)
  const outerGlazingBeadThickness = frameThickness * 0.15;
  const glassInset = frameThickness + outerGlazingBeadThickness;
  const glassX = startX + glassInset;
  // Overhead divider (top fixed section)
  const overheadDividerY = effectiveOverhead ? startY + scaledHeight * overheadRatio : null;
  // Underlight divider (bottom fixed section)
  const underlightDividerY = hasUnderlight ? (startY + scaledHeight - scaledHeight * underlightRatio) : null;

  // Main glass Y and height depending on overhead / underlight
  let glassY;
  let glassHeight;
  if (effectiveOverhead && hasUnderlight) {
    // Both overhead and underlight: main glass in the middle (below overhead transom, above underlight transom)
    glassY = overheadDividerY + mullionHalf;
    const bottomLimit = underlightDividerY - mullionHalf;
    glassHeight = bottomLimit - glassY;
  } else if (effectiveOverhead) {
    // Only overhead: glass below overhead transom rail (not on transom centerline)
    glassY = overheadDividerY + mullionHalf;
    glassHeight = startY + scaledHeight - glassInset - glassY;
  } else if (hasUnderlight) {
    // Only underlight: glass above underlight transom rail
    glassY = startY + glassInset;
    glassHeight = underlightDividerY - mullionHalf - glassY;
  } else {
    // No overhead or underlight
    glassY = startY + glassInset;
    glassHeight = scaledHeight - glassInset * 2;
  }
  const glassWidth = scaledWidth - glassInset * 2;

  // Normalized per-part widths in mm; always sum to total width
  const normalizedPartWidths =
    parts > 0 ? ensurePartWidthsSum(partWidths, parts, width) : [];

  const totalPartWidth = width || normalizedPartWidths.reduce((sum, w) => sum + w, 0) || 1;

  const getPartOverheadBandConfig = (partIndex) => {
    const partWidthMm = normalizedPartWidths[partIndex] ?? totalPartWidth / Math.max(parts, 1);
    return getPartBandSectionConfig({
      partIndex,
      partWidthMm,
      legacyWindowTypes: partOverheadWindowTypes,
      sections: partOverheadSectionsProp,
      sectionWindowTypes: partOverheadSectionWindowTypesProp,
      sectionWidths: partOverheadSectionWidthsProp,
    });
  };

  const getPartUnderlightBandConfig = (partIndex) => {
    const partWidthMm = normalizedPartWidths[partIndex] ?? totalPartWidth / Math.max(parts, 1);
    return getPartBandSectionConfig({
      partIndex,
      partWidthMm,
      legacyWindowTypes: partUnderlightWindowTypes,
      sections: partUnderlightSectionsProp,
      sectionWindowTypes: partUnderlightSectionWindowTypesProp,
      sectionWidths: partUnderlightSectionWidthsProp,
    });
  };

  const partSectionCountPicker = (band, partIndex, currentCount, onChange) => (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-gray-700">
        {band === 'overhead' ? t('drawing.numberOfOverheadSectionsPart') : t('drawing.numberOfUnderlightSectionsPart')}
      </Label>
      <div className="flex gap-1.5">
        {[1, 2].map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => onChange?.(partIndex, value)}
            className={`flex h-9 min-w-[2.25rem] flex-1 items-center justify-center rounded-lg border text-sm font-semibold transition-all ${
              clampPartBandSectionCount(currentCount) === value
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-400'
            }`}
          >
            {value}
          </button>
        ))}
      </div>
    </div>
  );
  
  // Normalized per-overhead-part widths in mm; always sum to total width
  const normalizedOverheadPartWidths =
    overheadParts > 0 ? ensurePartWidthsSum(overheadPartWidths, overheadParts, width) : [];

  const totalOverheadPartWidth = width || normalizedOverheadPartWidths.reduce((sum, w) => sum + w, 0) || 1;

  // Normalized per-underlight-part widths in mm; always sum to total width
  const normalizedUnderlightPartWidths =
    underlightParts > 0 ? ensurePartWidthsSum(underlightPartWidths, underlightParts, width) : [];

  const totalUnderlightPartWidth = width || normalizedUnderlightPartWidths.reduce((sum, w) => sum + w, 0) || 1;

  // Keep a local, string-based copy of per-part widths so the user can type
  // freely without immediate clamping.
  useEffect(() => {
    if (parts > 0) {
      setInputPartWidths(
        normalizedPartWidths.map((w) => (Number.isFinite(w) ? String(w) : ''))
      );
    } else {
      setInputPartWidths([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts, totalPartWidth, width, JSON.stringify(normalizedPartWidths)]);

  // Keep a local, string-based copy of per-overhead-part widths so the user can type
  // freely without immediate clamping.
  useEffect(() => {
    if (overheadParts > 0) {
      setInputOverheadPartWidths(
        normalizedOverheadPartWidths.map((w) => (Number.isFinite(w) ? String(w) : ''))
      );
    } else {
      setInputOverheadPartWidths([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overheadParts, totalOverheadPartWidth, width, JSON.stringify(normalizedOverheadPartWidths)]);

  // Keep a local, string-based copy of per-underlight-part widths so the user can type
  // freely without immediate clamping.
  useEffect(() => {
    if (underlightParts > 0) {
      setInputUnderlightPartWidths(
        normalizedUnderlightPartWidths.map((w) => (Number.isFinite(w) ? String(w) : ''))
      );
    } else {
      setInputUnderlightPartWidths([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [underlightParts, totalUnderlightPartWidth, width, JSON.stringify(normalizedUnderlightPartWidths)]);

  // Sync width and height inputs with props
  useEffect(() => {
    setWidthInput(String(width));
  }, [width]);

  useEffect(() => {
    setHeightInput(String(height));
  }, [height]);

  useEffect(() => {
    setOverheadHeightInput(String(overheadHeight));
  }, [overheadHeight]);

  useEffect(() => {
    setUnderlightHeightInput(String(underlightHeight));
  }, [underlightHeight]);

  // Open dialog when a part is selected and dialogType is set
  useEffect(() => {
    if (dialogType === 'general') {
      setSettingsOpen(true);
    } else if (dialogType === 'main' && selectedPartIndex !== null) {
      setSettingsOpen(true);
    } else if (dialogType === 'overhead' && selectedOverheadPartIndex !== null) {
      setSettingsOpen(true);
    } else if (dialogType === 'underlight' && selectedUnderlightPartIndex !== null) {
      setSettingsOpen(true);
    } else if (dialogType === 'part-overhead' && selectedPartOverheadIndex !== null) {
      setSettingsOpen(true);
    } else if (dialogType === 'part-underlight' && selectedPartUnderlightIndex !== null) {
      setSettingsOpen(true);
    }
  }, [dialogType, selectedPartIndex, selectedOverheadPartIndex, selectedUnderlightPartIndex, selectedPartOverheadIndex, selectedPartUnderlightIndex]);

  // Sync ventilator selection state with current parts (only when using local state)
  useEffect(() => {
    if (onVentilatorPartsChange) return;
    if (parts > 0) {
      setVentilatorPartsLocal((prev) => {
        const next = Array(parts).fill(false);
        for (let i = 0; i < Math.min(prev.length, parts); i += 1) next[i] = !!prev[i];
        return next;
      });
    } else setVentilatorPartsLocal([]);
  }, [parts, onVentilatorPartsChange]);

  useEffect(() => {
    if (onVentilatorOverheadPartsChange) return;
    if (overheadParts > 0) {
      setVentilatorOverheadPartsLocal((prev) => {
        const next = Array(overheadParts).fill(false);
        for (let i = 0; i < Math.min(prev.length, overheadParts); i += 1) next[i] = !!prev[i];
        return next;
      });
    } else setVentilatorOverheadPartsLocal([]);
  }, [overheadParts, onVentilatorOverheadPartsChange]);

  useEffect(() => {
    if (onVentilatorUnderlightPartsChange) return;
    if (underlightParts > 0) {
      setVentilatorUnderlightPartsLocal((prev) => {
        const next = Array(underlightParts).fill(false);
        for (let i = 0; i < Math.min(prev.length, underlightParts); i += 1) next[i] = !!prev[i];
        return next;
      });
    } else setVentilatorUnderlightPartsLocal([]);
  }, [underlightParts, onVentilatorUnderlightPartsChange]);

  useEffect(() => {
    if (parts > 0) {
      setPartOverheadWindowTypesLocal((prev) => {
        const next = [];
        for (let i = 0; i < parts; i += 1) next[i] = prev[i];
        return next;
      });
      setVentilatorPartOverheadLocal((prev) => {
        const next = Array(parts).fill(false);
        for (let i = 0; i < Math.min(prev.length, parts); i += 1) next[i] = !!prev[i];
        return next;
      });
      setPartUnderlightWindowTypesLocal((prev) => {
        const next = [];
        for (let i = 0; i < parts; i += 1) next[i] = prev[i];
        return next;
      });
      setVentilatorPartUnderlightLocal((prev) => {
        const next = Array(parts).fill(false);
        for (let i = 0; i < Math.min(prev.length, parts); i += 1) next[i] = !!prev[i];
        return next;
      });
    } else {
      setPartOverheadWindowTypesLocal([]);
      setVentilatorPartOverheadLocal([]);
      setPartUnderlightWindowTypesLocal([]);
      setVentilatorPartUnderlightLocal([]);
    }
  }, [parts]);

  // Sync per-part overhead with current parts (only when using local state)
  useEffect(() => {
    if (onOverheadPerPartChange) return;
    if (parts > 0) {
      setOverheadPerPartLocal((prev) => {
        const next = Array(parts).fill(false);
        for (let i = 0; i < Math.min(prev.length, parts); i += 1) next[i] = !!prev[i];
        return next;
      });
    } else setOverheadPerPartLocal([]);
  }, [parts, onOverheadPerPartChange]);

  // Sync per-part underlight with current parts (only when using local state)
  useEffect(() => {
    if (onUnderlightPerPartChange) return;
    if (parts > 0) {
      setUnderlightPerPartLocal((prev) => {
        const next = Array(parts).fill(false);
        for (let i = 0; i < Math.min(prev.length, parts); i += 1) next[i] = !!prev[i];
        return next;
      });
    } else setUnderlightPerPartLocal([]);
  }, [parts, onUnderlightPerPartChange]);

  // Sync panel colors with current parts (only if using local state)
  useEffect(() => {
    if (propPanelColors === undefined && parts > 0) {
      setLocalPanelColors((prev) => {
        const next = Array(parts).fill(null);
        for (let i = 0; i < Math.min(prev.length, parts); i += 1) {
          next[i] = prev[i] || null;
        }
        return next;
      });
    } else if (propPanelColors === undefined) {
      setLocalPanelColors([]);
    }
  }, [parts, propPanelColors]);

  // Sync overhead panel colors with current overhead parts (only if using local state)
  useEffect(() => {
    if (propPanelOverheadColors === undefined && overheadParts > 0) {
      setLocalPanelOverheadColors((prev) => {
        const next = Array(overheadParts).fill(null);
        for (let i = 0; i < Math.min(prev.length, overheadParts); i += 1) {
          next[i] = prev[i] || null;
        }
        return next;
      });
    } else if (propPanelOverheadColors === undefined) {
      setLocalPanelOverheadColors([]);
    }
  }, [overheadParts, propPanelOverheadColors]);

  // Sync underlight panel colors with current underlight parts (only if using local state)
  useEffect(() => {
    if (propPanelUnderlightColors === undefined && underlightParts > 0) {
      setLocalPanelUnderlightColors((prev) => {
        const next = Array(underlightParts).fill(null);
        for (let i = 0; i < Math.min(prev.length, underlightParts); i += 1) {
          next[i] = prev[i] || null;
        }
        return next;
      });
    } else if (propPanelUnderlightColors === undefined) {
      setLocalPanelUnderlightColors([]);
    }
  }, [underlightParts, propPanelUnderlightColors]);

  // Sync panel line types with current parts (only if using local state)
  useEffect(() => {
    if (propPanelLineTypes === undefined && parts > 0) {
      setLocalPanelLineTypes((prev) => {
        const next = Array(parts).fill('none');
        for (let i = 0; i < Math.min(prev.length, parts); i += 1) {
          next[i] = prev[i] || 'none';
        }
        return next;
      });
    } else if (propPanelLineTypes === undefined) {
      setLocalPanelLineTypes([]);
    }
  }, [parts, propPanelLineTypes]);

  // Sync overhead panel line types with current overhead parts (only if using local state)
  useEffect(() => {
    if (propPanelOverheadLineTypes === undefined && overheadParts > 0) {
      setLocalPanelOverheadLineTypes((prev) => {
        const next = Array(overheadParts).fill('none');
        for (let i = 0; i < Math.min(prev.length, overheadParts); i += 1) {
          next[i] = prev[i] || 'none';
        }
        return next;
      });
    } else if (propPanelOverheadLineTypes === undefined) {
      setLocalPanelOverheadLineTypes([]);
    }
  }, [overheadParts, propPanelOverheadLineTypes]);

  // Sync underlight panel line types with current underlight parts (only if using local state)
  useEffect(() => {
    if (propPanelUnderlightLineTypes === undefined && underlightParts > 0) {
      setLocalPanelUnderlightLineTypes((prev) => {
        const next = Array(underlightParts).fill('none');
        for (let i = 0; i < Math.min(prev.length, underlightParts); i += 1) {
          next[i] = prev[i] || 'none';
        }
        return next;
      });
    } else if (propPanelUnderlightLineTypes === undefined) {
      setLocalPanelUnderlightLineTypes([]);
    }
  }, [underlightParts, propPanelUnderlightLineTypes]);
  
  return (
    <div 
      className="w-full h-full flex flex-col items-center justify-center p-4 rounded-lg relative"
      style={{ cursor: (hoveredPartIndex !== null || hoveredOverheadPartIndex !== null || hoveredUnderlightPartIndex !== null || hoveredPartOverheadIndex !== null || hoveredPartUnderlightIndex !== null) ? 'pointer' : 'default' }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="max-w-full max-h-full"
        style={{ minHeight: '500px' }}
      >
        <defs>
          {/* Soft background gradient for the canvas */}
          <linearGradient id="canvas-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F7FAFC" />
            <stop offset="100%" stopColor="#E2E8F0" />
          </linearGradient>

          {/* Glass gradient: blue at top, fades to white */}
          <linearGradient id="glass-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C7EBFF" stopOpacity="0.95" />
            <stop offset="60%" stopColor="#E6F7FF" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.95" />
          </linearGradient>
          
          {/* Glass texture patterns - only the texture images, gradient applied separately */}
          {/* Chinchilla texture pattern - large pattern to prevent repeating, cover style */}
          <pattern id="glass-pattern-chinchilla" patternUnits="userSpaceOnUse" x="0" y="0" width="40" height="40">
            <image href="/images/chinchilla21.png" x="0" y="0" width="40" height="40" preserveAspectRatio="xMidYMid slice" />
          </pattern>
          
          {/* Satinato texture pattern - large pattern to prevent repeating */}
          <pattern id="glass-pattern-satinato" patternUnits="userSpaceOnUse" x="0" y="0" width="10000" height="10000">
            <image href="/images/santino.png" x="0" y="0" width="10000" height="10000" preserveAspectRatio="none" />
          </pattern>
          
          {/* Fixed Panel gradient: solid panel appearance - darker gray panel material */}
          <linearGradient id="panel-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5A6578" stopOpacity="1" />
            <stop offset="50%" stopColor="#4A5568" stopOpacity="1" />
            <stop offset="100%" stopColor="#3A4558" stopOpacity="1" />
          </linearGradient>
          
          {/* Screen mesh pattern for electrically operated screen - fine net/mesh pattern */}
          <pattern id="screen-mesh-pattern" patternUnits="userSpaceOnUse" width={6} height={6}>
            <line x1="0" y1="0" x2="6" y2="0" stroke="#666666" strokeWidth="0.8" opacity="0.7" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="#666666" strokeWidth="0.8" opacity="0.7" />
            <line x1="0" y1="6" x2="6" y2="6" stroke="#666666" strokeWidth="0.8" opacity="0.7" />
            <line x1="6" y1="0" x2="6" y2="6" stroke="#666666" strokeWidth="0.8" opacity="0.7" />
          </pattern>
        </defs>

        {/* Background (flat drawing board) */}
        <rect width={svgWidth} height={svgHeight} fill="#F5F5F5" />
        
        {/* Outer frame */}
        <rect
          x={startX}
          y={startY}
          width={scaledWidth}
          height={scaledHeight}
          fill={externalFrameColor}
        />

        {/* Inner frame (lighter reveal) - part of external frame */}
        <rect
          x={startX + frameThickness / 2}
          y={startY + frameThickness / 2}
          width={scaledWidth - frameThickness}
          height={scaledHeight - frameThickness}
          fill={externalFrameColor}
        />

        {/* Corner joints - 45° (diagonal) or 90° (horizontal/vertical) */}
        {woodJoint === 'joint-45' ? (
          <>
            {/* 45° mitered corners - diagonal lines */}
            <line
              x1={startX}
              y1={startY}
              x2={startX + frameThickness}
              y2={startY + frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="0.5"
            />
            <line
              x1={startX + scaledWidth}
              y1={startY}
              x2={startX + scaledWidth - frameThickness}
              y2={startY + frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="0.5"
            />
            <line
              x1={startX}
              y1={startY + scaledHeight}
              x2={startX + frameThickness}
              y2={startY + scaledHeight - frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="0.5"
            />
            <line
              x1={startX + scaledWidth}
              y1={startY + scaledHeight}
              x2={startX + scaledWidth - frameThickness}
              y2={startY + scaledHeight - frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="0.5"
            />
          </>
        ) : (
          <>
            {/* 90° corners - horizontal lines at top and bottom */}
            {/* Top-left corner - horizontal line going down (replaces diagonal) */}
            <line
              x1={startX}
              y1={startY + frameThickness}
              x2={startX + frameThickness}
              y2={startY + frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="0.5"
            />
            {/* Top-right corner - horizontal line going down (replaces diagonal) */}
            <line
              x1={startX + scaledWidth - frameThickness}
              y1={startY + frameThickness}
              x2={startX + scaledWidth}
              y2={startY + frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="0.5"
            />
            {/* Bottom-left corner */}
            <line
              x1={startX}
              y1={startY + scaledHeight - frameThickness}
              x2={startX + frameThickness}
              y2={startY + scaledHeight - frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="0.5"
            />
            <line
              x1={startX}
              y1={startY + scaledHeight - frameThickness}
              x2={startX}
              y2={startY + scaledHeight}
              stroke={frameEdgeStroke}
              strokeWidth="0.5"
            />
            {/* Bottom-right corner */}
            <line
              x1={startX + scaledWidth - frameThickness}
              y1={startY + scaledHeight - frameThickness}
              x2={startX + scaledWidth}
              y2={startY + scaledHeight - frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="0.5"
            />
            <line
              x1={startX + scaledWidth}
              y1={startY + scaledHeight - frameThickness}
              x2={startX + scaledWidth}
              y2={startY + scaledHeight}
              stroke={frameEdgeStroke}
              strokeWidth="0.5"
            />
          </>
        )}

        {/* Spacer / glazing bead between frame and glass */}
        <rect
          x={startX + frameThickness}
          y={startY + frameThickness}
          width={scaledWidth - frameThickness * 2}
          height={scaledHeight - frameThickness * 2}
          fill="#282C30"
        />


        {/* Roller shutter electrically operated - box representation above window */}
        {rollerShutterElectricallyOperated === true && (() => {
          const shutterBoxHeight = frameThickness * 1.2;
          const shutterBoxY = startY - shutterBoxHeight;
          const shutterBoxWidth = scaledWidth + frameThickness;
          const shutterBoxX = startX - frameThickness / 2;
          const shutterColorData = getColorByRAL(rollerShutterColor || '7016');
          const shutterColorHex = shutterColorData?.hex || '#3D3D3D';
          
          return (
            <g>
              {/* Roller shutter box */}
              <rect
                x={shutterBoxX}
                y={shutterBoxY}
                width={shutterBoxWidth}
                height={shutterBoxHeight}
                fill={shutterColorHex}
                stroke={externalFrameColor}
                strokeWidth="1"
                rx={frameThickness * 0.1}
              />
              
              {/* Roller shutter slats pattern */}
              <g>
                {Array.from({ length: Math.floor(shutterBoxWidth / (frameThickness * 0.8)) }, (_, i) => {
                  const slatX = shutterBoxX + i * frameThickness * 0.8;
                  return (
                    <line
                      key={`shutter-slat-${i}`}
                      x1={slatX}
                      y1={shutterBoxY + shutterBoxHeight * 0.2}
                      x2={slatX}
                      y2={shutterBoxY + shutterBoxHeight * 0.8}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                      opacity="0.7"
                    />
                  );
                })}
              </g>
              
              {/* Small icon indicator on top-left corner of shutter box */}
              <g transform={`translate(${shutterBoxX + frameThickness * 0.5}, ${shutterBoxY + shutterBoxHeight / 2})`}>
                <circle cx="0" cy="0" r={frameThickness * 0.3} fill="#4CAF50" opacity="0.9" />
                <text x="0" y="0" textAnchor="middle" dominantBaseline="central" fontSize={frameThickness * 0.4} fill="white" fontWeight="bold" pointerEvents="none">R</text>
              </g>
            </g>
          );
        })()}

        {/* Per-overhead-part horizontal dimensions (only when overhead is active and multiple overhead parts) */}
        {overhead === 'overhead' && overheadParts > 1 &&
          Array.from({ length: overheadParts }, (_, index) => {
            const startAccum = normalizedOverheadPartWidths
              .slice(0, index)
              .reduce((sum, w) => sum + w, 0);
            const overheadPartWidthMm = normalizedOverheadPartWidths[index];
            const endAccum = startAccum + overheadPartWidthMm;
            const segStart = glassX + (startAccum / totalOverheadPartWidth) * glassWidth;
            const segEnd = glassX + (endAccum / totalOverheadPartWidth) * glassWidth;
            // Place above the overhead area, adjusted position for better visibility
            const overheadY = startY - dimOffset + 6;
            return (
              <g key={`overhead-part-dim-${index}`}>
                {/* segment line */}
                <line
                  x1={segStart}
                  y1={overheadY}
                  x2={segEnd}
                  y2={overheadY}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* left tick */}
                <line
                  x1={segStart}
                  y1={overheadY - 5}
                  x2={segStart}
                  y2={overheadY + 5}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* right tick */}
                <line
                  x1={segEnd}
                  y1={overheadY - 5}
                  x2={segEnd}
                  y2={overheadY + 5}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* Editable label */}
                <foreignObject
                  x={(segStart + segEnd) / 2 - 25}
                  y={overheadY - 4}
                  width={partDimLabelWidth}
                  height={dimLabelHeight}
                >
                  <input
                    type="number"
                    value={inputOverheadPartWidths[index] ?? overheadPartWidthMm}
                    onChange={(e) => {
                      const newVal = e.target.value;
                      setInputOverheadPartWidths((prev) => {
                        const next = [...prev];
                        next[index] = newVal;
                        return next;
                      });
                    }}
                    onBlur={() => {
                      if (!onOverheadPartWidthChange) return;
                      const parsed = parseInt(inputOverheadPartWidths[index], 10);
                      if (!Number.isNaN(parsed)) {
                        onOverheadPartWidthChange(index, parsed);
                      } else {
                        // reset to current width if input was invalid/empty
                        setInputOverheadPartWidths((prev) => {
                          const next = [...prev];
                          next[index] = String(overheadPartWidthMm);
                          return next;
                        });
                      }
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'center',
                      fontSize: `${dimFontSizeSmall}px`,
                      fontWeight: 500,
                      color: '#4A4A4A',
                      outline: 'none',
                      padding: 0,
                      margin: 0,
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield',
                    }}
                  />
                </foreignObject>
              </g>
            );
          })}

        {/* Main frame glass background for overhead area - global (left panel) or per-part (part setting) */}
        {effectiveOverhead && (
          overhead === 'overhead' ? (
          /* Global overhead from left panel: full band, one or multiple overhead parts */
          overheadParts > 1 ? (
            // Multiple overhead parts: render each part separately for hover/click
            Array.from({ length: overheadParts }, (_, index) => {
              const startAccum = normalizedOverheadPartWidths
                .slice(0, index)
                .reduce((sum, w) => sum + w, 0);
              const overheadPartWidthMm = normalizedOverheadPartWidths[index];
              const overheadPartWidthPx = (overheadPartWidthMm / totalOverheadPartWidth) * glassWidth;
              const overheadPartX = glassX + (startAccum / totalOverheadPartWidth) * glassWidth;
              const overheadPartY = startY + glassInset;
              const overheadPartHeight = scaledHeight * overheadRatio - glassInset;
              const isHovered = hoveredOverheadPartIndex === index;
              // Ensure overheadPartWindowTypes array exists and has the right length
              const currentOverheadPartWindowTypes = overheadPartWindowTypes && overheadPartWindowTypes.length === overheadParts 
                ? overheadPartWindowTypes 
                : Array(overheadParts).fill('fixed-sash');
              const overheadPartWindowType = currentOverheadPartWindowTypes[index] || 'fixed-sash';
              const hasInnerFrame = overheadPartWindowType === 'fixed-glass' || overheadPartWindowType === 'tilt-only' || overheadPartWindowType === 'tilt-turn-left' || overheadPartWindowType === 'tilt-turn-right';
              
              return (
                <g key={`overhead-part-${index}`}>
                  <g>
                    <rect
                      x={overheadPartX}
                      y={overheadPartY}
                      width={overheadPartWidthPx}
                      height={overheadPartHeight}
                      fill={getPanelFill(overheadPartWindowType, panelOverheadColors[index])}
                      style={{ cursor: isHovered ? 'pointer' : 'default' }}
                      onMouseEnter={() => setHoveredOverheadPartIndex(index)}
                      onMouseLeave={() => setHoveredOverheadPartIndex(null)}
                      onClick={(e) => {
                        e.stopPropagation();
                        // If clicking the same part, reset dialogType first to trigger useEffect
                        if (selectedOverheadPartIndex === index && dialogType === 'overhead') {
                          setDialogType(null);
                          setTimeout(() => {
                            setSelectedPartIndex(null);
                            setSelectedUnderlightPartIndex(null);
                            setIsUnderlightPart(false);
                        setSelectedOverheadPartIndex(index);
                            setIsOverheadPart(true);
                            setDialogType('overhead');
                          }, 0);
                        } else {
                          // Clear all other selections first
                        setSelectedPartIndex(null);
                          setSelectedUnderlightPartIndex(null);
                          setIsUnderlightPart(false);
                          // Set overhead part selection and dialog type together
                          setSelectedOverheadPartIndex(index);
                        setIsOverheadPart(true);
                          setDialogType('overhead');
                        }
                      }}
                    >
                      <title>{t('drawing.clickToConfigureOverhead', { number: index + 1 })}</title>
                    </rect>
                    {/* Panel lines for overhead parts */}
                    {overheadPartWindowType === 'fixed-panel' && renderPanelLines(
                      panelOverheadLineTypes[index] || 'none',
                      overheadPartX,
                      overheadPartY,
                      overheadPartWidthPx,
                      overheadPartHeight,
                      getPanelFill(overheadPartWindowType, panelOverheadColors[index])
                    )}
                    {overheadPartWindowType !== 'fixed-panel' && glassTexturePattern && (
                      <rect
                        x={overheadPartX}
                        y={overheadPartY}
                        width={overheadPartWidthPx}
                        height={overheadPartHeight}
                        fill={glassTexturePattern}
                        opacity="1"
                        pointerEvents="none"
                      />
                    )}
                  </g>
                  {/* Ventilator for overhead parts when Fixed Sash and enabled */}
                  {overheadPartWindowType === 'fixed-sash' && ventilatorOverheadParts[index] && (
                    <g pointerEvents="none">
                      {(() => {
                        const hasLeftMullion = index > 0;
                        const hasRightMullion = index < overheadParts - 1;
                        const ventilatorInnerFrameX = overheadPartX + (hasLeftMullion ? mullionHalf : 0);
                        const ventilatorInnerFrameWidth = overheadPartWidthPx - (hasLeftMullion ? mullionHalf : 0) - (hasRightMullion ? mullionHalf : 0);

                        const ventilatorMarginX = 0;
                        const ventilatorHeight = frameThickness * 0.8;
                        const ventilatorX = ventilatorInnerFrameX + ventilatorMarginX;
                        const ventilatorY = overheadPartY;
                        const ventilatorWidth = Math.max(0, ventilatorInnerFrameWidth - ventilatorMarginX * 2);
                        const slatCount = 13;
                        const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                        const slats = [];
                        for (let i = 0; i < slatCount; i += 1) {
                          const x = ventilatorX + 3 * scale + i * slatGap;
                          slats.push(
                            <line
                              key={`overhead-vent-slat-${index}-${i}`}
                              x1={x}
                              y1={ventilatorY + 2 * scale}
                              x2={x}
                              y2={ventilatorY + ventilatorHeight - 2 * scale}
                              stroke="#000000"
                              strokeWidth={1.4 * scale}
                              strokeLinecap="round"
                            />
                          );
                        }
                        return (
                          <>
                            {/* Ventilator body */}
                            <rect
                              x={ventilatorX}
                              y={ventilatorY}
                              width={ventilatorWidth}
                              height={ventilatorHeight}
                              fill="#D5DADF"
                              stroke="#8A929A"
                              strokeWidth={1 * scale}
                              rx={2 * scale}
                              ry={2 * scale}
                            />
                            {/* Dark bottom outline under ventilator */}
                            <line
                              x1={ventilatorX}
                              y1={ventilatorY + ventilatorHeight}
                              x2={ventilatorX + ventilatorWidth}
                              y2={ventilatorY + ventilatorHeight}
                              stroke="#000000"
                              strokeWidth={1.6 * scale}
                            />
                            {/* Slats */}
                            {slats}
                          </>
                        );
                      })()}
                    </g>
                  )}
                  {/* Inner frame for overhead parts - same as main window parts */}
                  {hasInnerFrame && (() => {
                    // Adjust inner frame to account for mullions (use mullionHalf from top level)
                    const hasLeftMullion = index > 0;
                    const hasRightMullion = index < overheadParts - 1;
                    
                    const innerFrameX = overheadPartX + (hasLeftMullion ? mullionHalf : 0);
                    const innerFrameY = overheadPartY;
                    const innerFrameWidth = overheadPartWidthPx - (hasLeftMullion ? mullionHalf : 0) - (hasRightMullion ? mullionHalf : 0);
                    // Height should fit within overhead part area, ensuring it doesn't extend beyond the horizontal divider
                    // The divider is at startY + scaledHeight * overheadRatio, so inner frame should stop before the divider center
                    const dividerY = startY + scaledHeight * overheadRatio;
                    // Stop inner frame before the divider center (accounting for half the divider thickness)
                    const maxInnerFrameHeight = (dividerY - mullionThickness / 2) - innerFrameY;
                    const innerFrameHeight = Math.min(overheadPartHeight, maxInnerFrameHeight);
                    const innerGlassX = innerFrameX + innerGlassInset;
                    const innerGlassY = innerFrameY + innerGlassInset;
                    const innerGlassWidth = innerFrameWidth - innerGlassInset * 2;
                    const innerGlassHeight = innerFrameHeight - innerGlassInset * 2;
                    
                    return (
                      <g style={{ pointerEvents: 'none' }}>
                        {/* Outer frame (dark) */}
                        <rect
                          x={innerFrameX}
                          y={innerFrameY}
                          width={innerFrameWidth}
                          height={innerFrameHeight}
                          fill={interiorFrameColorValue}
                          pointerEvents="none"
                        />
                        {renderInnerSashReveal({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerFrameThickness })}
                        {renderInnerSashWoodJoints({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerGlassX, innerGlassWidth, innerFrameThickness })}
                        {/* Spacer / glazing bead */}
                        <rect
                          x={innerFrameX + innerFrameThickness}
                          y={innerFrameY + innerFrameThickness}
                          width={innerFrameWidth - innerFrameThickness * 2}
                          height={innerFrameHeight - innerFrameThickness * 2}
                          fill="#282C30"
                          pointerEvents="none"
                        />
                        {/* Inner glass with gradient base and texture overlay */}
                        <g pointerEvents="none">
                          <rect
                            x={innerGlassX}
                            y={innerGlassY}
                            width={innerGlassWidth}
                            height={innerGlassHeight}
                            fill={glassFill}
                          />
                          {glassTexturePattern && (
                            <rect
                              x={innerGlassX}
                              y={innerGlassY}
                              width={innerGlassWidth}
                              height={innerGlassHeight}
                              fill={glassTexturePattern}
                              opacity="1"
                            />
                          )}
                        </g>
                        {/* Diagonal lines for Tilt Only */}
                        {overheadPartWindowType === 'tilt-only' && (
                          <>
                            <line
                              x1={innerGlassX}
                              y1={innerGlassY + innerGlassHeight}
                              x2={innerGlassX + innerGlassWidth / 2}
                              y2={innerGlassY}
                              stroke="#999999"
                              strokeWidth="3"
                              strokeDasharray="3"
                              pointerEvents="none"
                            />
                            <line
                              x1={innerGlassX + innerGlassWidth}
                              y1={innerGlassY + innerGlassHeight}
                              x2={innerGlassX + innerGlassWidth / 2}
                              y2={innerGlassY}
                              stroke="#999999"
                              strokeWidth="3"
                              strokeDasharray="3"
                              pointerEvents="none"
                            />
                          </>
                        )}
                        {/* Diagonal lines for Tilt & Turn Left */}
                        {overheadPartWindowType === 'tilt-turn-left' && (
                          <>
                            <line
                              x1={innerGlassX}
                              y1={innerGlassY + innerGlassHeight}
                              x2={innerGlassX + innerGlassWidth / 2}
                              y2={innerGlassY}
                              stroke="#999999"
                              strokeWidth="3"
                              strokeDasharray="3"
                              pointerEvents="none"
                            />
                            <line
                              x1={innerGlassX + innerGlassWidth}
                              y1={innerGlassY + innerGlassHeight}
                              x2={innerGlassX + innerGlassWidth / 2}
                              y2={innerGlassY}
                              stroke="#999999"
                              strokeWidth="3"
                              strokeDasharray="3"
                              pointerEvents="none"
                            />
                            <line
                              x1={innerGlassX + innerGlassWidth}
                              y1={innerGlassY}
                              x2={innerGlassX}
                              y2={innerGlassY + innerGlassHeight / 2}
                              stroke="#999999"
                              strokeWidth="3"
                              strokeDasharray="3"
                              pointerEvents="none"
                            />
                            <line
                              x1={innerGlassX + innerGlassWidth}
                              y1={innerGlassY + innerGlassHeight}
                              x2={innerGlassX}
                              y2={innerGlassY + innerGlassHeight / 2}
                              stroke="#999999"
                              strokeWidth="3"
                              strokeDasharray="3"
                              pointerEvents="none"
                            />
                          </>
                        )}
                        {/* Diagonal lines for Tilt & Turn Right */}
                        {overheadPartWindowType === 'tilt-turn-right' && (
                          <>
                            <line
                              x1={innerGlassX}
                              y1={innerGlassY + innerGlassHeight}
                              x2={innerGlassX + innerGlassWidth / 2}
                              y2={innerGlassY}
                              stroke="#999999"
                              strokeWidth="3"
                              strokeDasharray="3"
                              pointerEvents="none"
                            />
                            <line
                              x1={innerGlassX + innerGlassWidth}
                              y1={innerGlassY + innerGlassHeight}
                              x2={innerGlassX + innerGlassWidth / 2}
                              y2={innerGlassY}
                              stroke="#999999"
                              strokeWidth="3"
                              strokeDasharray="3"
                              pointerEvents="none"
                            />
                            <line
                              x1={innerGlassX}
                              y1={innerGlassY}
                              x2={innerGlassX + innerGlassWidth}
                              y2={innerGlassY + innerGlassHeight / 2}
                              stroke="#999999"
                              strokeWidth="3"
                              strokeDasharray="3"
                              pointerEvents="none"
                            />
                            <line
                              x1={innerGlassX}
                              y1={innerGlassY + innerGlassHeight}
                              x2={innerGlassX + innerGlassWidth}
                              y2={innerGlassY + innerGlassHeight / 2}
                              stroke="#999999"
                              strokeWidth="3"
                              strokeDasharray="3"
                              pointerEvents="none"
                            />
                          </>
                        )}
                      </g>
                    );
                  })()}
                  {/* Hover highlight overlay */}
                  {isHovered && (
                    <rect
                      x={overheadPartX}
                      y={overheadPartY}
                      width={overheadPartWidthPx}
                      height={overheadPartHeight}
                      fill="rgba(255, 165, 0, 0.15)"
                      stroke="rgba(255, 140, 0, 0.6)"
                      strokeWidth="2"
                      style={{ pointerEvents: 'none', cursor: 'pointer' }}
                    />
                  )}
                </g>
              );
            })
          ) : (
            // Single overhead part
            <g>
              {(() => {
                const singleOverheadPartWindowType = overheadPartWindowTypes?.[0] || windowType?.id || 'fixed-sash';
                const hasInnerFrameSingle = singleOverheadPartWindowType === 'fixed-glass' || 
                                           singleOverheadPartWindowType === 'tilt-only' || 
                                           singleOverheadPartWindowType === 'tilt-turn-left' || 
                                           singleOverheadPartWindowType === 'tilt-turn-right';
                
                return (
                  <>
                    <g>
          <rect
            x={glassX}
            y={startY + glassInset}
            width={glassWidth}
                        height={scaledHeight * overheadRatio - glassInset}
                        fill={getPanelFill(singleOverheadPartWindowType === 'fixed-panel' ? 'fixed-panel' : (windowType?.id === 'fixed-panel' ? 'fixed-panel' : 'other'), panelOverheadColors[0])}
                        style={{ cursor: hoveredOverheadPartIndex === 0 ? 'pointer' : 'default', pointerEvents: 'auto' }}
                        onMouseEnter={() => setHoveredOverheadPartIndex(0)}
                        onMouseLeave={() => setHoveredOverheadPartIndex(null)}
                        onClick={(e) => {
                          e.stopPropagation();
                          // If clicking the same part, reset dialogType first to trigger useEffect
                          if (selectedOverheadPartIndex === 0 && dialogType === 'overhead') {
                            setDialogType(null);
                            setTimeout(() => {
                              setSelectedPartIndex(null);
                              setSelectedUnderlightPartIndex(null);
                              setIsUnderlightPart(false);
                          setSelectedOverheadPartIndex(0);
                              setIsOverheadPart(true);
                              setDialogType('overhead');
                            }, 0);
                          } else {
                            // Clear all other selections first
                          setSelectedPartIndex(null);
                            setSelectedUnderlightPartIndex(null);
                            setIsUnderlightPart(false);
                            // Set overhead part selection and dialog type together
                            setSelectedOverheadPartIndex(0);
                          setIsOverheadPart(true);
                            setDialogType('overhead');
                          }
                        }}
                      >
                        <title>{t('drawing.clickToConfigureOverheadSettings')}</title>
                      </rect>
                      {/* Panel lines for single overhead part */}
                      {(singleOverheadPartWindowType === 'fixed-panel' || windowType?.id === 'fixed-panel') && renderPanelLines(
                        panelOverheadLineTypes[0] || 'none',
                        glassX,
                        startY + glassInset,
                        glassWidth,
                        scaledHeight * overheadRatio - glassInset,
                        getPanelFill(singleOverheadPartWindowType === 'fixed-panel' ? 'fixed-panel' : (windowType?.id === 'fixed-panel' ? 'fixed-panel' : 'other'), panelOverheadColors[0])
                      )}
                      {(singleOverheadPartWindowType !== 'fixed-panel' && windowType?.id !== 'fixed-panel') && glassTexturePattern && (
                        <rect
                          x={glassX}
                          y={startY + glassInset}
                          width={glassWidth}
                          height={scaledHeight * overheadRatio - glassInset}
                          fill={glassTexturePattern}
                          opacity="1"
                          pointerEvents="none"
                        />
                      )}
                    </g>
                    {/* Ventilator for single overhead part when Fixed Sash and enabled */}
                    {singleOverheadPartWindowType === 'fixed-sash' && ventilatorOverheadParts[0] && (
                      <g pointerEvents="none">
                        {(() => {
                          const ventilatorInnerFrameX = glassX;
                          const ventilatorInnerFrameWidth = glassWidth;

                          const ventilatorMarginX = 0;
                          const ventilatorHeight = frameThickness * 0.8;
                          const ventilatorX = ventilatorInnerFrameX + ventilatorMarginX;
                          const ventilatorY = startY + glassInset;
                          const ventilatorWidth = Math.max(0, ventilatorInnerFrameWidth - ventilatorMarginX * 2);
                          const slatCount = 13;
                          const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                          const slats = [];
                          for (let i = 0; i < slatCount; i += 1) {
                            const x = ventilatorX + 3 * scale + i * slatGap;
                            slats.push(
                              <line
                                key={`overhead-single-vent-slat-${i}`}
                                x1={x}
                                y1={ventilatorY + 2 * scale}
                                x2={x}
                                y2={ventilatorY + ventilatorHeight - 2 * scale}
                                stroke="#000000"
                                strokeWidth={1.4 * scale}
                                strokeLinecap="round"
                              />
                            );
                          }
                          return (
                            <>
                              {/* Ventilator body */}
                              <rect
                                x={ventilatorX}
                                y={ventilatorY}
                                width={ventilatorWidth}
                                height={ventilatorHeight}
                                fill="#D5DADF"
                                stroke="#8A929A"
                                strokeWidth={1 * scale}
                                rx={2 * scale}
                                ry={2 * scale}
                              />
                              {/* Dark bottom outline under ventilator */}
                              <line
                                x1={ventilatorX}
                                y1={ventilatorY + ventilatorHeight}
                                x2={ventilatorX + ventilatorWidth}
                                y2={ventilatorY + ventilatorHeight}
                                stroke="#000000"
                                strokeWidth={1.6 * scale}
                              />
                              {/* Slats */}
                              {slats}
                            </>
                          );
                        })()}
                      </g>
                    )}
                    {/* Inner frame for single overhead part */}
                    {hasInnerFrameSingle && (() => {
                      const innerFrameX = glassX;
                      const innerFrameY = startY + glassInset;
                      const innerFrameWidth = glassWidth;
                      // Height should fit within overhead area, ensuring it doesn't extend beyond the horizontal divider
                      // The divider is at startY + scaledHeight * overheadRatio, so inner frame should stop before the divider center
                      const dividerY = startY + scaledHeight * overheadRatio;
                      // Stop inner frame before the divider center (accounting for half the divider thickness)
                      const maxInnerFrameHeight = (dividerY - mullionThickness / 2) - innerFrameY;
                      const innerFrameHeight = Math.min(scaledHeight * overheadRatio - glassInset, maxInnerFrameHeight);
                      const innerGlassX = innerFrameX + innerGlassInset;
                      const innerGlassY = innerFrameY + innerGlassInset;
                      const innerGlassWidth = innerFrameWidth - innerGlassInset * 2;
                      const innerGlassHeight = innerFrameHeight - innerGlassInset * 2;
                      
                      return (
                        <g style={{ pointerEvents: 'none' }}>
                          {/* Outer frame (dark) */}
                          <rect
                            x={innerFrameX}
                            y={innerFrameY}
                            width={innerFrameWidth}
                            height={innerFrameHeight}
                            fill={interiorFrameColorValue}
                            pointerEvents="none"
                          />
                          {renderInnerSashReveal({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerFrameThickness })}
                          {renderInnerSashWoodJoints({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerGlassX, innerGlassWidth, innerFrameThickness })}
                          {/* Spacer / glazing bead */}
                          <rect
                            x={innerFrameX + innerFrameThickness}
                            y={innerFrameY + innerFrameThickness}
                            width={innerFrameWidth - innerFrameThickness * 2}
                            height={innerFrameHeight - innerFrameThickness * 2}
                            fill="#282C30"
                            pointerEvents="none"
                          />
                          {/* Inner glass with gradient base and texture overlay */}
                          <g pointerEvents="none">
                            <rect
                              x={innerGlassX}
                              y={innerGlassY}
                              width={innerGlassWidth}
                              height={innerGlassHeight}
                              fill={glassFill}
          />
                            {glassTexturePattern && (
                              <rect
                                x={innerGlassX}
                                y={innerGlassY}
                                width={innerGlassWidth}
                                height={innerGlassHeight}
                                fill={glassTexturePattern}
                                opacity="1"
                              />
                            )}
                          </g>
                          {/* Diagonal lines for Tilt Only */}
                          {singleOverheadPartWindowType === 'tilt-only' && (
                            <>
                              <line
                                x1={innerGlassX}
                                y1={innerGlassY + innerGlassHeight}
                                x2={innerGlassX + innerGlassWidth / 2}
                                y2={innerGlassY}
                                stroke="#999999"
                                strokeWidth="3"
                                strokeDasharray="3"
                                pointerEvents="none"
                              />
                              <line
                                x1={innerGlassX + innerGlassWidth}
                                y1={innerGlassY + innerGlassHeight}
                                x2={innerGlassX + innerGlassWidth / 2}
                                y2={innerGlassY}
                                stroke="#999999"
                                strokeWidth="3"
                                strokeDasharray="3"
                                pointerEvents="none"
                              />
                            </>
                          )}
                          {/* Diagonal lines for Tilt & Turn Left */}
                          {singleOverheadPartWindowType === 'tilt-turn-left' && (
                            <>
                              <line
                                x1={innerGlassX}
                                y1={innerGlassY + innerGlassHeight}
                                x2={innerGlassX + innerGlassWidth / 2}
                                y2={innerGlassY}
                                stroke="#999999"
                                strokeWidth="3"
                                strokeDasharray="3"
                                pointerEvents="none"
                              />
                              <line
                                x1={innerGlassX + innerGlassWidth}
                                y1={innerGlassY + innerGlassHeight}
                                x2={innerGlassX + innerGlassWidth / 2}
                                y2={innerGlassY}
                                stroke="#999999"
                                strokeWidth="3"
                                strokeDasharray="3"
                                pointerEvents="none"
                              />
                              <line
                                x1={innerGlassX + innerGlassWidth}
                                y1={innerGlassY}
                                x2={innerGlassX}
                                y2={innerGlassY + innerGlassHeight / 2}
                                stroke="#999999"
                                strokeWidth="3"
                                strokeDasharray="3"
                                pointerEvents="none"
                              />
                              <line
                                x1={innerGlassX + innerGlassWidth}
                                y1={innerGlassY + innerGlassHeight}
                                x2={innerGlassX}
                                y2={innerGlassY + innerGlassHeight / 2}
                                stroke="#999999"
                                strokeWidth="3"
                                strokeDasharray="3"
                                pointerEvents="none"
                              />
                            </>
                          )}
                          {/* Diagonal lines for Tilt & Turn Right */}
                          {singleOverheadPartWindowType === 'tilt-turn-right' && (
                            <>
                              <line
                                x1={innerGlassX}
                                y1={innerGlassY + innerGlassHeight}
                                x2={innerGlassX + innerGlassWidth / 2}
                                y2={innerGlassY}
                                stroke="#999999"
                                strokeWidth="3"
                                strokeDasharray="3"
                                pointerEvents="none"
                              />
                              <line
                                x1={innerGlassX + innerGlassWidth}
                                y1={innerGlassY + innerGlassHeight}
                                x2={innerGlassX + innerGlassWidth / 2}
                                y2={innerGlassY}
                                stroke="#999999"
                                strokeWidth="3"
                                strokeDasharray="3"
                                pointerEvents="none"
                              />
                              <line
                                x1={innerGlassX}
                                y1={innerGlassY}
                                x2={innerGlassX + innerGlassWidth}
                                y2={innerGlassY + innerGlassHeight / 2}
                                stroke="#999999"
                                strokeWidth="3"
                                strokeDasharray="3"
                                pointerEvents="none"
                              />
                              <line
                                x1={innerGlassX}
                                y1={innerGlassY + innerGlassHeight}
                                x2={innerGlassX + innerGlassWidth}
                                y2={innerGlassY + innerGlassHeight / 2}
                                stroke="#999999"
                                strokeWidth="3"
                                strokeDasharray="3"
                                pointerEvents="none"
                              />
                            </>
                          )}
                        </g>
                      );
                    })()}
                    {/* Hover highlight overlay for single overhead part */}
                    {hoveredOverheadPartIndex === 0 && (
                      <rect
                        x={glassX}
                        y={startY + glassInset}
                        width={glassWidth}
                        height={scaledHeight * overheadRatio - glassInset}
                        fill="rgba(255, 165, 0, 0.15)"
                        stroke="rgba(255, 140, 0, 0.6)"
                        strokeWidth="2"
                        style={{ pointerEvents: 'none', cursor: 'pointer' }}
                      />
                    )}
                  </>
                );
              })()}
            </g>
          )
        ) : (hasPerPartOverhead ? (
            // Per-part overhead (from part settings): one segment per main part that has "With Overhead"
            Array.from({ length: parts }, (_, index) => {
              if (!overheadPerPart[index]) return null;
              const startAccum = normalizedPartWidths.slice(0, index).reduce((sum, w) => sum + w, 0);
              const partWidthMm = normalizedPartWidths[index];
              const partWidthPx = (partWidthMm / totalPartWidth) * glassWidth;
              const overheadPartX = glassX + (startAccum / totalPartWidth) * glassWidth;
              const overheadPartY = startY + glassInset;
              const overheadPartHeight = scaledHeight * overheadRatio - glassInset;
              const bandConfig = getPartOverheadBandConfig(index);
              const hasLeftMullion = index > 0;
              const hasRightMullion = index < parts - 1;
              const innerX = overheadPartX + (hasLeftMullion ? mullionHalf : 0);
              const innerW = partWidthPx - (hasLeftMullion ? mullionHalf : 0) - (hasRightMullion ? mullionHalf : 0);
              const sectionLayouts = buildPartBandSectionLayouts(
                innerW,
                partWidthMm,
                bandConfig.count,
                bandConfig.widthsMm,
                bandConfig.windowTypes
              );
              const openPartOverheadDialog = (sectionIndex) => {
                setSelectedPartOverheadSubIndex(sectionIndex);
                if (selectedPartOverheadIndex === index && dialogType === 'part-overhead') {
                  setDialogType(null);
                  setSelectedPartOverheadIndex(null);
                  setTimeout(() => {
                    setSelectedPartOverheadIndex(index);
                    setDialogType('part-overhead');
                  }, 0);
                } else {
                  setSelectedPartOverheadIndex(index);
                  setDialogType('part-overhead');
                }
              };
              return (
                <g key={`overhead-per-part-${index}`}>
                  {sectionLayouts.map((layout) => {
                    const secX = innerX + layout.xOffset;
                    const partOverheadWindowType = layout.windowType;
                    const isHoveredOverhead =
                      hoveredPartOverheadIndex === index && hoveredPartOverheadSubIndex === layout.sectionIndex;
                    return (
                      <g key={`overhead-per-part-${index}-section-${layout.sectionIndex}`}>
                        <rect
                          x={secX}
                          y={overheadPartY}
                          width={layout.widthPx}
                          height={overheadPartHeight}
                          fill={getPanelFill(partOverheadWindowType, panelOverheadColors[index] || panelColors[index])}
                          style={{ cursor: isHoveredOverhead ? 'pointer' : 'default' }}
                          onMouseEnter={() => {
                            setHoveredPartOverheadIndex(index);
                            setHoveredPartOverheadSubIndex(layout.sectionIndex);
                          }}
                          onMouseLeave={() => {
                            setHoveredPartOverheadIndex(null);
                            setHoveredPartOverheadSubIndex(null);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openPartOverheadDialog(layout.sectionIndex);
                          }}
                        >
                          <title>{t('drawing.overheadSettings')}</title>
                        </rect>
                        {partOverheadWindowType === 'fixed-panel' && renderPanelLines(
                          panelOverheadLineTypes[index] || panelLineTypes[index] || 'none',
                          secX,
                          overheadPartY,
                          layout.widthPx,
                          overheadPartHeight,
                          getPanelFill(partOverheadWindowType, panelOverheadColors[index] || panelColors[index])
                        )}
                        {partOverheadWindowType !== 'fixed-panel' && glassTexturePattern && (
                          <rect
                            x={secX}
                            y={overheadPartY}
                            width={layout.widthPx}
                            height={overheadPartHeight}
                            fill={glassTexturePattern}
                            opacity="1"
                            pointerEvents="none"
                          />
                        )}
                        {partOverheadWindowType === 'fixed-sash' && ventilatorPartOverhead[index] && bandConfig.count === 1 && (
                          <g pointerEvents="none">
                            {(() => {
                              const ventilatorHeight = frameThickness * 0.8;
                              const ventilatorX = secX;
                              const ventilatorY = overheadPartY;
                              const ventilatorWidth = Math.max(0, layout.widthPx);
                              const slatCount = 13;
                              const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                              const slats = [];
                              for (let i = 0; i < slatCount; i += 1) {
                                const x = ventilatorX + 3 * scale + i * slatGap;
                                slats.push(
                                  <line
                                    key={`overhead-per-part-vent-slat-${index}-${layout.sectionIndex}-${i}`}
                                    x1={x}
                                    y1={ventilatorY + 2 * scale}
                                    x2={x}
                                    y2={ventilatorY + ventilatorHeight - 2 * scale}
                                    stroke="#000000"
                                    strokeWidth={1.4 * scale}
                                    strokeLinecap="round"
                                  />
                                );
                              }
                              return (
                                <>
                                  <rect x={ventilatorX} y={ventilatorY} width={ventilatorWidth} height={ventilatorHeight} fill="#D5DADF" stroke="#8A929A" strokeWidth={1 * scale} rx={2 * scale} ry={2 * scale} />
                                  <line x1={ventilatorX} y1={ventilatorY + ventilatorHeight} x2={ventilatorX + ventilatorWidth} y2={ventilatorY + ventilatorHeight} stroke="#000000" strokeWidth={1.6 * scale} />
                                  <rect x={ventilatorX + 1.5 * scale} y={ventilatorY + 1.5 * scale} width={ventilatorWidth - 3 * scale} height={ventilatorHeight - 3 * scale} fill="url(#panel-gradient)" opacity="0.35" />
                                  {slats}
                                </>
                              );
                            })()}
                          </g>
                        )}
                        {(partOverheadWindowType === 'fixed-glass' || partOverheadWindowType === 'tilt-only' || partOverheadWindowType === 'tilt-turn-left' || partOverheadWindowType === 'tilt-turn-right') &&
                          renderPerPartBandInnerSash(
                            partOverheadWindowType,
                            secX,
                            overheadPartY,
                            layout.widthPx,
                            Math.max(overheadPartHeight - 2, 4)
                          )}
                        {isHoveredOverhead && (
                          <rect
                            x={secX}
                            y={overheadPartY}
                            width={layout.widthPx}
                            height={overheadPartHeight}
                            fill="rgba(255, 165, 0, 0.15)"
                            stroke="rgba(255, 140, 0, 0.6)"
                            strokeWidth="2"
                            style={{ pointerEvents: 'none', cursor: 'pointer' }}
                          />
                        )}
                      </g>
                    );
                  })}
                  {bandConfig.count > 1 &&
                    sectionLayouts.slice(0, -1).map((layout) => {
                      const mullionX = innerX + layout.xOffset + layout.widthPx;
                      const overheadBandDividerY = startY + scaledHeight * overheadRatio;
                      const sectionMullionEndY = overheadBandDividerY - mullionHalf;
                      return (
                        <g key={`overhead-per-part-mullion-${index}-${layout.sectionIndex}`}>
                          {renderVerticalMullionPair(mullionX, overheadPartY, sectionMullionEndY)}
                        </g>
                      );
                    })}
                </g>
              );
            })
          ) : null)
        )}

        {/* Glass area - individual parts for interactivity */}
        {parts > 1 ? (
          // Multiple parts: render each part separately for hover/click
          Array.from({ length: parts }, (_, index) => {
            const startAccum = normalizedPartWidths
              .slice(0, index)
              .reduce((sum, w) => sum + w, 0);
            const partWidthMm = normalizedPartWidths[index];
            const partWidthPx = (partWidthMm / totalPartWidth) * glassWidth;
            const partX = glassX + (startAccum / totalPartWidth) * glassWidth;
            const isHovered = hoveredPartIndex === index;
            // Ensure partWindowTypes array exists and has the right length, default to 'fixed-sash'
            const currentPartWindowTypes = partWindowTypes && partWindowTypes.length === parts 
              ? partWindowTypes 
              : Array(parts).fill('fixed-sash');
            const partWindowType = currentPartWindowTypes[index] || 'fixed-sash';
            const hasInnerFrame = partWindowType === 'fixed-glass' || partWindowType === 'tilt-only' || partWindowType === 'tilt-turn-left' || partWindowType === 'tilt-turn-right';
            const partOverheadHeightInMain = scaledHeight * overheadRatio - glassInset;
            const partUnderlightHeightInMain = scaledHeight * underlightRatio - glassInset;
            // partGlassY/Height: per-part only without overhead = full height; global + part with overhead = below part's second divider; else normal.
            let partGlassY = glassY;
            let partGlassHeight = glassHeight;
            if (overhead !== 'overhead' && hasPerPartOverhead && !overheadPerPart[index]) {
              partGlassY = startY + glassInset;
              partGlassHeight = (glassY + glassHeight) - (startY + glassInset);
            } else if (overhead === 'overhead' && overheadPerPart[index]) {
              partGlassY = glassY + partOverheadHeightInMain + mullionHalf;
              partGlassHeight = glassHeight - partOverheadHeightInMain - mullionHalf;
            }
            if (underlight !== 'underlight' && hasPerPartUnderlight && !underlightPerPart[index]) {
              partGlassHeight = (startY + scaledHeight - glassInset) - partGlassY;
            } else if (underlight !== 'underlight' && hasPerPartUnderlight && underlightPerPart[index]) {
              partGlassHeight = (underlightDividerY - mullionHalf) - partGlassY;
            } else if (underlight === 'underlight' && underlightPerPart[index]) {
              partGlassHeight = glassHeight - partUnderlightHeightInMain - mullionHalf;
            }
            
            return (
              <g key={`glass-part-${index}`}>
                {/* When global overhead + this part has "With Overhead": draw part's overhead segment (between main divider and part's second divider) */}
                {overhead === 'overhead' && overheadPerPart[index] && (() => {
                  const bandConfig = getPartOverheadBandConfig(index);
                  const hasLeftMullion = index > 0;
                  const hasRightMullion = index < parts - 1;
                  const innerX = partX + (hasLeftMullion ? mullionHalf : 0);
                  const innerW = partWidthPx - (hasLeftMullion ? mullionHalf : 0) - (hasRightMullion ? mullionHalf : 0);
                  const sectionLayouts = buildPartBandSectionLayouts(
                    innerW,
                    partWidthMm,
                    bandConfig.count,
                    bandConfig.widthsMm,
                    bandConfig.windowTypes
                  );
                  const openPartOverheadDialog = (sectionIndex) => {
                    setSelectedPartOverheadSubIndex(sectionIndex);
                    if (selectedPartOverheadIndex === index && dialogType === 'part-overhead') {
                      setDialogType(null);
                      setSelectedPartOverheadIndex(null);
                      setTimeout(() => {
                        setSelectedPartOverheadIndex(index);
                        setDialogType('part-overhead');
                      }, 0);
                    } else {
                      setSelectedPartOverheadIndex(index);
                      setDialogType('part-overhead');
                    }
                  };
                  return (
                    <g>
                      {sectionLayouts.map((layout) => {
                        const secX = innerX + layout.xOffset;
                        const partOverheadWindowType = layout.windowType;
                        const isHoveredOverhead =
                          hoveredPartOverheadIndex === index && hoveredPartOverheadSubIndex === layout.sectionIndex;
                        return (
                          <g key={`global-part-overhead-${index}-section-${layout.sectionIndex}`}>
                            <rect
                              x={secX}
                              y={glassY}
                              width={layout.widthPx}
                              height={partOverheadHeightInMain}
                              fill={getPanelFill(partOverheadWindowType, panelOverheadColors[index] || panelColors[index])}
                              style={{ cursor: isHoveredOverhead ? 'pointer' : 'default' }}
                              onMouseEnter={() => {
                                setHoveredPartOverheadIndex(index);
                                setHoveredPartOverheadSubIndex(layout.sectionIndex);
                              }}
                              onMouseLeave={() => {
                                setHoveredPartOverheadIndex(null);
                                setHoveredPartOverheadSubIndex(null);
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openPartOverheadDialog(layout.sectionIndex);
                              }}
                            >
                              <title>{t('drawing.overheadSettings')}</title>
                            </rect>
                            {partOverheadWindowType === 'fixed-panel' && renderPanelLines(
                              panelOverheadLineTypes[index] || panelLineTypes[index] || 'none',
                              secX,
                              glassY,
                              layout.widthPx,
                              partOverheadHeightInMain,
                              getPanelFill(partOverheadWindowType, panelOverheadColors[index] || panelColors[index])
                            )}
                            {partOverheadWindowType !== 'fixed-panel' && glassTexturePattern && (
                              <rect
                                x={secX}
                                y={glassY}
                                width={layout.widthPx}
                                height={partOverheadHeightInMain}
                                fill={glassTexturePattern}
                                opacity="1"
                                pointerEvents="none"
                              />
                            )}
                            {partOverheadWindowType === 'fixed-sash' && ventilatorPartOverhead[index] && bandConfig.count === 1 && (
                              <g pointerEvents="none">
                                {(() => {
                                  const vh = frameThickness * 0.8;
                                  const slatCount = 13;
                                  const slatGap = (layout.widthPx - 6 * scale) / (slatCount - 1 || 1);
                                  return (
                                    <>
                                      <rect x={secX} y={glassY} width={layout.widthPx} height={vh} fill="#D5DADF" stroke="#8A929A" strokeWidth={1 * scale} rx={2 * scale} ry={2 * scale} />
                                      <line x1={secX} y1={glassY + vh} x2={secX + layout.widthPx} y2={glassY + vh} stroke="#000000" strokeWidth={1.6 * scale} />
                                      {Array.from({ length: slatCount }, (_, i) => {
                                        const x = secX + 3 * scale + i * slatGap;
                                        return <line key={i} x1={x} y1={glassY + 2 * scale} x2={x} y2={glassY + vh - 2 * scale} stroke="#000000" strokeWidth={1.4 * scale} strokeLinecap="round" />;
                                      })}
                                    </>
                                  );
                                })()}
                              </g>
                            )}
                            {(partOverheadWindowType === 'fixed-glass' || partOverheadWindowType === 'tilt-only' || partOverheadWindowType === 'tilt-turn-left' || partOverheadWindowType === 'tilt-turn-right') &&
                              renderPerPartBandInnerSash(
                                partOverheadWindowType,
                                secX,
                                glassY,
                                layout.widthPx,
                                Math.max(partOverheadHeightInMain - 2, 4)
                              )}
                            {isHoveredOverhead && (
                              <rect
                                x={secX}
                                y={glassY}
                                width={layout.widthPx}
                                height={partOverheadHeightInMain}
                                fill="rgba(255, 165, 0, 0.15)"
                                stroke="rgba(255, 140, 0, 0.6)"
                                strokeWidth="2"
                                style={{ pointerEvents: 'none', cursor: 'pointer' }}
                              />
                            )}
                          </g>
                        );
                      })}
                      {bandConfig.count > 1 &&
                        sectionLayouts.slice(0, -1).map((layout) => {
                          const mullionX = innerX + layout.xOffset + layout.widthPx;
                          const partOverheadDividerY = glassY + partOverheadHeightInMain;
                          const sectionMullionEndY = partOverheadDividerY - mullionHalf;
                          return (
                            <g key={`global-part-overhead-mullion-${index}-${layout.sectionIndex}`}>
                              {renderVerticalMullionPair(mullionX, glassY, sectionMullionEndY)}
                            </g>
                          );
                        })}
                    </g>
                  );
                })()}
                <g>
                <rect
                  x={partX}
                  y={partGlassY}
                  width={partWidthPx}
                  height={partGlassHeight}
                    fill={getPanelFill(partWindowType, panelColors[index])}
                  style={{ cursor: isHovered ? 'pointer' : 'default' }}
                  onMouseEnter={() => setHoveredPartIndex(index)}
                  onMouseLeave={() => setHoveredPartIndex(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      // If clicking the same part, reset dialogType first to trigger useEffect
                      if (selectedPartIndex === index && dialogType === 'main') {
                        setDialogType(null);
                        setTimeout(() => {
                          setSelectedOverheadPartIndex(null);
                          setSelectedUnderlightPartIndex(null);
                          setIsOverheadPart(false);
                          setIsUnderlightPart(false);
                    setSelectedPartIndex(index);
                          setDialogType('main');
                        }, 0);
                      } else {
                        // Clear all other selections first
                    setSelectedOverheadPartIndex(null);
                        setSelectedUnderlightPartIndex(null);
                    setIsOverheadPart(false);
                        setIsUnderlightPart(false);
                        // Set main part selection and dialog type together
                        setSelectedPartIndex(index);
                        setDialogType('main');
                      }
                    }}
                  >
                    <title>{t('drawing.clickToConfigurePart', { number: index + 1 })}</title>
                </rect>
                  {/* Panel lines for main parts */}
                  {partWindowType === 'fixed-panel' && renderPanelLines(
                    panelLineTypes[index] || 'none',
                    partX,
                    partGlassY,
                    partWidthPx,
                    partGlassHeight,
                    getPanelFill(partWindowType, panelColors[index])
                  )}
                  {partWindowType !== 'fixed-panel' && glassTexturePattern && (
                    <rect
                      x={partX}
                      y={partGlassY}
                      width={partWidthPx}
                      height={partGlassHeight}
                      fill={glassTexturePattern}
                      opacity="1"
                      pointerEvents="none"
                    />
                  )}
                </g>
                {/* Ventilator at top of inner frame when enabled for fixed-sash */}
                {partWindowType === 'fixed-sash' && ventilatorParts[index] && (
                  <g pointerEvents="none">
                    {(() => {
                      // Match the inner frame width so the ventilator touches both left and right inner borders
                      const hasLeftMullion = index > 0;
                      const hasRightMullion = index < parts - 1;
                      const ventilatorInnerFrameX = partX + (hasLeftMullion ? mullionHalf : 0);
                      const ventilatorInnerFrameWidth = partWidthPx - (hasLeftMullion ? mullionHalf : 0) - (hasRightMullion ? mullionHalf : 0);

                      const ventilatorMarginX = 0; // stretch edge to edge of inner frame
                      const ventilatorHeight = frameThickness * 0.8; // taller, more visible
                      const ventilatorX = ventilatorInnerFrameX + ventilatorMarginX;
                      const ventilatorY = partGlassY;
                      const ventilatorWidth = Math.max(0, ventilatorInnerFrameWidth - ventilatorMarginX * 2);
                      const slatCount = 13;
                      const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                      const slats = [];
                      for (let i = 0; i < slatCount; i += 1) {
                        const x = ventilatorX + 3 * scale + i * slatGap;
                        slats.push(
                          <line
                            key={`vent-slat-${index}-${i}`}
                            x1={x}
                            y1={ventilatorY + 2 * scale}
                            x2={x}
                            y2={ventilatorY + ventilatorHeight - 2 * scale}
                            stroke="#000000"
                            strokeWidth={1.4 * scale}
                            strokeLinecap="round"
                          />
                        );
                      }
                      return (
                        <>
                          {/* Ventilator body */}
                          <rect
                            x={ventilatorX}
                            y={ventilatorY}
                            width={ventilatorWidth}
                            height={ventilatorHeight}
                            fill="#D5DADF"
                            stroke="#8A929A"
                            strokeWidth={1 * scale}
                            rx={2 * scale}
                            ry={2 * scale}
                          />
                          {/* Dark bottom outline under ventilator */}
                          <line
                            x1={ventilatorX}
                            y1={ventilatorY + ventilatorHeight}
                            x2={ventilatorX + ventilatorWidth}
                            y2={ventilatorY + ventilatorHeight}
                            stroke="#000000"
                            strokeWidth={1.6 * scale}
                          />
                          {/* Inner shadow */}
                          <rect
                            x={ventilatorX + 1.5 * scale}
                            y={ventilatorY + 1.5 * scale}
                            width={ventilatorWidth - 3 * scale}
                            height={ventilatorHeight - 3 * scale}
                            fill="url(#panel-gradient)"
                            opacity="0.35"
                          />
                          {/* Slats */}
                          {slats}
                        </>
                      );
                    })()}
                  </g>
                )}
                {/* Inner frame for Fixed Glass - complete frame structure edge-to-edge */}
                {hasInnerFrame && (() => {
                  // Adjust inner frame to account for mullions (use mullionHalf from top level)
                  // If not the first part, there's a mullion on the left
                  const hasLeftMullion = index > 0;
                  // If not the last part, there's a mullion on the right
                  const hasRightMullion = index < parts - 1;
                  
                  // Adjust X position and width to account for mullions
                  const innerFrameX = partX + (hasLeftMullion ? mullionHalf : 0);
                  // When global + this part has per-part overhead: inner frame starts at part's main glass (below part's second divider). When global only or per-part only with overhead: below (main or part) divider. Else partGlassY.
                  const innerFrameY = (overhead === 'overhead' && overheadPerPart[index]) ? partGlassY : partGlassY;
                  const innerFrameWidth = partWidthPx - (hasLeftMullion ? mullionHalf : 0) - (hasRightMullion ? mullionHalf : 0);
                  // Height should fit within this part's glass area
                  const maxInnerFrameHeight = (partGlassY + partGlassHeight) - innerFrameY;
                  let innerFrameHeight;
                  const partUnderlightActive = partHasUnderlight(index);
                  if ((overhead === 'overhead' || (hasPerPartOverhead && overheadPerPart[index])) && partUnderlightActive) {
                    innerFrameHeight = partGlassHeight - mullionThickness + 1;
                  } else if (overhead === 'overhead' || (hasPerPartOverhead && overheadPerPart[index])) {
                    innerFrameHeight = partUnderlightActive ? partGlassHeight - mullionThickness + 1 : partGlassHeight;
                  } else if (effectiveOverhead && partUnderlightActive) {
                    innerFrameHeight = partGlassHeight - mullionThickness + 1;
                  } else if (effectiveOverhead) {
                    innerFrameHeight = partGlassHeight;
                  } else if (partUnderlightActive) {
                    innerFrameHeight = partGlassHeight - mullionThickness / 2 + 0.5;
                  } else {
                    innerFrameHeight = partGlassHeight;
                  }
                  const innerFrameHeightFinal = Math.min(innerFrameHeight, maxInnerFrameHeight);
                  const innerGlassX = innerFrameX + innerGlassInset;
                  const innerGlassY = innerFrameY + innerGlassInset;
                  const innerGlassWidth = innerFrameWidth - innerGlassInset * 2;
                  const innerGlassHeight = innerFrameHeightFinal - innerGlassInset * 2;
                  
                  return (
                    <g style={{ pointerEvents: 'none' }}>
                      {/* Outer frame (dark) */}
                      <rect
                        x={innerFrameX}
                        y={innerFrameY}
                        width={innerFrameWidth}
                        height={innerFrameHeightFinal}
                        fill={interiorFrameColorValue}
                        pointerEvents="none"
                      />
                      {renderInnerSashReveal({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight: innerFrameHeightFinal, innerFrameThickness })}
                      {renderInnerSashWoodJoints({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight: innerFrameHeightFinal, innerGlassX, innerGlassWidth, innerFrameThickness })}
                      {/* Spacer / glazing bead */}
                      <rect
                        x={innerFrameX + innerFrameThickness}
                        y={innerFrameY + innerFrameThickness}
                        width={innerFrameWidth - innerFrameThickness * 2}
                        height={innerFrameHeightFinal - innerFrameThickness * 2}
                        fill="#282C30"
                        pointerEvents="none"
                      />
                      {/* Inner glass with gradient base and texture overlay */}
                      <g pointerEvents="none">
                      <rect
                        x={innerGlassX}
                        y={innerGlassY}
                        width={innerGlassWidth}
                        height={innerGlassHeight}
                          fill={glassFill}
                        />
                        {glassTexturePattern && (
                          <rect
                            x={innerGlassX}
                            y={innerGlassY}
                            width={innerGlassWidth}
                            height={innerGlassHeight}
                            fill={glassTexturePattern}
                            opacity="1"
                      />
                        )}
                      </g>
                      {/* Diagonal lines for Tilt Only - drawn inside inner frame glass area */}
                      {partWindowType === 'tilt-only' && (
                        <>
                          {/* Line from bottom-left to middle-top */}
                          <line
                            x1={innerGlassX}
                            y1={innerGlassY + innerGlassHeight}
                            x2={innerGlassX + innerGlassWidth / 2}
                            y2={innerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                            pointerEvents="none"
                          />
                          {/* Line from bottom-right to middle-top */}
                          <line
                            x1={innerGlassX + innerGlassWidth}
                            y1={innerGlassY + innerGlassHeight}
                            x2={innerGlassX + innerGlassWidth / 2}
                            y2={innerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                            pointerEvents="none"
                          />
                        </>
                      )}
                      {/* Diagonal lines for Tilt & Turn Left - drawn inside inner frame glass area */}
                      {partWindowType === 'tilt-turn-left' && (
                        <>
                          {/* Line from bottom-left to middle-top */}
                          <line
                            x1={innerGlassX}
                            y1={innerGlassY + innerGlassHeight}
                            x2={innerGlassX + innerGlassWidth / 2}
                            y2={innerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                            pointerEvents="none"
                          />
                          {/* Line from bottom-right to middle-top */}
                          <line
                            x1={innerGlassX + innerGlassWidth}
                            y1={innerGlassY + innerGlassHeight}
                            x2={innerGlassX + innerGlassWidth / 2}
                            y2={innerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                            pointerEvents="none"
                          />
                          {/* Line from top-right to middle-left */}
                          <line
                            x1={innerGlassX + innerGlassWidth}
                            y1={innerGlassY}
                            x2={innerGlassX}
                            y2={innerGlassY + innerGlassHeight / 2}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                            pointerEvents="none"
                          />
                          {/* Line from bottom-right to middle-left */}
                          <line
                            x1={innerGlassX + innerGlassWidth}
                            y1={innerGlassY + innerGlassHeight}
                            x2={innerGlassX}
                            y2={innerGlassY + innerGlassHeight / 2}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                            pointerEvents="none"
                          />
                        </>
                      )}
                      {/* Diagonal lines for Tilt & Turn Right - drawn inside inner frame glass area */}
                      {partWindowType === 'tilt-turn-right' && (
                        <>
                          {/* Line from bottom-left to middle-top */}
                          <line
                            x1={innerGlassX}
                            y1={innerGlassY + innerGlassHeight}
                            x2={innerGlassX + innerGlassWidth / 2}
                            y2={innerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                            pointerEvents="none"
                          />
                          {/* Line from bottom-right to middle-top */}
                          <line
                            x1={innerGlassX + innerGlassWidth}
                            y1={innerGlassY + innerGlassHeight}
                            x2={innerGlassX + innerGlassWidth / 2}
                            y2={innerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                            pointerEvents="none"
                          />
                          {/* Line from top-left to middle-right */}
                          <line
                            x1={innerGlassX}
                            y1={innerGlassY}
                            x2={innerGlassX + innerGlassWidth}
                            y2={innerGlassY + innerGlassHeight / 2}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                            pointerEvents="none"
                          />
                          {/* Line from bottom-left to middle-right */}
                          <line
                            x1={innerGlassX}
                            y1={innerGlassY + innerGlassHeight}
                            x2={innerGlassX + innerGlassWidth}
                            y2={innerGlassY + innerGlassHeight / 2}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                            pointerEvents="none"
                          />
                        </>
                      )}
                    </g>
                  );
                })()}
                {/* Hover highlight overlay */}
                {isHovered && (
                  <rect
                    x={partX}
                    y={partGlassY}
                    width={partWidthPx}
                    height={partGlassHeight}
                    fill="rgba(255, 165, 0, 0.15)"
                    stroke="rgba(255, 140, 0, 0.6)"
                    strokeWidth="2"
                    style={{ pointerEvents: 'none', cursor: 'pointer' }}
                  />
                )}
              </g>
            );
          })
        ) : (
          // Single part: render glass with hover and inner frame if Fixed Glass
          (() => {
            const partOverheadHeightInMainSingle = scaledHeight * overheadRatio - glassInset;
            let partGlassYSingle = (overhead !== 'overhead' && hasPerPartOverhead && !overheadPerPart[0]) ? (startY + glassInset) : glassY;
            let partGlassHeightSingle = (overhead !== 'overhead' && hasPerPartOverhead && !overheadPerPart[0]) ? ((glassY + glassHeight) - (startY + glassInset)) : glassHeight;
            if (overhead === 'overhead' && overheadPerPart[0]) {
              partGlassYSingle = glassY + partOverheadHeightInMainSingle + mullionHalf;
              partGlassHeightSingle = glassHeight - partOverheadHeightInMainSingle - mullionHalf;
            }
            if (underlight !== 'underlight' && hasPerPartUnderlight && !underlightPerPart[0]) {
              partGlassHeightSingle = (startY + scaledHeight - glassInset) - partGlassYSingle;
            } else if (underlight !== 'underlight' && hasPerPartUnderlight && underlightPerPart[0]) {
              partGlassHeightSingle = (underlightDividerY - mullionHalf) - partGlassYSingle;
            } else if (underlight === 'underlight' && underlightPerPart[0]) {
              const partUnderlightHeightInMain = scaledHeight * underlightRatio - glassInset;
              partGlassHeightSingle = glassHeight - partUnderlightHeightInMain - mullionHalf;
            }
            return (
          <g>
            {overhead === 'overhead' && overheadPerPart[0] && (() => {
              const index = 0;
              const bandConfig = getPartOverheadBandConfig(index);
              const partWidthMm = normalizedPartWidths[index] ?? width;
              const sectionLayouts = buildPartBandSectionLayouts(
                glassWidth,
                partWidthMm,
                bandConfig.count,
                bandConfig.widthsMm,
                bandConfig.windowTypes
              );
              const openPartOverheadDialog = (sectionIndex) => {
                setSelectedPartOverheadSubIndex(sectionIndex);
                if (selectedPartOverheadIndex === index && dialogType === 'part-overhead') {
                  setDialogType(null);
                  setSelectedPartOverheadIndex(null);
                  setTimeout(() => {
                    setSelectedPartOverheadIndex(index);
                    setDialogType('part-overhead');
                  }, 0);
                } else {
                  setSelectedPartOverheadIndex(index);
                  setDialogType('part-overhead');
                }
              };
              return (
                <g>
                  {sectionLayouts.map((layout) => {
                    const secX = glassX + layout.xOffset;
                    const partOverheadWindowType = layout.windowType;
                    const isHoveredOverhead =
                      hoveredPartOverheadIndex === index && hoveredPartOverheadSubIndex === layout.sectionIndex;
                    return (
                      <g key={`global-part-overhead-single-section-${layout.sectionIndex}`}>
                        <rect
                          x={secX}
                          y={glassY}
                          width={layout.widthPx}
                          height={partOverheadHeightInMainSingle}
                          fill={getPanelFill(partOverheadWindowType, panelOverheadColors[index] || panelColors[index])}
                          style={{ cursor: isHoveredOverhead ? 'pointer' : 'default' }}
                          onMouseEnter={() => {
                            setHoveredPartOverheadIndex(index);
                            setHoveredPartOverheadSubIndex(layout.sectionIndex);
                          }}
                          onMouseLeave={() => {
                            setHoveredPartOverheadIndex(null);
                            setHoveredPartOverheadSubIndex(null);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openPartOverheadDialog(layout.sectionIndex);
                          }}
                        >
                          <title>{t('drawing.overheadSettings')}</title>
                        </rect>
                        {partOverheadWindowType === 'fixed-panel' && renderPanelLines(
                          panelOverheadLineTypes[index] || panelLineTypes[index] || 'none',
                          secX,
                          glassY,
                          layout.widthPx,
                          partOverheadHeightInMainSingle,
                          getPanelFill(partOverheadWindowType, panelOverheadColors[index] || panelColors[index])
                        )}
                        {partOverheadWindowType !== 'fixed-panel' && glassTexturePattern && (
                          <rect
                            x={secX}
                            y={glassY}
                            width={layout.widthPx}
                            height={partOverheadHeightInMainSingle}
                            fill={glassTexturePattern}
                            opacity="1"
                            pointerEvents="none"
                          />
                        )}
                        {partOverheadWindowType === 'fixed-sash' && ventilatorPartOverhead[index] && bandConfig.count === 1 && (
                          <g pointerEvents="none">
                            {(() => {
                              const vh = frameThickness * 0.8;
                              const slatCount = 13;
                              const slatGap = (layout.widthPx - 6 * scale) / (slatCount - 1 || 1);
                              return (
                                <>
                                  <rect x={secX} y={glassY} width={layout.widthPx} height={vh} fill="#D5DADF" stroke="#8A929A" strokeWidth={1 * scale} rx={2 * scale} ry={2 * scale} />
                                  <line x1={secX} y1={glassY + vh} x2={secX + layout.widthPx} y2={glassY + vh} stroke="#000000" strokeWidth={1.6 * scale} />
                                  {Array.from({ length: slatCount }, (_, i) => {
                                    const x = secX + 3 * scale + i * slatGap;
                                    return <line key={i} x1={x} y1={glassY + 2 * scale} x2={x} y2={glassY + vh - 2 * scale} stroke="#000000" strokeWidth={1.4 * scale} strokeLinecap="round" />;
                                  })}
                                </>
                              );
                            })()}
                          </g>
                        )}
                        {(partOverheadWindowType === 'fixed-glass' || partOverheadWindowType === 'tilt-only' || partOverheadWindowType === 'tilt-turn-left' || partOverheadWindowType === 'tilt-turn-right') &&
                          renderPerPartBandInnerSash(
                            partOverheadWindowType,
                            secX,
                            glassY,
                            layout.widthPx,
                            Math.max(partOverheadHeightInMainSingle - 2, 4)
                          )}
                        {isHoveredOverhead && (
                          <rect
                            x={secX}
                            y={glassY}
                            width={layout.widthPx}
                            height={partOverheadHeightInMainSingle}
                            fill="rgba(255, 165, 0, 0.15)"
                            stroke="rgba(255, 140, 0, 0.6)"
                            strokeWidth="2"
                            style={{ pointerEvents: 'none', cursor: 'pointer' }}
                          />
                        )}
                      </g>
                    );
                  })}
                  {bandConfig.count > 1 &&
                    sectionLayouts.slice(0, -1).map((layout) => {
                      const mullionX = glassX + layout.xOffset + layout.widthPx;
                      const partOverheadDividerY = glassY + partOverheadHeightInMainSingle;
                      const sectionMullionEndY = partOverheadDividerY - mullionHalf;
                      return (
                        <g key={`global-part-overhead-single-mullion-${layout.sectionIndex}`}>
                          {renderVerticalMullionPair(mullionX, glassY, sectionMullionEndY)}
                        </g>
                      );
                    })}
                </g>
              );
            })()}
            {/* Glass rect - render first as background, interactive */}
            <g>
            <rect
              x={glassX}
              y={partGlassYSingle}
              width={glassWidth}
              height={partGlassHeightSingle}
                fill={getPanelFill((partWindowTypes?.[0] === 'fixed-panel' || windowType?.id === 'fixed-panel') ? 'fixed-panel' : 'other', panelColors[0])}
              style={{ cursor: hoveredPartIndex === 0 ? 'pointer' : 'default', pointerEvents: 'auto' }}
              onMouseEnter={() => setHoveredPartIndex(0)}
              onMouseLeave={() => setHoveredPartIndex(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  // If clicking the same part, reset dialogType first to trigger useEffect
                  if (selectedPartIndex === 0 && dialogType === 'main') {
                    setDialogType(null);
                    setTimeout(() => {
                      setSelectedOverheadPartIndex(null);
                      setSelectedUnderlightPartIndex(null);
                      setIsOverheadPart(false);
                      setIsUnderlightPart(false);
                setSelectedPartIndex(0);
                      setDialogType('main');
                    }, 0);
                  } else {
                    // Clear all other selections first
                setSelectedOverheadPartIndex(null);
                    setSelectedUnderlightPartIndex(null);
                setIsOverheadPart(false);
                    setIsUnderlightPart(false);
                    // Set main part selection and dialog type together
                    setSelectedPartIndex(0);
                    setDialogType('main');
                  }
                }}
              >
                <title>{t('drawing.clickToConfigureWindow')}</title>
            </rect>
              {/* Panel lines for single part */}
              {(partWindowTypes?.[0] === 'fixed-panel' || windowType?.id === 'fixed-panel') && renderPanelLines(
                panelLineTypes[0] || 'none',
                glassX,
                partGlassYSingle,
                glassWidth,
                partGlassHeightSingle,
                getPanelFill((partWindowTypes?.[0] === 'fixed-panel' || windowType?.id === 'fixed-panel') ? 'fixed-panel' : 'other', panelColors[0])
              )}
              {(partWindowTypes?.[0] !== 'fixed-panel' && windowType?.id !== 'fixed-panel') && glassTexturePattern && (
                <rect
                  x={glassX}
                  y={partGlassYSingle}
                  width={glassWidth}
                  height={partGlassHeightSingle}
                  fill={glassTexturePattern}
                  opacity="1"
                  pointerEvents="none"
                />
              )}
            </g>
            {/* Ventilator for single-part fixed-sash window */}
            {((partWindowTypes?.[0] || windowType?.id || 'fixed-sash') === 'fixed-sash') && ventilatorParts[0] && (
              <g pointerEvents="none">
                {(() => {
                  // Single-part inner frame spans whole glass area, so use that for edge-to-edge ventilator
                  const ventilatorInnerFrameX = glassX;
                  const ventilatorInnerFrameWidth = glassWidth;

                  const ventilatorMarginX = 0;
                  const ventilatorHeight = frameThickness * 0.8; // taller, more visible
                  const ventilatorX = ventilatorInnerFrameX + ventilatorMarginX;
                  const ventilatorY = partGlassYSingle;
                  const ventilatorWidth = Math.max(0, ventilatorInnerFrameWidth - ventilatorMarginX * 2);
                  const slatCount = 13;
                  const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                  const slats = [];
                  for (let i = 0; i < slatCount; i += 1) {
                    const x = ventilatorX + 3 * scale + i * slatGap;
                    slats.push(
                      <line
                        key={`vent-slat-single-${i}`}
                        x1={x}
                        y1={ventilatorY + 2 * scale}
                        x2={x}
                        y2={ventilatorY + ventilatorHeight - 2 * scale}
                        stroke="#000000"
                        strokeWidth={1.4 * scale}
                        strokeLinecap="round"
                      />
                    );
                  }
                  return (
                    <>
                      {/* Ventilator body */}
                      <rect
                        x={ventilatorX}
                        y={ventilatorY}
                        width={ventilatorWidth}
                        height={ventilatorHeight}
                        fill="#D5DADF"
                        stroke="#8A929A"
                        strokeWidth={1 * scale}
                        rx={2 * scale}
                        ry={2 * scale}
                      />
                      {/* Dark bottom outline under ventilator */}
                      <line
                        x1={ventilatorX}
                        y1={ventilatorY + ventilatorHeight}
                        x2={ventilatorX + ventilatorWidth}
                        y2={ventilatorY + ventilatorHeight}
                        stroke="#000000"
                        strokeWidth={1.6 * scale}
                      />
                      {/* Inner shadow */}
                      <rect
                        x={ventilatorX + 1.5 * scale}
                        y={ventilatorY + 1.5 * scale}
                        width={ventilatorWidth - 3 * scale}
                        height={ventilatorHeight - 3 * scale}
                        fill="url(#panel-gradient)"
                        opacity="0.35"
                      />
                      {/* Slats */}
                      {slats}
                    </>
                  );
                })()}
              </g>
            )}
            {/* Inner frame for Fixed Glass, Tilt Only, Tilt & Turn Left, or Tilt & Turn Right (single part) - complete frame structure edge-to-edge - render on top */}
            {((partWindowTypes?.[0] === 'fixed-glass' || windowType?.id === 'fixed-glass') ||
              (partWindowTypes?.[0] === 'tilt-only' || windowType?.id === 'tilt-only') ||
              (partWindowTypes?.[0] === 'tilt-turn-left' || windowType?.id === 'tilt-turn-left') ||
              (partWindowTypes?.[0] === 'tilt-turn-right' || windowType?.id === 'tilt-turn-right')) && (() => {
              // Determine the window type for single part
              const singlePartWindowType = partWindowTypes?.[0] || windowType?.id || 'fixed-sash';
              const innerFrameX = glassX;
              // When global overhead or (per-part and this part has overhead): start below divider; else use part's glass top
              const innerFrameY = partGlassYSingle;
              const innerFrameWidth = glassWidth;
              const maxInnerFrameHeight = (partGlassYSingle + partGlassHeightSingle) - innerFrameY;
              const innerFrameHeight = (overhead === 'overhead' || (hasPerPartOverhead && overheadPerPart[0]))
                ? (hasUnderlight ? partGlassHeightSingle - mullionThickness + 1 : partGlassHeightSingle)
                : (effectiveOverhead && hasUnderlight ? partGlassHeightSingle - mullionThickness + 1 : partGlassHeightSingle);
              const innerFrameHeightFinal = Math.min(innerFrameHeight, maxInnerFrameHeight);
              const innerGlassX = innerFrameX + innerGlassInset;
              const innerGlassY = innerFrameY + innerGlassInset;
              const innerGlassWidth = innerFrameWidth - innerGlassInset * 2;
              const innerGlassHeight = innerFrameHeightFinal - innerGlassInset * 2;
              
              return (
                <g style={{ pointerEvents: 'none' }}>
                  {/* Outer frame (dark) */}
                  <rect
                    x={innerFrameX}
                    y={innerFrameY}
                    width={innerFrameWidth}
                    height={innerFrameHeightFinal}
                    fill={interiorFrameColorValue}
                    pointerEvents="none"
                  />
                  {renderInnerSashReveal({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight: innerFrameHeightFinal, innerFrameThickness })}
                  {renderInnerSashWoodJoints({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight: innerFrameHeightFinal, innerGlassX, innerGlassWidth, innerFrameThickness })}
                  {/* Spacer / glazing bead */}
                  <rect
                    x={innerFrameX + innerFrameThickness}
                    y={innerFrameY + innerFrameThickness}
                    width={innerFrameWidth - innerFrameThickness * 2}
                    height={innerFrameHeightFinal - innerFrameThickness * 2}
                    fill="#282C30"
                    pointerEvents="none"
                  />
                  {/* Inner glass with gradient base and texture overlay */}
                  <g pointerEvents="none">
                  <rect
                    x={innerGlassX}
                    y={innerGlassY}
                    width={innerGlassWidth}
                    height={innerGlassHeight}
                      fill={glassFill}
                    />
                    {glassTexturePattern && (
                      <rect
                        x={innerGlassX}
                        y={innerGlassY}
                        width={innerGlassWidth}
                        height={innerGlassHeight}
                        fill={glassTexturePattern}
                        opacity="1"
                  />
                    )}
                  </g>
                  {/* Diagonal lines for Tilt Only - drawn inside inner frame glass area */}
                  {singlePartWindowType === 'tilt-only' && (
                    <>
                      {/* Line from bottom-left to middle-top */}
                      <line
                        x1={innerGlassX}
                        y1={innerGlassY + innerGlassHeight}
                        x2={innerGlassX + innerGlassWidth / 2}
                        y2={innerGlassY}
                        stroke="#999999"
                        strokeWidth="3"
                        strokeDasharray="3"
                        pointerEvents="none"
                      />
                      {/* Line from bottom-right to middle-top */}
                      <line
                        x1={innerGlassX + innerGlassWidth}
                        y1={innerGlassY + innerGlassHeight}
                        x2={innerGlassX + innerGlassWidth / 2}
                        y2={innerGlassY}
                        stroke="#999999"
                        strokeWidth="3"
                        strokeDasharray="3"
                        pointerEvents="none"
                      />
                    </>
                  )}
                  {/* Diagonal lines for Tilt & Turn Left - drawn inside inner frame glass area */}
                  {singlePartWindowType === 'tilt-turn-left' && (
                    <>
                      {/* Line from bottom-left to middle-top */}
                      <line
                        x1={innerGlassX}
                        y1={innerGlassY + innerGlassHeight}
                        x2={innerGlassX + innerGlassWidth / 2}
                        y2={innerGlassY}
                        stroke="#999999"
                        strokeWidth="3"
                        strokeDasharray="3"
                        pointerEvents="none"
                      />
                      {/* Line from bottom-right to middle-top */}
                      <line
                        x1={innerGlassX + innerGlassWidth}
                        y1={innerGlassY + innerGlassHeight}
                        x2={innerGlassX + innerGlassWidth / 2}
                        y2={innerGlassY}
                        stroke="#999999"
                        strokeWidth="3"
                        strokeDasharray="3"
                        pointerEvents="none"
                      />
                      {/* Line from top-right to middle-left */}
                      <line
                        x1={innerGlassX + innerGlassWidth}
                        y1={innerGlassY}
                        x2={innerGlassX}
                        y2={innerGlassY + innerGlassHeight / 2}
                        stroke="#999999"
                        strokeWidth="3"
                        strokeDasharray="3"
                        pointerEvents="none"
                      />
                      {/* Line from bottom-right to middle-left */}
                      <line
                        x1={innerGlassX + innerGlassWidth}
                        y1={innerGlassY + innerGlassHeight}
                        x2={innerGlassX}
                        y2={innerGlassY + innerGlassHeight / 2}
                        stroke="#999999"
                        strokeWidth="3"
                        strokeDasharray="3"
                        pointerEvents="none"
                      />
                    </>
                  )}
                  {/* Diagonal lines for Tilt & Turn Right - drawn inside inner frame glass area */}
                  {singlePartWindowType === 'tilt-turn-right' && (
                    <>
                      {/* Line from bottom-left to middle-top */}
                      <line
                        x1={innerGlassX}
                        y1={innerGlassY + innerGlassHeight}
                        x2={innerGlassX + innerGlassWidth / 2}
                        y2={innerGlassY}
                        stroke="#999999"
                        strokeWidth="3"
                        strokeDasharray="3"
                        pointerEvents="none"
                      />
                      {/* Line from bottom-right to middle-top */}
                      <line
                        x1={innerGlassX + innerGlassWidth}
                        y1={innerGlassY + innerGlassHeight}
                        x2={innerGlassX + innerGlassWidth / 2}
                        y2={innerGlassY}
                        stroke="#999999"
                        strokeWidth="3"
                        strokeDasharray="3"
                        pointerEvents="none"
                      />
                      {/* Line from top-left to middle-right */}
                      <line
                        x1={innerGlassX}
                        y1={innerGlassY}
                        x2={innerGlassX + innerGlassWidth}
                        y2={innerGlassY + innerGlassHeight / 2}
                        stroke="#999999"
                        strokeWidth="3"
                        strokeDasharray="3"
                        pointerEvents="none"
                      />
                      {/* Line from bottom-left to middle-right */}
                      <line
                        x1={innerGlassX}
                        y1={innerGlassY + innerGlassHeight}
                        x2={innerGlassX + innerGlassWidth}
                        y2={innerGlassY + innerGlassHeight / 2}
                        stroke="#999999"
                        strokeWidth="3"
                        strokeDasharray="3"
                        pointerEvents="none"
                      />
                    </>
                  )}
                </g>
              );
            })()}
            {/* Hover highlight overlay for single part */}
            {hoveredPartIndex === 0 && (
              <rect
                x={glassX}
                y={partGlassYSingle}
                width={glassWidth}
                height={partGlassHeightSingle}
                fill="rgba(255, 165, 0, 0.15)"
                stroke="rgba(255, 140, 0, 0.6)"
                strokeWidth="2"
                style={{ pointerEvents: 'none', cursor: 'pointer' }}
              />
            )}
          </g>
        );
      })() )
        }

        {/* Vertical divisions for parts (mullions) */}
        {parts > 1 &&
          Array.from({ length: parts - 1 }, (_, index) => {
            const accumulatedWidth = normalizedPartWidths
              .slice(0, index + 1)
              .reduce((sum, w) => sum + w, 0);
            const fraction = accumulatedWidth / totalPartWidth;
            const x = glassX + glassWidth * fraction;
            // When global overhead: mullions start below transom. When global underlight: stop above transom.
            const mullionStartY = (overhead === 'overhead') ? glassY : (startY + glassInset);
            const mullionEndY = (underlight === 'underlight' && !hasPerPartUnderlight)
              ? underlightDividerY - mullionHalf
              : (startY + scaledHeight - glassInset);
            return (
              <g key={`mullion-${index}`}>
                {renderVerticalMullionPair(x, mullionStartY, mullionEndY)}
              </g>
            );
          })}

        {/* Vertical divisions for overhead parts (mullions) — before horizontal transom so transom paints on top at junction */}
        {overhead === 'overhead' && overheadParts > 1 &&
          Array.from({ length: overheadParts - 1 }, (_, index) => {
            const accumulatedWidth = normalizedOverheadPartWidths
              .slice(0, index + 1)
              .reduce((sum, w) => sum + w, 0);
            const fraction = accumulatedWidth / totalOverheadPartWidth;
            const x = glassX + glassWidth * fraction;
            const overheadMullionStartY = startY + glassInset;
            const dividerY = startY + scaledHeight * overheadRatio;
            const overheadMullionEndY = dividerY - mullionThickness / 2;
            return (
              <g key={`overhead-mullion-${index}`}>
                {renderVerticalMullionPair(x, overheadMullionStartY, overheadMullionEndY)}
              </g>
            );
          })}

        {/* Horizontal divider: full width for global overhead (left panel), per-part segments inside verticals for per-part overhead */}
        {effectiveOverhead && (() => {
          const dividerY = startY + scaledHeight * overheadRatio;
          const renderDividerSegment = (x1, x2) => renderHorizontalDividerEdges(x1, x2, dividerY);
          if (overhead === 'overhead') {
            const partOverheadHeightInMain = scaledHeight * overheadRatio - glassInset;
            const partDividerY = glassY + partOverheadHeightInMain;
            return (
              <g>
                {/* Main horizontal divider (full width) */}
                {renderDividerSegment(glassX, glassX + glassWidth)}
                {/* Second horizontal divider(s) for parts that have "With Overhead" - each inside its column only */}
                {hasPerPartOverhead && Array.from({ length: parts }, (_, index) => {
                  if (!overheadPerPart[index]) return null;
                  const startAccum = normalizedPartWidths.slice(0, index).reduce((sum, w) => sum + w, 0);
                  const partWidthMm = normalizedPartWidths[index];
                  const partWidthPx = (partWidthMm / totalPartWidth) * glassWidth;
                  const partX = glassX + (startAccum / totalPartWidth) * glassWidth;
                  const segX1 = partX + (index > 0 ? mullionHalf : 0);
                  const segX2 = partX + partWidthPx - (index < parts - 1 ? mullionHalf : 0);
                  return (
                    <g key={`part-divider-${index}`}>
                      {renderHorizontalMullionPair(segX1, segX2, partDividerY)}
                    </g>
                  );
                })}
              </g>
            );
          }
          if (hasPerPartOverhead) {
            return (
              <g>
                {Array.from({ length: parts }, (_, index) => {
                  if (!overheadPerPart[index]) return null;
                  const startAccum = normalizedPartWidths.slice(0, index).reduce((sum, w) => sum + w, 0);
                  const partWidthMm = normalizedPartWidths[index];
                  const partWidthPx = (partWidthMm / totalPartWidth) * glassWidth;
                  const partX = glassX + (startAccum / totalPartWidth) * glassWidth;
                  // Inset so horizontal line sits inside vertical mullions, not on top of them
                  const segX1 = partX + (index > 0 ? mullionHalf : 0);
                  const segX2 = partX + partWidthPx - (index < parts - 1 ? mullionHalf : 0);
                  return <g key={`overhead-divider-${index}`}>{renderDividerSegment(segX1, segX2)}</g>;
                })}
              </g>
            );
          }
          return null;
        })()}

        {/* Horizontal divider line for underlight (bottom fixed section) - full width or per-part segments.
            Global underlight vertical mullions are drawn AFTER underlight glass (see below) so they stay visible. */}
        {effectiveUnderlight && (() => {
          const dividerY = underlightDividerY;
          const renderUnderlightDividerSegment = (x1, x2) => renderHorizontalDividerEdges(x1, x2, dividerY);
          if (hasPerPartUnderlight) {
            return (
              <g>
                {Array.from({ length: parts }, (_, index) => {
                  if (!partHasUnderlight(index)) return null;
                  const startAccum = normalizedPartWidths.slice(0, index).reduce((sum, w) => sum + w, 0);
                  const partWidthMm = normalizedPartWidths[index];
                  const partWidthPx = (partWidthMm / totalPartWidth) * glassWidth;
                  const partX = glassX + (startAccum / totalPartWidth) * glassWidth;
                  const segX1 = partX + (index > 0 ? mullionHalf : 0);
                  const segX2 = partX + partWidthPx - (index < parts - 1 ? mullionHalf : 0);
                  return (
                    <g key={`underlight-divider-${index}`}>
                      {renderUnderlightDividerSegment(segX1, segX2)}
                    </g>
                  );
                })}
              </g>
            );
          }
          if (underlight === 'underlight') {
            return (
              <g>
                {renderUnderlightDividerSegment(glassX, glassX + glassWidth)}
              </g>
            );
          }
          return null;
        })()}

        {/* Per-part underlight (from part settings) — drawn after main glass so window types stay visible */}
        {underlight !== 'underlight' && hasPerPartUnderlight &&
          Array.from({ length: parts }, (_, index) => {
            if (!underlightPerPart[index]) return null;
            const startAccum = normalizedPartWidths.slice(0, index).reduce((sum, w) => sum + w, 0);
            const partWidthMm = normalizedPartWidths[index];
            const partWidthPx = (partWidthMm / totalPartWidth) * glassWidth;
            const underlightPartX = glassX + (startAccum / totalPartWidth) * glassWidth;
            const underlightPartY = underlightDividerY + mullionHalf;
            const underlightPartHeight = startY + scaledHeight - glassInset - underlightPartY;
            const bandConfig = getPartUnderlightBandConfig(index);
            const panelColor = panelUnderlightColors[index] ?? panelColors[index];
            const hasLeftMullion = index > 0;
            const hasRightMullion = index < parts - 1;
            const innerX = underlightPartX + (hasLeftMullion ? mullionHalf : 0);
            const innerW = partWidthPx - (hasLeftMullion ? mullionHalf : 0) - (hasRightMullion ? mullionHalf : 0);
            const sectionLayouts = buildPartBandSectionLayouts(
              innerW,
              partWidthMm,
              bandConfig.count,
              bandConfig.widthsMm,
              bandConfig.windowTypes
            );
            const openPartUnderlightDialog = (sectionIndex) => {
              setSelectedPartUnderlightSubIndex(sectionIndex);
              if (selectedPartUnderlightIndex === index && dialogType === 'part-underlight') {
                setDialogType(null);
                setSelectedPartUnderlightIndex(null);
                setTimeout(() => {
                  setSelectedPartUnderlightIndex(index);
                  setDialogType('part-underlight');
                }, 0);
              } else {
                setSelectedPartUnderlightIndex(index);
                setDialogType('part-underlight');
              }
            };
            return (
              <g key={`underlight-per-part-${index}`}>
                {sectionLayouts.map((layout) => {
                  const secX = innerX + layout.xOffset;
                  const partUnderlightWindowType = layout.windowType;
                  const isHoveredUnderlight =
                    hoveredPartUnderlightIndex === index && hoveredPartUnderlightSubIndex === layout.sectionIndex;
                  return (
                    <g key={`underlight-per-part-${index}-section-${layout.sectionIndex}`}>
                      <rect
                        x={secX}
                        y={underlightPartY}
                        width={layout.widthPx}
                        height={underlightPartHeight}
                        fill={getPanelFill(partUnderlightWindowType, panelColor)}
                        style={{ cursor: isHoveredUnderlight ? 'pointer' : 'default' }}
                        onMouseEnter={() => {
                          setHoveredPartUnderlightIndex(index);
                          setHoveredPartUnderlightSubIndex(layout.sectionIndex);
                        }}
                        onMouseLeave={() => {
                          setHoveredPartUnderlightIndex(null);
                          setHoveredPartUnderlightSubIndex(null);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openPartUnderlightDialog(layout.sectionIndex);
                        }}
                      >
                        <title>{t('drawing.underlightSettings')}</title>
                      </rect>
                      {partUnderlightWindowType === 'fixed-panel' && renderPanelLines(
                        panelUnderlightLineTypes[index] || panelLineTypes[index] || 'none',
                        secX,
                        underlightPartY,
                        layout.widthPx,
                        underlightPartHeight,
                        getPanelFill(partUnderlightWindowType, panelColor)
                      )}
                      {partUnderlightWindowType !== 'fixed-panel' && glassTexturePattern && (
                        <rect
                          x={secX}
                          y={underlightPartY}
                          width={layout.widthPx}
                          height={underlightPartHeight}
                          fill={glassTexturePattern}
                          opacity="1"
                          pointerEvents="none"
                        />
                      )}
                      {partUnderlightWindowType === 'fixed-sash' && ventilatorPartUnderlight[index] && bandConfig.count === 1 && (
                        <g pointerEvents="none">
                          {(() => {
                            const ventilatorHeight = frameThickness * 0.8;
                            const ventilatorX = secX;
                            const ventilatorY = underlightPartY;
                            const ventilatorWidth = Math.max(0, layout.widthPx);
                            const slatCount = 13;
                            const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                            const slats = [];
                            for (let i = 0; i < slatCount; i += 1) {
                              const x = ventilatorX + 3 * scale + i * slatGap;
                              slats.push(
                                <line
                                  key={`underlight-per-part-vent-slat-${index}-${layout.sectionIndex}-${i}`}
                                  x1={x}
                                  y1={ventilatorY + 2 * scale}
                                  x2={x}
                                  y2={ventilatorY + ventilatorHeight - 2 * scale}
                                  stroke="#000000"
                                  strokeWidth={1.4 * scale}
                                  strokeLinecap="round"
                                />
                              );
                            }
                            return (
                              <>
                                <rect x={ventilatorX} y={ventilatorY} width={ventilatorWidth} height={ventilatorHeight} fill="#D5DADF" stroke="#8A929A" strokeWidth={1 * scale} rx={2 * scale} ry={2 * scale} />
                                <line x1={ventilatorX} y1={ventilatorY + ventilatorHeight} x2={ventilatorX + ventilatorWidth} y2={ventilatorY + ventilatorHeight} stroke="#000000" strokeWidth={1.6 * scale} />
                                {slats}
                              </>
                            );
                          })()}
                        </g>
                      )}
                      {(partUnderlightWindowType === 'fixed-glass' || partUnderlightWindowType === 'tilt-only' || partUnderlightWindowType === 'tilt-turn-left' || partUnderlightWindowType === 'tilt-turn-right') && (() => {
                        const innerFrameX = secX;
                        const innerFrameWidth = layout.widthPx;
                        const innerFrameY = underlightPartY;
                        const innerFrameHeight = Math.max(underlightPartHeight - 2, 4);
                        const innerGlassX = innerFrameX + innerGlassInset;
                        const innerGlassY = innerFrameY + innerGlassInset;
                        const innerGlassWidth = innerFrameWidth - innerGlassInset * 2;
                        const innerGlassHeight = Math.max(innerFrameHeight - innerGlassInset * 2, 2);
                        return (
                          <g pointerEvents="none">
                            <rect x={innerFrameX} y={innerFrameY} width={innerFrameWidth} height={innerFrameHeight} fill={interiorFrameColorValue} />
                            {renderInnerSashReveal({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerFrameThickness })}
                            {renderInnerSashWoodJoints({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerGlassX, innerGlassWidth, innerFrameThickness })}
                            <rect x={innerFrameX + innerFrameThickness} y={innerFrameY + innerFrameThickness} width={innerFrameWidth - innerFrameThickness * 2} height={innerFrameHeight - innerFrameThickness * 2} fill="#282C30" />
                            <rect x={innerGlassX} y={innerGlassY} width={innerGlassWidth} height={innerGlassHeight} fill={glassFill} />
                            {glassTexturePattern && <rect x={innerGlassX} y={innerGlassY} width={innerGlassWidth} height={innerGlassHeight} fill={glassTexturePattern} opacity="1" />}
                            {partUnderlightWindowType === 'tilt-only' && (
                              <>
                                <line x1={innerGlassX} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
                                <line x1={innerGlassX + innerGlassWidth} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
                              </>
                            )}
                            {partUnderlightWindowType === 'tilt-turn-left' && (
                              <>
                                <line x1={innerGlassX} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
                                <line x1={innerGlassX + innerGlassWidth} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
                                <line x1={innerGlassX + innerGlassWidth} y1={innerGlassY} x2={innerGlassX} y2={innerGlassY + innerGlassHeight / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
                                <line x1={innerGlassX + innerGlassWidth} y1={innerGlassY + innerGlassHeight} x2={innerGlassX} y2={innerGlassY + innerGlassHeight / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
                              </>
                            )}
                            {partUnderlightWindowType === 'tilt-turn-right' && (
                              <>
                                <line x1={innerGlassX} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
                                <line x1={innerGlassX + innerGlassWidth} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth / 2} y2={innerGlassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
                                <line x1={innerGlassX} y1={innerGlassY} x2={innerGlassX + innerGlassWidth} y2={innerGlassY + innerGlassHeight / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
                                <line x1={innerGlassX} y1={innerGlassY + innerGlassHeight} x2={innerGlassX + innerGlassWidth} y2={innerGlassY + innerGlassHeight / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
                              </>
                            )}
                          </g>
                        );
                      })()}
                      {isHoveredUnderlight && (
                        <rect
                          x={secX}
                          y={underlightPartY}
                          width={layout.widthPx}
                          height={underlightPartHeight}
                          fill="rgba(255, 165, 0, 0.15)"
                          stroke="rgba(255, 140, 0, 0.6)"
                          strokeWidth="2"
                          style={{ pointerEvents: 'none', cursor: 'pointer' }}
                        />
                      )}
                    </g>
                  );
                })}
                {bandConfig.count > 1 &&
                  sectionLayouts.slice(0, -1).map((layout) => {
                    const mullionX = innerX + layout.xOffset + layout.widthPx;
                    return (
                      <g key={`underlight-per-part-mullion-${index}-${layout.sectionIndex}`}>
                        {renderVerticalMullionPair(mullionX, underlightPartY, underlightPartY + underlightPartHeight)}
                      </g>
                    );
                  })}
              </g>
            );
          })}

        {/* Underlight area - global from left panel */}
        {underlight === 'underlight' && underlightParts > 0 && (() => {
          const dividerY = startY + scaledHeight - scaledHeight * underlightRatio;
          const underlightTopY = dividerY + mullionThickness / 2;
          const underlightBottomY = startY + scaledHeight - glassInset;
          const underlightHeight = underlightBottomY - underlightTopY;

          if (underlightParts > 1) {
            return (
              <>
                {Array.from({ length: underlightParts }, (_, index) => {
                  const startAccum = normalizedUnderlightPartWidths
                    .slice(0, index)
                    .reduce((sum, w) => sum + w, 0);
                  const underlightPartWidthMm = normalizedUnderlightPartWidths[index];
                  const endAccum = startAccum + underlightPartWidthMm;
                  const segStart = glassX + (startAccum / totalUnderlightPartWidth) * glassWidth;
                  const segEnd = glassX + (endAccum / totalUnderlightPartWidth) * glassWidth;
                  const underlightPartWidthPx = segEnd - segStart;
                  const underlightPartX = segStart;
                  const underlightPartY = underlightTopY;
                  const isHovered = hoveredUnderlightPartIndex === index;
                  // Window type for this underlight part: own array, same behaviour as overhead
                  const underPartsCount = underlightParts || 1;
                  const effectiveIndex = Math.min(index, underPartsCount - 1);
                  const currentUnderlightTypes = getNormalizedUnderlightPartWindowTypes(underPartsCount);
                  const underlightPartWindowType = currentUnderlightTypes[effectiveIndex] || 'fixed-sash';
                  const hasUnderlightInnerFrame =
                    underlightPartWindowType === 'fixed-glass' ||
                    underlightPartWindowType === 'tilt-only' ||
                    underlightPartWindowType === 'tilt-turn-left' ||
                    underlightPartWindowType === 'tilt-turn-right';

                  return (
                    <g key={`underlight-part-${index}`}>
                      <g>
                        <rect
                          x={underlightPartX}
                          y={underlightPartY}
                          width={underlightPartWidthPx}
                          height={underlightHeight}
                          fill={getPanelFill(underlightPartWindowType, panelUnderlightColors[effectiveIndex])}
                          style={{ cursor: isHovered ? 'pointer' : 'default' }}
                          onMouseEnter={() => setHoveredUnderlightPartIndex(index)}
                          onMouseLeave={() => setHoveredUnderlightPartIndex(null)}
                          onClick={(e) => {
                            e.stopPropagation();
                            // If clicking the same part, reset dialogType first to trigger useEffect
                            if (selectedUnderlightPartIndex === effectiveIndex && dialogType === 'underlight') {
                              setDialogType(null);
                              setTimeout(() => {
                                setSelectedPartIndex(null);
                                setSelectedOverheadPartIndex(null);
                                setIsOverheadPart(false);
                                setSelectedUnderlightPartIndex(effectiveIndex);
                                setIsUnderlightPart(true);
                                setDialogType('underlight');
                              }, 0);
                            } else {
                              // Clear all other selections first
                              setSelectedPartIndex(null);
                              setSelectedOverheadPartIndex(null);
                              setIsOverheadPart(false);
                              // Set underlight part selection and dialog type together
                              setSelectedUnderlightPartIndex(effectiveIndex);
                              setIsUnderlightPart(true);
                              setDialogType('underlight');
                            }
                          }}
                        >
                          <title>{t('drawing.clickToConfigureUnderlight', { number: index + 1 })}</title>
                        </rect>
                        {/* Panel lines for underlight parts */}
                        {underlightPartWindowType === 'fixed-panel' && renderPanelLines(
                          panelUnderlightLineTypes[effectiveIndex] || 'none',
                          underlightPartX,
                          underlightPartY,
                          underlightPartWidthPx,
                          underlightHeight,
                          getPanelFill(underlightPartWindowType, panelUnderlightColors[effectiveIndex])
                        )}
                        {underlightPartWindowType !== 'fixed-panel' && glassTexturePattern && (
                          <rect
                            x={underlightPartX}
                            y={underlightPartY}
                            width={underlightPartWidthPx}
                            height={underlightHeight}
                            fill={glassTexturePattern}
                            opacity="1"
                            pointerEvents="none"
                          />
                        )}
                        {/* Ventilator for underlight parts when Fixed Sash and enabled */}
                        {underlightPartWindowType === 'fixed-sash' && ventilatorUnderlightParts[effectiveIndex] && (
                          <g pointerEvents="none">
                            {(() => {
                              const hasLeftMullion = index > 0;
                              const hasRightMullion = index < underlightParts - 1;
                              const ventilatorInnerFrameX = underlightPartX + (hasLeftMullion ? mullionHalf : 0);
                              const ventilatorInnerFrameWidth =
                                underlightPartWidthPx -
                                (hasLeftMullion ? mullionHalf : 0) -
                                (hasRightMullion ? mullionHalf : 0);

                              const ventilatorMarginX = 0;
                              const ventilatorHeight = frameThickness * 0.8;
                              const ventilatorX = ventilatorInnerFrameX + ventilatorMarginX;
                              const ventilatorY = underlightPartY;
                              const ventilatorWidth = Math.max(0, ventilatorInnerFrameWidth - ventilatorMarginX * 2);
                              const slatCount = 13;
                              const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                              const slats = [];
                              for (let i = 0; i < slatCount; i += 1) {
                                const x = ventilatorX + 3 * scale + i * slatGap;
                                slats.push(
                                  <line
                                    key={`underlight-vent-slat-${effectiveIndex}-${i}`}
                                    x1={x}
                                    y1={ventilatorY + 2 * scale}
                                    x2={x}
                                    y2={ventilatorY + ventilatorHeight - 2 * scale}
                                    stroke="#000000"
                                    strokeWidth={1.4 * scale}
                                    strokeLinecap="round"
                                  />
                                );
                              }
                              return (
                                <>
                                  {/* Ventilator body */}
                                  <rect
                                    x={ventilatorX}
                                    y={ventilatorY}
                                    width={ventilatorWidth}
                                    height={ventilatorHeight}
                                    fill="#D5DADF"
                                    stroke="#8A929A"
                                    strokeWidth={1 * scale}
                                    rx={2 * scale}
                                    ry={2 * scale}
                                  />
                                  {/* Dark bottom outline under ventilator */}
                                  <line
                                    x1={ventilatorX}
                                    y1={ventilatorY + ventilatorHeight}
                                    x2={ventilatorX + ventilatorWidth}
                                    y2={ventilatorY + ventilatorHeight}
                                    stroke="#000000"
                                    strokeWidth={1.6 * scale}
                                  />
                                  {/* Slats */}
                                  {slats}
                                </>
                              );
                            })()}
                          </g>
                        )}
                        {/* Inner frame for underlight parts - mirror overhead/main inner frame style */}
                        {hasUnderlightInnerFrame && (() => {
                          const hasLeftMullion = index > 0;
                          const hasRightMullion = index < underlightParts - 1;
                          const innerFrameX = underlightPartX + (hasLeftMullion ? mullionHalf : 0);
                          const innerFrameY = underlightPartY;
                          const innerFrameWidth =
                            underlightPartWidthPx -
                            (hasLeftMullion ? mullionHalf : 0) -
                            (hasRightMullion ? mullionHalf : 0);
                          const innerFrameHeight = underlightHeight;
                          const innerGlassX = innerFrameX + innerGlassInset;
                          const innerGlassY = innerFrameY + innerGlassInset;
                          const innerGlassWidth = innerFrameWidth - innerGlassInset * 2;
                          const innerGlassHeight = innerFrameHeight - innerGlassInset * 2;

                        return (
                          <g style={{ pointerEvents: 'none' }}>
                            {/* Outer frame (dark) */}
                            <rect
                              x={innerFrameX}
                              y={innerFrameY}
                              width={innerFrameWidth}
                              height={innerFrameHeight}
                              fill={interiorFrameColorValue}
                              pointerEvents="none"
                            />
                            {renderInnerSashReveal({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerFrameThickness })}
                            {renderInnerSashWoodJoints({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerGlassX, innerGlassWidth, innerFrameThickness, glassEdgeRightOffset: 0, glassEdgeLeftOffset: 0 })}
                            {/* Spacer / glazing bead */}
                            <rect
                              x={innerFrameX + innerFrameThickness}
                              y={innerFrameY + innerFrameThickness}
                              width={innerFrameWidth - innerFrameThickness * 2}
                              height={innerFrameHeight - innerFrameThickness * 2}
                              fill="#282C30"
                              pointerEvents="none"
                            />
                            {/* Inner glass with gradient base and texture overlay */}
                            <g pointerEvents="none">
                              <rect
                                x={innerGlassX}
                                y={innerGlassY}
                                width={innerGlassWidth}
                                height={innerGlassHeight}
                                fill={glassFill}
                              />
                              {glassTexturePattern && (
                                <rect
                                  x={innerGlassX}
                                  y={innerGlassY}
                                  width={innerGlassWidth}
                                  height={innerGlassHeight}
                                  fill={glassTexturePattern}
                                  opacity="1"
                                />
                              )}
                            </g>
                            {/* Diagonal lines for Tilt Only */}
                            {underlightPartWindowType === 'tilt-only' && (
                              <>
                                <line
                                  x1={innerGlassX}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX + innerGlassWidth}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                              </>
                            )}
                            {/* Diagonal lines for Tilt & Turn Left */}
                            {underlightPartWindowType === 'tilt-turn-left' && (
                              <>
                                <line
                                  x1={innerGlassX}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX + innerGlassWidth}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX + innerGlassWidth}
                                  y1={innerGlassY}
                                  x2={innerGlassX}
                                  y2={innerGlassY + innerGlassHeight / 2}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX + innerGlassWidth}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX}
                                  y2={innerGlassY + innerGlassHeight / 2}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                              </>
                            )}
                            {/* Diagonal lines for Tilt & Turn Right */}
                            {underlightPartWindowType === 'tilt-turn-right' && (
                              <>
                                <line
                                  x1={innerGlassX}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX + innerGlassWidth}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX}
                                  y1={innerGlassY}
                                  x2={innerGlassX + innerGlassWidth}
                                  y2={innerGlassY + innerGlassHeight / 2}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth}
                                  y2={innerGlassY + innerGlassHeight / 2}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                              </>
                            )}
                          </g>
                        );
                        })()}
                      </g>
                      {/* Hover highlight overlay for underlight multi-part */}
                      {isHovered && (
                        <rect
                          x={underlightPartX}
                          y={underlightPartY}
                          width={underlightPartWidthPx}
                          height={underlightHeight}
                          fill="rgba(255, 165, 0, 0.15)"
                          stroke="rgba(255, 140, 0, 0.6)"
                          strokeWidth="2"
                          style={{ pointerEvents: 'none', cursor: 'pointer' }}
                        />
                      )}
                    </g>
                  );
                })}
                {/* Draw underlight mullions AFTER glass so they are not covered (same as overhead / per-part underlight) */}
                {Array.from({ length: underlightParts - 1 }, (_, index) => {
                  const accumulatedWidth = normalizedUnderlightPartWidths
                    .slice(0, index + 1)
                    .reduce((sum, w) => sum + w, 0);
                  const fraction = accumulatedWidth / totalUnderlightPartWidth;
                  const x = glassX + glassWidth * fraction;
                  return (
                    <g key={`underlight-mullion-${index}`}>
                      {renderVerticalMullionPair(x, underlightTopY, underlightBottomY)}
                    </g>
                  );
                })}
                {/* Re-draw horizontal transom on top of vertical mullions at the junction */}
                {renderHorizontalDividerEdges(glassX, glassX + glassWidth, dividerY)}
              </>
            );
          }

          // Single underlight section - own type like overhead[0]
          return (
            <g>
              {(() => {
                const currentTypes = getNormalizedUnderlightPartWindowTypes(underlightParts || 1);
                const singleUnderlightType = currentTypes[0] || 'fixed-sash';
                const isHovered = hoveredUnderlightPartIndex === 0;
                const hasUnderlightInnerFrame =
                  singleUnderlightType === 'fixed-glass' ||
                  singleUnderlightType === 'tilt-only' ||
                  singleUnderlightType === 'tilt-turn-left' ||
                  singleUnderlightType === 'tilt-turn-right';
                return (
                  <>
                    <g>
                      <rect
                        x={glassX}
                        y={underlightTopY}
                        width={glassWidth}
                        height={underlightHeight}
                        fill={getPanelFill(singleUnderlightType, panelUnderlightColors[0])}
                        style={{ cursor: isHovered ? 'pointer' : 'default', pointerEvents: 'auto' }}
                        onMouseEnter={() => setHoveredUnderlightPartIndex(0)}
                        onMouseLeave={() => setHoveredUnderlightPartIndex(null)}
                        onClick={(e) => {
                          e.stopPropagation();
                          // If clicking the same part, reset dialogType first to trigger useEffect
                          if (selectedUnderlightPartIndex === 0 && dialogType === 'underlight') {
                            setDialogType(null);
                            setTimeout(() => {
                              setSelectedPartIndex(null);
                              setSelectedOverheadPartIndex(null);
                              setIsOverheadPart(false);
                              setSelectedUnderlightPartIndex(0);
                              setIsUnderlightPart(true);
                              setDialogType('underlight');
                            }, 0);
                          } else {
                            // Clear all other selections first
                            setSelectedPartIndex(null);
                            setSelectedOverheadPartIndex(null);
                            setIsOverheadPart(false);
                            // Set underlight part selection and dialog type together
                            setSelectedUnderlightPartIndex(0);
                            setIsUnderlightPart(true);
                            setDialogType('underlight');
                          }
                        }}
                      >
                        <title>{t('drawing.clickToConfigureUnderlightSettings')}</title>
                      </rect>
                      {/* Panel lines for single underlight part */}
                      {singleUnderlightType === 'fixed-panel' && renderPanelLines(
                        panelUnderlightLineTypes[0] || 'none',
                        glassX,
                        underlightTopY,
                        glassWidth,
                        underlightHeight,
                        getPanelFill(singleUnderlightType, panelUnderlightColors[0])
                      )}
                      {singleUnderlightType !== 'fixed-panel' && glassTexturePattern && (
                        <rect
                          x={glassX}
                          y={underlightTopY}
                          width={glassWidth}
                          height={underlightHeight}
                          fill={glassTexturePattern}
                          opacity="1"
                          pointerEvents="none"
                        />
                      )}
                      {/* Ventilator for single underlight section when Fixed Sash and enabled */}
                      {singleUnderlightType === 'fixed-sash' && ventilatorUnderlightParts[0] && (
                        <g pointerEvents="none">
                          {(() => {
                            const ventilatorInnerFrameX = glassX;
                            const ventilatorInnerFrameWidth = glassWidth;

                            const ventilatorMarginX = 0;
                            const ventilatorHeight = frameThickness * 0.8;
                            const ventilatorX = ventilatorInnerFrameX + ventilatorMarginX;
                            const ventilatorY = underlightTopY;
                            const ventilatorWidth = Math.max(0, ventilatorInnerFrameWidth - ventilatorMarginX * 2);
                            const slatCount = 13;
                            const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                            const slats = [];
                            for (let i = 0; i < slatCount; i += 1) {
                              const x = ventilatorX + 3 * scale + i * slatGap;
                              slats.push(
                                <line
                                  key={`underlight-single-vent-slat-${i}`}
                                  x1={x}
                                  y1={ventilatorY + 2 * scale}
                                  x2={x}
                                  y2={ventilatorY + ventilatorHeight - 2 * scale}
                                  stroke="#000000"
                                  strokeWidth={1.4 * scale}
                                  strokeLinecap="round"
                                />
                              );
                            }
                            return (
                              <>
                                {/* Ventilator body */}
                                <rect
                                  x={ventilatorX}
                                  y={ventilatorY}
                                  width={ventilatorWidth}
                                  height={ventilatorHeight}
                                  fill="#D5DADF"
                                  stroke="#8A929A"
                                  strokeWidth={1 * scale}
                                  rx={2 * scale}
                                  ry={2 * scale}
                                />
                                {/* Dark bottom outline under ventilator */}
                                <line
                                  x1={ventilatorX}
                                  y1={ventilatorY + ventilatorHeight}
                                  x2={ventilatorX + ventilatorWidth}
                                  y2={ventilatorY + ventilatorHeight}
                                  stroke="#000000"
                                  strokeWidth={1.6 * scale}
                                />
                                {/* Slats */}
                                {slats}
                              </>
                            );
                          })()}
                        </g>
                      )}
                      {/* Inner frame for single underlight - mirror overhead/main inner frame style */}
                      {hasUnderlightInnerFrame && (() => {
                        const innerFrameX = glassX;
                        const innerFrameY = underlightTopY;
                        const innerFrameWidth = glassWidth;
                        const innerFrameHeight = underlightHeight;
                        const innerGlassX = innerFrameX + innerGlassInset;
                        const innerGlassY = innerFrameY + innerGlassInset;
                        const innerGlassWidth = innerFrameWidth - innerGlassInset * 2;
                        const innerGlassHeight = innerFrameHeight - innerGlassInset * 2;

                        return (
                          <g style={{ pointerEvents: 'none' }}>
                            {/* Outer frame (dark) */}
                            <rect
                              x={innerFrameX}
                              y={innerFrameY}
                              width={innerFrameWidth}
                              height={innerFrameHeight}
                              fill={interiorFrameColorValue}
                              pointerEvents="none"
                            />
                            {renderInnerSashReveal({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerFrameThickness })}
                            {renderInnerSashWoodJoints({ innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight, innerGlassX, innerGlassWidth, innerFrameThickness, glassEdgeRightOffset: 0, glassEdgeLeftOffset: 0 })}
                            {/* Spacer / glazing bead */}
                            <rect
                              x={innerFrameX + innerFrameThickness}
                              y={innerFrameY + innerFrameThickness}
                              width={innerFrameWidth - innerFrameThickness * 2}
                              height={innerFrameHeight - innerFrameThickness * 2}
                              fill="#282C30"
                              pointerEvents="none"
                            />
                            {/* Inner glass with gradient base and texture overlay */}
                            <g pointerEvents="none">
                              <rect
                                x={innerGlassX}
                                y={innerGlassY}
                                width={innerGlassWidth}
                                height={innerGlassHeight}
                                fill={glassFill}
                              />
                              {glassTexturePattern && (
                                <rect
                                  x={innerGlassX}
                                  y={innerGlassY}
                                  width={innerGlassWidth}
                                  height={innerGlassHeight}
                                  fill={glassTexturePattern}
                                  opacity="1"
                                />
                              )}
                            </g>
                            {/* Diagonal lines for Tilt Only */}
                            {singleUnderlightType === 'tilt-only' && (
                              <>
                                <line
                                  x1={innerGlassX}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX + innerGlassWidth}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                              </>
                            )}
                            {/* Diagonal lines for Tilt & Turn Left */}
                            {singleUnderlightType === 'tilt-turn-left' && (
                              <>
                                <line
                                  x1={innerGlassX}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX + innerGlassWidth}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX + innerGlassWidth}
                                  y1={innerGlassY}
                                  x2={innerGlassX}
                                  y2={innerGlassY + innerGlassHeight / 2}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX + innerGlassWidth}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX}
                                  y2={innerGlassY + innerGlassHeight / 2}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                              </>
                            )}
                            {/* Diagonal lines for Tilt & Turn Right */}
                            {singleUnderlightType === 'tilt-turn-right' && (
                              <>
                                <line
                                  x1={innerGlassX}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX + innerGlassWidth}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth / 2}
                                  y2={innerGlassY}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX}
                                  y1={innerGlassY}
                                  x2={innerGlassX + innerGlassWidth}
                                  y2={innerGlassY + innerGlassHeight / 2}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                                <line
                                  x1={innerGlassX}
                                  y1={innerGlassY + innerGlassHeight}
                                  x2={innerGlassX + innerGlassWidth}
                                  y2={innerGlassY + innerGlassHeight / 2}
                                  stroke="#999999"
                                  strokeWidth="3"
                                  strokeDasharray="3"
                                  pointerEvents="none"
                                />
                              </>
                            )}
                          </g>
                        );
                      })()}
                    </g>
                    {/* Hover highlight overlay for single underlight */}
                    {isHovered && (
                      <rect
                        x={glassX}
                        y={underlightTopY}
                        width={glassWidth}
                        height={underlightHeight}
                        fill="rgba(255, 165, 0, 0.15)"
                        stroke="rgba(255, 140, 0, 0.6)"
                        strokeWidth="2"
                        style={{ pointerEvents: 'none', cursor: 'pointer' }}
                      />
                    )}
                  </>
                );
              })()}
            </g>
          );
        })()}
        
        {/* Printable frame edges — thin dark outlines when frame colors are light (e.g. white on paper) */}
        {usePrintableFrameEdges && (
          <g pointerEvents="none" fill="none" stroke={FRAME_EDGE_STROKE_DARK}>
            {isLightFrameColor(externalFrameColor) && (
              <>
                <rect
                  x={startX}
                  y={startY}
                  width={scaledWidth}
                  height={scaledHeight}
                  strokeWidth={0.6}
                />
                <rect
                  x={startX + frameThickness}
                  y={startY + frameThickness}
                  width={scaledWidth - frameThickness * 2}
                  height={scaledHeight - frameThickness * 2}
                  strokeWidth={0.5}
                />
              </>
            )}
            {isLightFrameColor(interiorFrameColorValue) && (
              <>
                {(parts === 1 ? [0] : Array.from({ length: parts }, (_, i) => i)).map((index) => {
                  const currentPartWindowTypes =
                    partWindowTypes && partWindowTypes.length === parts
                      ? partWindowTypes
                      : Array(parts).fill('fixed-sash');
                  const partWindowType = currentPartWindowTypes[index] || 'fixed-sash';
                  const hasInnerFrame =
                    partWindowType === 'fixed-glass' ||
                    partWindowType === 'tilt-only' ||
                    partWindowType === 'tilt-turn-left' ||
                    partWindowType === 'tilt-turn-right';
                  if (!hasInnerFrame) return null;

                  let partX = glassX;
                  let partWidthPx = glassWidth;
                  let partGlassY = glassY;
                  let partGlassHeight = glassHeight;

                  if (parts > 1) {
                    const startAccum = normalizedPartWidths
                      .slice(0, index)
                      .reduce((sum, w) => sum + w, 0);
                    partWidthPx = (normalizedPartWidths[index] / totalPartWidth) * glassWidth;
                    partX = glassX + (startAccum / totalPartWidth) * glassWidth;
                    if (overhead !== 'overhead' && hasPerPartOverhead && !overheadPerPart[index]) {
                      partGlassY = startY + glassInset;
                      partGlassHeight = glassY + glassHeight - (startY + glassInset);
                    } else if (overhead === 'overhead' && overheadPerPart[index]) {
                      const partOverheadHeightInMain = scaledHeight * overheadRatio - glassInset;
                      partGlassY = glassY + partOverheadHeightInMain + mullionHalf;
                      partGlassHeight = glassHeight - partOverheadHeightInMain - mullionHalf;
                    }
                    if (underlight !== 'underlight' && hasPerPartUnderlight && !underlightPerPart[index]) {
                      partGlassHeight = (startY + scaledHeight - glassInset) - partGlassY;
                    } else if (underlight !== 'underlight' && hasPerPartUnderlight && underlightPerPart[index]) {
                      partGlassHeight = (underlightDividerY - mullionHalf) - partGlassY;
                    } else if (underlight === 'underlight' && underlightPerPart[index]) {
                      const partUnderlightHeightInMain = scaledHeight * underlightRatio - glassInset;
                      partGlassHeight = glassHeight - partUnderlightHeightInMain - mullionHalf;
                    }
                  }

                  const hasLeftMullion = parts > 1 && index > 0;
                  const hasRightMullion = parts > 1 && index < parts - 1;
                  const sashX = partX + (hasLeftMullion ? mullionHalf : 0);
                  const sashY = partGlassY;
                  const sashW =
                    partWidthPx -
                    (hasLeftMullion ? mullionHalf : 0) -
                    (hasRightMullion ? mullionHalf : 0);
                  const sashH = partGlassHeight;

                  return (
                    <rect
                      key={`printable-inner-sash-${index}`}
                      x={sashX}
                      y={sashY}
                      width={Math.max(0, sashW)}
                      height={Math.max(0, sashH)}
                      fill="none"
                      stroke={FRAME_EDGE_STROKE_DARK}
                      strokeWidth={0.5}
                    />
                  );
                })}
              </>
            )}
            {/* Inner frame dividers — dark outlines on top so mullions/transoms match exterior frame */}
            {parts > 1 &&
              Array.from({ length: parts - 1 }, (_, index) => {
                const accumulatedWidth = normalizedPartWidths
                  .slice(0, index + 1)
                  .reduce((sum, w) => sum + w, 0);
                const fraction = accumulatedWidth / totalPartWidth;
                const x = glassX + glassWidth * fraction;
                const mullionStartY =
                  overhead === 'overhead' ? glassY : startY + glassInset;
                const mullionEndY = underlight === 'underlight' && !hasPerPartUnderlight
                  ? underlightDividerY - mullionHalf
                  : startY + scaledHeight - glassInset;
                return (
                  <g key={`printable-main-mullion-${index}`}>
                    {renderVerticalMullionEdgeOutlines(x, mullionStartY, mullionEndY, FRAME_EDGE_STROKE_DARK)}
                  </g>
                );
              })}
            {overhead === 'overhead' &&
              overheadParts > 1 &&
              Array.from({ length: overheadParts - 1 }, (_, index) => {
                const accumulatedWidth = normalizedOverheadPartWidths
                  .slice(0, index + 1)
                  .reduce((sum, w) => sum + w, 0);
                const fraction = accumulatedWidth / totalOverheadPartWidth;
                const x = glassX + glassWidth * fraction;
                const dividerY = startY + scaledHeight * overheadRatio;
                const overheadMullionStartY = startY + glassInset;
                const overheadMullionEndY = dividerY - mullionThickness / 2;
                return (
                  <g key={`printable-overhead-mullion-${index}`}>
                    {renderVerticalMullionEdgeOutlines(
                      x,
                      overheadMullionStartY,
                      overheadMullionEndY,
                      FRAME_EDGE_STROKE_DARK
                    )}
                  </g>
                );
              })}
            {effectiveOverhead &&
              Array.from({ length: parts }, (_, index) => {
                if (!overheadPerPart[index]) return null;
                const bandConfig = getPartOverheadBandConfig(index);
                if (bandConfig.count <= 1) return null;
                const startAccum = normalizedPartWidths.slice(0, index).reduce((sum, w) => sum + w, 0);
                const partWidthMm = normalizedPartWidths[index];
                const partWidthPx = (partWidthMm / totalPartWidth) * glassWidth;
                const partX = glassX + (startAccum / totalPartWidth) * glassWidth;
                const innerX = partX + (index > 0 ? mullionHalf : 0);
                const innerW = partWidthPx - (index > 0 ? mullionHalf : 0) - (index < parts - 1 ? mullionHalf : 0);
                const sectionLayouts = buildPartBandSectionLayouts(
                  innerW,
                  partWidthMm,
                  bandConfig.count,
                  bandConfig.widthsMm,
                  bandConfig.windowTypes
                );
                const partOverheadHeightInMain = scaledHeight * overheadRatio - glassInset;
                const mullionStartY = overhead === 'overhead' ? glassY : startY + glassInset;
                const mullionEndY = overhead === 'overhead'
                  ? glassY + partOverheadHeightInMain - mullionHalf
                  : startY + scaledHeight * overheadRatio - mullionHalf;
                return sectionLayouts.slice(0, -1).map((layout) => {
                  const mullionX = innerX + layout.xOffset + layout.widthPx;
                  return (
                    <g key={`printable-part-overhead-section-mullion-${index}-${layout.sectionIndex}`}>
                      {renderVerticalMullionEdgeOutlines(mullionX, mullionStartY, mullionEndY, FRAME_EDGE_STROKE_DARK)}
                    </g>
                  );
                });
              })}
            {effectiveOverhead &&
              overhead === 'overhead' &&
              renderHorizontalMullionEdgeOutlines(
                glassX,
                glassX + glassWidth,
                startY + scaledHeight * overheadRatio,
                FRAME_EDGE_STROKE_DARK
              )}
            {effectiveOverhead &&
              overhead === 'overhead' &&
              hasPerPartOverhead &&
              Array.from({ length: parts }, (_, index) => {
                if (!overheadPerPart[index]) return null;
                const startAccum = normalizedPartWidths.slice(0, index).reduce((sum, w) => sum + w, 0);
                const partWidthMm = normalizedPartWidths[index];
                const partWidthPx = (partWidthMm / totalPartWidth) * glassWidth;
                const partX = glassX + (startAccum / totalPartWidth) * glassWidth;
                const segX1 = partX + (index > 0 ? mullionHalf : 0);
                const segX2 = partX + partWidthPx - (index < parts - 1 ? mullionHalf : 0);
                const partOverheadHeightInMain = scaledHeight * overheadRatio - glassInset;
                const partDividerY = glassY + partOverheadHeightInMain;
                return (
                  <g key={`printable-part-overhead-divider-${index}`}>
                    {renderHorizontalMullionEdgeOutlines(segX1, segX2, partDividerY, FRAME_EDGE_STROKE_DARK)}
                  </g>
                );
              })}
            {underlight === 'underlight' &&
              underlightParts > 1 &&
              Array.from({ length: underlightParts - 1 }, (_, index) => {
                const accumulatedWidth = normalizedUnderlightPartWidths
                  .slice(0, index + 1)
                  .reduce((sum, w) => sum + w, 0);
                const fraction = accumulatedWidth / totalUnderlightPartWidth;
                const x = glassX + glassWidth * fraction;
                const underlightTopY = underlightDividerY + mullionThickness / 2;
                const underlightBottomY = startY + scaledHeight - glassInset;
                return (
                  <g key={`printable-underlight-mullion-${index}`}>
                    {renderVerticalMullionEdgeOutlines(
                      x,
                      underlightTopY,
                      underlightBottomY,
                      FRAME_EDGE_STROKE_DARK
                    )}
                  </g>
                );
              })}
            {hasPerPartUnderlight &&
              Array.from({ length: parts }, (_, index) => {
                if (!partHasUnderlight(index)) return null;
                const startAccum = normalizedPartWidths.slice(0, index).reduce((sum, w) => sum + w, 0);
                const partWidthMm = normalizedPartWidths[index];
                const partWidthPx = (partWidthMm / totalPartWidth) * glassWidth;
                const partX = glassX + (startAccum / totalPartWidth) * glassWidth;
                const segX1 = partX + (index > 0 ? mullionHalf : 0);
                const segX2 = partX + partWidthPx - (index < parts - 1 ? mullionHalf : 0);
                return (
                  <g key={`printable-part-underlight-divider-${index}`}>
                    {renderHorizontalMullionEdgeOutlines(segX1, segX2, underlightDividerY, FRAME_EDGE_STROKE_DARK)}
                  </g>
                );
              })}
            {!hasPerPartUnderlight && underlight === 'underlight' &&
              renderHorizontalMullionEdgeOutlines(
                glassX,
                glassX + glassWidth,
                underlightDividerY,
                FRAME_EDGE_STROKE_DARK
              )}
            {effectiveUnderlight &&
              hasPerPartUnderlight &&
              Array.from({ length: parts }, (_, index) => {
                if (!partHasUnderlight(index)) return null;
                const bandConfig = getPartUnderlightBandConfig(index);
                if (bandConfig.count <= 1) return null;
                const startAccum = normalizedPartWidths.slice(0, index).reduce((sum, w) => sum + w, 0);
                const partWidthMm = normalizedPartWidths[index];
                const partWidthPx = (partWidthMm / totalPartWidth) * glassWidth;
                const partX = glassX + (startAccum / totalPartWidth) * glassWidth;
                const innerX = partX + (index > 0 ? mullionHalf : 0);
                const innerW = partWidthPx - (index > 0 ? mullionHalf : 0) - (index < parts - 1 ? mullionHalf : 0);
                const sectionLayouts = buildPartBandSectionLayouts(
                  innerW,
                  partWidthMm,
                  bandConfig.count,
                  bandConfig.widthsMm,
                  bandConfig.windowTypes
                );
                const mullionStartY = underlight === 'underlight'
                  ? underlightDividerY + mullionHalf
                  : startY + scaledHeight - scaledHeight * underlightRatio;
                const mullionEndY = startY + scaledHeight - glassInset;
                return sectionLayouts.slice(0, -1).map((layout) => {
                  const mullionX = innerX + layout.xOffset + layout.widthPx;
                  return (
                    <g key={`printable-part-underlight-section-mullion-${index}-${layout.sectionIndex}`}>
                      {renderVerticalMullionEdgeOutlines(mullionX, mullionStartY, mullionEndY, FRAME_EDGE_STROKE_DARK)}
                    </g>
                  );
                });
              })}
          </g>
        )}

        {/* Horizontal dimension line (width) - bottom */}
        <g>
          {/* Line */}
          <line
            x1={startX}
            y1={startY + scaledHeight + dimOffset + dimStackOffset}
            x2={startX + scaledWidth}
            y2={startY + scaledHeight + dimOffset + dimStackOffset}
            stroke="#2C2C2C"
            strokeWidth={dimStrokeMain}
          />
          {/* Left arrow */}
          <line
            x1={startX}
            y1={startY + scaledHeight + dimOffset + dimStackOffset}
            x2={startX}
            y2={startY + scaledHeight + dimOffset + dimStackOffset - dimTick}
            stroke="#2C2C2C"
            strokeWidth={dimStrokeMain}
          />
          <line
            x1={startX}
            y1={startY + scaledHeight + dimOffset + dimStackOffset}
            x2={startX}
            y2={startY + scaledHeight + dimOffset + dimStackOffset + dimTick}
            stroke="#2C2C2C"
            strokeWidth={dimStrokeMain}
          />
          {/* Right arrow */}
          <line
            x1={startX + scaledWidth}
            y1={startY + scaledHeight + dimOffset + dimStackOffset}
            x2={startX + scaledWidth}
            y2={startX + scaledWidth && startY + scaledHeight + dimOffset + dimStackOffset - dimTick}
            stroke="#2C2C2C"
            strokeWidth={dimStrokeMain}
          />
          <line
            x1={startX + scaledWidth}
            y1={startY + scaledHeight + dimOffset + dimStackOffset}
            x2={startX + scaledWidth}
            y2={startY + scaledHeight + dimOffset + dimStackOffset + dimTick}
            stroke="#2C2C2C"
            strokeWidth={dimStrokeMain}
          />
          {/* Editable width label */}
          <foreignObject
            x={startX + scaledWidth / 2 - widthDimLabelWidth / 2}
            y={startY + scaledHeight + dimOffset + dimStackOffset + dimLabelGap}
            width={widthDimLabelWidth}
            height={dimLabelHeight}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: `${dimLabelGapSmall}px`, whiteSpace: 'nowrap' }}>
              <input
                type="number"
                value={widthInput}
                onChange={(e) => setWidthInput(e.target.value)}
                onBlur={() => {
                  if (!onWidthChange) return;
                  const parsed = parseInt(widthInput, 10);
                  if (!Number.isNaN(parsed) && parsed >= DIMENSION_CONSTRAINTS.MIN_WIDTH && parsed <= DIMENSION_CONSTRAINTS.MAX_WIDTH) {
                    onWidthChange(parsed);
                  } else {
                    setWidthInput(String(width));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  }
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'center',
                  fontSize: `${dimFontSize}px`,
                  fontWeight: 600,
                  color: '#2C2C2C',
                  outline: 'none',
                  padding: 0,
                  margin: 0,
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield',
                  width: getDimensionInputWidth(widthInput, dimUiScale),
                  minWidth: getDimensionInputWidth(widthInput, dimUiScale),
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: `${dimFontSize}px`, fontWeight: 600, color: '#2C2C2C', opacity: 0.7, flexShrink: 0 }}>mm</span>
            </div>
          </foreignObject>
        </g>

        {/* Per-part horizontal dimensions (only when multiple parts) */}
        {parts > 1 &&
          Array.from({ length: parts }, (_, index) => {
            const startAccum = normalizedPartWidths
              .slice(0, index)
              .reduce((sum, w) => sum + w, 0);
            const partWidthMm = normalizedPartWidths[index];
            const endAccum = startAccum + partWidthMm;
            const segStart = glassX + (startAccum / totalPartWidth) * glassWidth;
            const segEnd = glassX + (endAccum / totalPartWidth) * glassWidth;
            // Place these closer to the frame so there's more gap from the main dimension,
            // but also stack them with any underlight rulers using dimStackOffset.
            const y = startY + scaledHeight + dimOffset / 4 + dimStackOffset;
            return (
              <g key={`part-dim-${index}`}>
                {/* segment line */}
                <line
                  x1={segStart}
                  y1={y}
                  x2={segEnd}
                  y2={y}
                  stroke="#4A4A4A"
                  strokeWidth={dimStrokeSub}
                />
                {/* left tick */}
                <line
                  x1={segStart}
                  y1={y - dimTickSmall}
                  x2={segStart}
                  y2={y + dimTickSmall}
                  stroke="#4A4A4A"
                  strokeWidth={dimStrokeSub}
                />
                {/* right tick */}
                <line
                  x1={segEnd}
                  y1={y - dimTickSmall}
                  x2={segEnd}
                  y2={y + dimTickSmall}
                  stroke="#4A4A4A"
                  strokeWidth={dimStrokeSub}
                />
                {/* Editable label using an invisible input to keep the visual style */}
                <foreignObject
                  x={(segStart + segEnd) / 2 - partDimLabelWidth / 2}
                  y={y - dimLabelGapSmall}
                  width={partDimLabelWidth}
                  height={dimLabelHeight}
                >
                  <input
                    type="number"
                    value={inputPartWidths[index] ?? partWidthMm}
                    onChange={(e) => {
                      const newVal = e.target.value;
                      setInputPartWidths((prev) => {
                        const next = [...prev];
                        next[index] = newVal;
                        return next;
                      });
                    }}
                    onBlur={() => {
                      if (!onPartWidthChange) return;
                      const parsed = parseInt(inputPartWidths[index], 10);
                      if (!Number.isNaN(parsed)) {
                        onPartWidthChange(index, parsed);
                      } else {
                        // reset to current width if input was invalid/empty
                        setInputPartWidths((prev) => {
                          const next = [...prev];
                          next[index] = String(partWidthMm);
                          return next;
                        });
                      }
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'center',
                      fontSize: `${dimFontSizeSmall}px`,
                      fontWeight: 500,
                      color: '#4A4A4A',
                      outline: 'none',
                      padding: 0,
                      margin: 0,
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield',
                    }}
                  />
                </foreignObject>
              </g>
            );
          })}
        
        {/* Vertical dimension line (height) - right */}
        <g>
          {/* Line - moved further right when overhead is active to avoid overlap */}
          <line
            x1={startX + scaledWidth + dimOffset + heightDimExtraOffset}
            y1={startY}
            x2={startX + scaledWidth + dimOffset + heightDimExtraOffset}
            y2={startY + scaledHeight}
            stroke="#2C2C2C"
            strokeWidth={dimStrokeMain}
          />
          {/* Top arrow */}
          <line
            x1={startX + scaledWidth + dimOffset + heightDimExtraOffset}
            y1={startY}
            x2={startX + scaledWidth + dimOffset + heightDimExtraOffset - dimTick}
            y2={startY}
            stroke="#2C2C2C"
            strokeWidth={dimStrokeMain}
          />
          <line
            x1={startX + scaledWidth + dimOffset + heightDimExtraOffset}
            y1={startY}
            x2={startX + scaledWidth + dimOffset + heightDimExtraOffset + dimTick}
            y2={startY}
            stroke="#2C2C2C"
            strokeWidth={dimStrokeMain}
          />
          {/* Bottom arrow */}
          <line
            x1={startX + scaledWidth + dimOffset + heightDimExtraOffset}
            y1={startY + scaledHeight}
            x2={startX + scaledWidth + dimOffset + heightDimExtraOffset - dimTick}
            y2={startY + scaledHeight}
            stroke="#2C2C2C"
            strokeWidth={dimStrokeMain}
          />
          <line
            x1={startX + scaledWidth + dimOffset + heightDimExtraOffset}
            y1={startY + scaledHeight}
            x2={startX + scaledWidth + dimOffset + heightDimExtraOffset + dimTick}
            y2={startY + scaledHeight}
            stroke="#2C2C2C"
            strokeWidth={dimStrokeMain}
          />
          {/* Editable height label */}
          <foreignObject
            x={startX + scaledWidth + dimOffset + heightDimExtraOffset + 20 * dimUiScale}
            y={startY + scaledHeight / 2 - dimLabelHeight / 2}
            width={heightDimLabelWidth}
            height={dimLabelHeight}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: `${dimLabelGapSmall}px`, whiteSpace: 'nowrap', overflow: 'visible' }}>
              <input
                type="number"
                value={heightInput}
                onChange={(e) => setHeightInput(e.target.value)}
                onBlur={() => {
                  if (!onHeightChange) return;
                  const parsed = parseInt(heightInput, 10);
                  if (!Number.isNaN(parsed) && parsed >= DIMENSION_CONSTRAINTS.MIN_HEIGHT && parsed <= DIMENSION_CONSTRAINTS.MAX_HEIGHT) {
                    onHeightChange(parsed);
                  } else {
                    setHeightInput(String(height));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  }
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  fontSize: `${dimFontSize}px`,
                  fontWeight: 600,
                  color: '#2C2C2C',
                  outline: 'none',
                  padding: 0,
                  margin: 0,
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield',
                  width: getDimensionInputWidth(heightInput, dimUiScale),
                  minWidth: getDimensionInputWidth(heightInput, dimUiScale),
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: `${dimFontSize}px`, fontWeight: 600, color: '#2C2C2C', opacity: 0.7, flexShrink: 0 }}>mm</span>
            </div>
          </foreignObject>
        </g>

        {/* Overhead / main window vertical dimensions (shown when overhead or underlight are active) */}
        {(effectiveOverhead || hasUnderlight) && (() => {
          const overheadHeightMm = effectiveOverhead ? overheadHeight : 0;
          const mainWindowHeightMm = height - overheadHeightMm - (hasUnderlight ? underlightHeight : 0); // middle clear height
          const topY = startY;
          const bottomY = startY + scaledHeight;
          const overheadY = effectiveOverhead ? startY + scaledHeight * overheadRatio : topY;
          const underlightY = hasUnderlight ? (startY + scaledHeight - scaledHeight * underlightRatio) : bottomY;
          const mainTopY = overheadY;
          const mainBottomY = underlightY;
          const x = startX + scaledWidth + dimOffset / 4 + 3; // Place closer to the frame, shifted 3px to the right
          
          return (
            <>
              {/* Overhead height measurement (only when overhead is active) */}
              {effectiveOverhead && (
              <g>
                {/* segment line */}
                <line
                  x1={x}
                    y1={topY}
                  x2={x}
                    y2={overheadY}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* top tick */}
                <line
                  x1={x - 5}
                    y1={topY}
                  x2={x + 5}
                    y2={topY}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* bottom tick */}
                <line
                  x1={x - 5}
                    y1={overheadY}
                  x2={x + 5}
                    y2={overheadY}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* Editable label */}
                <foreignObject
                  x={x + 8}
                    y={(topY + overheadY) / 2 - 11}
                  width={partDimLabelWidth}
                  height={dimLabelHeight}
                >
                  <input
                    type="number"
                    min="150"
                      max={Math.floor(safeHeight / 2)}
                    value={overheadHeightInput}
                    onChange={(e) => {
                      setOverheadHeightInput(e.target.value);
                    }}
                    onBlur={() => {
                      if (!onOverheadHeightChange) return;
                      const parsed = parseInt(overheadHeightInput, 10);
                      if (!Number.isNaN(parsed)) {
                          // Clamp to min 150, max height/2
                          const maxOverhead = Math.floor(safeHeight / 2);
                          const clamped = Math.max(150, Math.min(maxOverhead, parsed));
                        onOverheadHeightChange(clamped);
                        setOverheadHeightInput(String(clamped));
                      } else {
                        // Reset to current overhead height if input was invalid/empty
                        setOverheadHeightInput(String(overheadHeightMm));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      fontSize: `${dimFontSizeSmall}px`,
                      fontWeight: 500,
                      color: '#4A4A4A',
                      outline: 'none',
                      padding: 0,
                      margin: 0,
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield',
                    }}
                  />
                </foreignObject>
              </g>
              )}
              
              {/* Main window height (middle band when both overhead and underlight, or below overhead only) */}
              {effectiveOverhead && (
              <g>
                {/* segment line */}
                <line
                  x1={x}
                  y1={mainTopY}
                  x2={x}
                  y2={mainBottomY}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* top tick */}
                <line
                  x1={x - 5}
                  y1={mainTopY}
                  x2={x + 5}
                  y2={mainTopY}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* bottom tick */}
                <line
                  x1={x - 5}
                  y1={mainBottomY}
                  x2={x + 5}
                  y2={mainBottomY}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* Read-only label */}
                <foreignObject
                  x={x + 8}
                  y={(mainTopY + mainBottomY) / 2 - 11}
                  width={partDimLabelWidth}
                  height={dimLabelHeight}
                >
                  <input
                    type="number"
                    value={mainWindowHeightMm}
                    readOnly
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      fontSize: `${dimFontSizeSmall}px`,
                      fontWeight: 500,
                      color: '#4A4A4A',
                      outline: 'none',
                      padding: 0,
                      margin: 0,
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield',
                    }}
                  />
                </foreignObject>
              </g>
              )}
            </>
          );
        })()}
        
        {/* Underlight height vertical dimension (only when underlight is active) */}
        {hasUnderlight && (() => {
          const underlightHeightMm = underlightHeight;
          const dividerY = startY + scaledHeight - scaledHeight * underlightRatio;
          // Use the same vertical ruler position as overhead so they share the same column
          const x = startX + scaledWidth + dimOffset / 4 + 3;
          const totalHeightPx = startY + scaledHeight;
          
          return (
            <>
              {/* Main window height when underlight only (no overhead) */}
              {!effectiveOverhead && (
                <>
                  {/* Main window height measurement (top segment) */}
                  <g>
                    <line
                      x1={x}
                      y1={startY}
                      x2={x}
                      y2={dividerY}
                      stroke="#4A4A4A"
                      strokeWidth="1"
                    />
                    <line
                      x1={x - 5}
                      y1={startY}
                      x2={x + 5}
                      y2={startY}
                      stroke="#4A4A4A"
                      strokeWidth="1"
                    />
                    <line
                      x1={x - 5}
                      y1={dividerY}
                      x2={x + 5}
                      y2={dividerY}
                      stroke="#4A4A4A"
                      strokeWidth="1"
                    />
                    <foreignObject
                      x={x + 8}
                      y={(startY + dividerY) / 2 - dimLabelHeight / 2}
                      width={partDimLabelWidth}
                      height={dimLabelHeight}
                    >
                      <input
                        type="number"
                        value={height - underlightHeightMm}
                        readOnly
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          background: 'transparent',
                          textAlign: 'left',
                          fontSize: `${dimFontSizeSmall}px`,
                          fontWeight: 500,
                          color: '#4A4A4A',
                          outline: 'none',
                          padding: 0,
                          margin: 0,
                          WebkitAppearance: 'none',
                          MozAppearance: 'textfield',
                        }}
                      />
                    </foreignObject>
                  </g>
                </>
              )}

              {/* Underlight height measurement (bottom segment with editable input) */}
              <g>
                {/* segment line from divider down to bottom of frame */}
                <line
                  x1={x}
                  y1={dividerY}
                  x2={x}
                  y2={totalHeightPx}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* top tick */}
                <line
                  x1={x - 5}
                  y1={dividerY}
                  x2={x + 5}
                  y2={dividerY}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* bottom tick */}
                <line
                  x1={x - 5}
                  y1={totalHeightPx}
                  x2={x + 5}
                  y2={totalHeightPx}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* Editable label for underlight height */}
                <foreignObject
                  x={x + 8}
                  y={(dividerY + totalHeightPx) / 2 - 11}
                  width={partDimLabelWidth}
                  height={dimLabelHeight}
                >
                  <input
                    type="number"
                    min="150"
                    max={Math.floor(safeHeight / 2)}
                    value={underlightHeightInput}
                    onChange={(e) => setUnderlightHeightInput(e.target.value)}
                    onBlur={() => {
                      if (!onUnderlightHeightChange) return;
                      const parsed = parseInt(underlightHeightInput, 10);
                      if (Number.isNaN(parsed)) {
                        setUnderlightHeightInput(String(underlightHeightMm));
                        return;
                      }
                      const maxUnder = Math.floor(safeHeight / 2);
                      const clamped = Math.max(150, Math.min(maxUnder, parsed));
                      onUnderlightHeightChange(clamped);
                      setUnderlightHeightInput(String(clamped));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      fontSize: `${dimFontSizeSmall}px`,
                      fontWeight: 500,
                      color: '#4A4A4A',
                      outline: 'none',
                      padding: 0,
                      margin: 0,
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield',
                    }}
                  />
                </foreignObject>
              </g>
            </>
          );
        })()}
        
        {/* Per-underlight-part horizontal dimensions (only when underlight is active and multiple underlight parts) */}
        {hasUnderlight && underlightParts > 1 &&
          Array.from({ length: underlightParts }, (_, index) => {
            const startAccum = normalizedUnderlightPartWidths
              .slice(0, index)
              .reduce((sum, w) => sum + w, 0);
            const underlightPartWidthMm = normalizedUnderlightPartWidths[index];
            const endAccum = startAccum + underlightPartWidthMm;
            const segStart = glassX + (startAccum / totalUnderlightPartWidth) * glassWidth;
            const segEnd = glassX + (endAccum / totalUnderlightPartWidth) * glassWidth;
            // Place above the main part sub-rulers (when parts > 1) with same spacing.
            // Main part sub-rulers are at: startY + scaledHeight + dimOffset / 4 + dimStackOffset
            // When parts === 1 there is no main-part sub-ruler — sit at that tier instead
            // so labels stay below the frame (not inside the bottom rail).
            const mainPartSubRulerY = startY + scaledHeight + dimOffset / 4 + dimStackOffset;
            const spacingBetweenRulers = dimOffset * 3 / 4;
            const underlightY =
              parts > 1
                ? mainPartSubRulerY - spacingBetweenRulers
                : mainPartSubRulerY;
            return (
              <g key={`underlight-part-dim-${index}`}>
                {/* segment line */}
                <line
                  x1={segStart}
                  y1={underlightY}
                  x2={segEnd}
                  y2={underlightY}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* left tick */}
                <line
                  x1={segStart}
                  y1={underlightY - 5}
                  x2={segStart}
                  y2={underlightY + 5}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* right tick */}
                <line
                  x1={segEnd}
                  y1={underlightY - 5}
                  x2={segEnd}
                  y2={underlightY + 5}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* Editable label */}
                <foreignObject
                  x={(segStart + segEnd) / 2 - 25}
                  y={underlightY - 4}
                  width={partDimLabelWidth}
                  height={dimLabelHeight}
                >
                  <input
                    type="number"
                    value={inputUnderlightPartWidths[index] ?? underlightPartWidthMm}
                    onChange={(e) => {
                      const newVal = e.target.value;
                      setInputUnderlightPartWidths((prev) => {
                        const next = [...prev];
                        next[index] = newVal;
                        return next;
                      });
                    }}
                    onBlur={() => {
                      if (!onUnderlightPartWidthChange) return;
                      const parsed = parseInt(inputUnderlightPartWidths[index], 10);
                      if (!Number.isNaN(parsed)) {
                        onUnderlightPartWidthChange(index, parsed);
                      } else {
                        // reset to current width if input was invalid/empty
                        setInputUnderlightPartWidths((prev) => {
                          const next = [...prev];
                          next[index] = String(underlightPartWidthMm);
                          return next;
                        });
                      }
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'center',
                      fontSize: `${dimFontSizeSmall}px`,
                      fontWeight: 500,
                      color: '#4A4A4A',
                      outline: 'none',
                      padding: 0,
                      margin: 0,
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield',
                    }}
                  />
                </foreignObject>
              </g>
            );
          })}

        {/* Screen electrically operated - mesh pattern overlay on all glass areas */}
        {screenElectricallyOperated === true && (() => {
          const screenColorData = getColorByRAL(screenColor || '7016');
          const screenColorHex = screenColorData?.hex || '#3D3D3D';
          
          return (
            <g pointerEvents="none">
              {/* Apply colored mesh pattern to main glass area */}
              <rect
                x={glassX}
                y={glassY}
                width={glassWidth}
                height={glassHeight}
                fill="url(#screen-mesh-pattern)"
                opacity="0.6"
              />
              <rect
                x={glassX}
                y={glassY}
                width={glassWidth}
                height={glassHeight}
                fill={screenColorHex}
                opacity="0.25"
              />
              
              {/* Apply colored mesh pattern to overhead glass area if present */}
              {effectiveOverhead && (
                <>
                  <rect
                    x={glassX}
                    y={startY + glassInset}
                    width={glassWidth}
                    height={scaledHeight * overheadRatio - glassInset}
                    fill="url(#screen-mesh-pattern)"
                    opacity="0.6"
                  />
                  <rect
                    x={glassX}
                    y={startY + glassInset}
                    width={glassWidth}
                    height={scaledHeight * overheadRatio - glassInset}
                    fill={screenColorHex}
                    opacity="0.25"
                  />
                </>
              )}
              
              {/* Apply colored mesh pattern to underlight glass area if present */}
              {hasUnderlight && (() => {
                const dividerY = startY + scaledHeight - scaledHeight * underlightRatio;
                const underlightTopY = dividerY + mullionThickness / 2;
                const underlightBottomY = startY + scaledHeight - glassInset;
                const underlightHeight = underlightBottomY - underlightTopY;
                return (
                  <>
                    <rect
                      x={glassX}
                      y={underlightTopY}
                      width={glassWidth}
                      height={underlightHeight}
                      fill="url(#screen-mesh-pattern)"
                      opacity="0.6"
                    />
                    <rect
                      x={glassX}
                      y={underlightTopY}
                      width={glassWidth}
                      height={underlightHeight}
                      fill={screenColorHex}
                      opacity="0.25"
                    />
                  </>
                );
              })()}
              
              {/* Small icon indicator on top-right corner of frame */}
              <g transform={`translate(${startX + scaledWidth - frameThickness * 1.5}, ${startY + frameThickness * 0.8})`}>
                <circle cx="0" cy="0" r={frameThickness * 0.3} fill="#4CAF50" opacity="0.9" />
                <text x="0" y="0" textAnchor="middle" dominantBaseline="central" fontSize={frameThickness * 0.4} fill="white" fontWeight="bold">S</text>
              </g>
            </g>
          );
        })()}

        {/* Ramp dorpel: /__________\ shape */}
        {rampDorpel !== 'no-ramp-dorpel' && (() => {
          // Extract height from ramp dorpel type (e.g., 'ramp-dorpel-30mm' -> 30)
          const rampHeightMm = parseInt(rampDorpel.replace('ramp-dorpel-', '').replace('mm', '')) || 30;
          const rampHeightPx = rampHeightMm * scale;
          const frameBottomY = startY + scaledHeight;
          
          // Extend dorpel to reach wood joints at frame corners and beyond
          // Wood joints are at the outer edges of the frame, extend beyond them
          const frameThickness = 54 * scale;
          const widthExtension = frameThickness * 0.5; // Extend beyond wood joints
          const leftWoodJointX = startX - widthExtension; // Left wood joint position extended outward
          const rightWoodJointX = startX + scaledWidth + widthExtension; // Right wood joint position extended outward
          
          // Position dorpel below the frame (not at the frame bottom)
          // The dorpel should start below the frame, with the top of dorpel at frame bottom
          const dorpelTopY = frameBottomY; // Top of dorpel aligns with frame bottom
          const dorpelBottomY = frameBottomY + rampHeightPx; // Bottom of dorpel extends below
          
          // Left edge: / slanted 45 degrees to the right
          // Starts at frame bottom-left (at wood joint), goes up and to the right at 45 degrees
          // For 45 degrees: equal horizontal and vertical distance
          const leftOuterX = leftWoodJointX + rampHeightPx; // Move right by rampHeightPx
          const leftOuterY = dorpelTopY; // Top of dorpel (at frame bottom)
          
          // Right edge: \ slanted 45 degrees to the left (opposite of left edge)
          // Starts at top-right point, goes down and to the left at 45 degrees
          // For 45 degrees: equal horizontal and vertical distance
          const rightOuterX = rightWoodJointX - rampHeightPx; // Move left by rampHeightPx
          const rightOuterY = dorpelTopY; // Top of dorpel (at frame bottom)
          
          // Frame connection points - extend to wood joints at frame corners
          // Bottom of dorpel extends below frame, connecting at wood joint positions
          const leftFrameX = leftWoodJointX; // Left wood joint position
          const rightFrameX = rightWoodJointX; // Right wood joint position
          const leftFrameY = dorpelBottomY; // Bottom of dorpel
          const rightFrameY = dorpelBottomY; // Bottom of dorpel
          
          // Complete dorpel path: /__________\
          // Path: start at frame bottom-left -> go up/right at 45° to leftOuter -> horizontal to rightOuter -> down/left at 45° to frame bottom-right -> close
          const dorpelPath = `M ${leftFrameX} ${leftFrameY} 
                               L ${leftOuterX} ${leftOuterY} 
                               L ${rightOuterX} ${rightOuterY} 
                               L ${rightFrameX} ${rightFrameY} 
                               Z`;
          
          return (
            <g>
              {/* Complete dorpel shape: /__________\ */}
              <path
                d={dorpelPath}
                fill="#5A6578"
                stroke={externalFrameColor}
                strokeWidth="1"
              />
              
              {/* Left edge diagonal line: / - 45 degrees to the right (up and right) */}
              <line
                x1={leftFrameX}
                y1={leftFrameY}
                x2={leftOuterX}
                y2={leftOuterY}
                stroke={frameEdgeStroke}
                strokeWidth="2"
                opacity="1"
              />
              
              {/* Top horizontal edge: __________ - connecting left and right outer points */}
              <line
                x1={leftOuterX}
                y1={leftOuterY}
                x2={rightOuterX}
                y2={rightOuterY}
                stroke={frameEdgeStroke}
                strokeWidth="2"
                opacity="1"
              />
              
              {/* Right edge diagonal line: \ - from top-right outer point down to bottom-right frame corner */}
              <line
                x1={rightOuterX}
                y1={rightOuterY}
                x2={rightFrameX}
                y2={rightFrameY}
                stroke={frameEdgeStroke}
                strokeWidth="2"
                opacity="1"
              />
              
              {/* Bottom edge at frame */}
              <line
                x1={leftFrameX}
                y1={leftFrameY}
                x2={rightFrameX}
                y2={rightFrameY}
                stroke="#FFFFFF"
                strokeWidth="0.5"
                opacity="0.5"
              />
            </g>
          );
        })()}
        
      </svg>
      
      {/* Window Type Settings Dialog - Shows for main parts, overhead parts, or underlight parts */}
      {dialogType !== null && (
        <Dialog open={settingsOpen} onOpenChange={(open) => {
          setSettingsOpen(open);
          if (!open) {
            // Reset all selections when closing
            setSelectedPartIndex(null);
            setSelectedOverheadPartIndex(null);
            setSelectedUnderlightPartIndex(null);
            setSelectedPartOverheadIndex(null);
            setSelectedPartUnderlightIndex(null);
            setIsOverheadPart(false);
            setIsUnderlightPart(false);
            setDialogType(null);
          }
        }}>
          {(() => {
            // Existing part/overhead/underlight dialogs
            // Determine if fixed-panel is selected to adjust modal height
            let hasColorOption = false;
            if (dialogType === 'overhead' && selectedOverheadPartIndex !== null) {
              const index = selectedOverheadPartIndex;
              const currentWindowTypeId = overheadPartWindowTypes?.[index] || 'fixed-sash';
              hasColorOption = currentWindowTypeId === 'fixed-panel';
            } else if (dialogType === 'underlight' && selectedUnderlightPartIndex !== null) {
              const index = selectedUnderlightPartIndex;
              const underPartsCount = underlightParts || 1;
              const effectiveIndex = Math.min(index, underPartsCount - 1);
              const currentUnderlightTypes = getNormalizedUnderlightPartWindowTypes(underPartsCount);
              const currentWindowTypeId = currentUnderlightTypes[effectiveIndex] || 'fixed-sash';
              hasColorOption = currentWindowTypeId === 'fixed-panel';
            } else if (dialogType === 'main' && selectedPartIndex !== null) {
              const index = selectedPartIndex;
              const currentWindowTypeId = parts === 1 
                ? (partWindowTypes?.[index] || windowType?.id || 'fixed-sash')
                : (partWindowTypes?.[index] || 'fixed-sash');
              hasColorOption = currentWindowTypeId === 'fixed-panel';
            } else if (dialogType === 'part-overhead' && selectedPartOverheadIndex !== null) {
              const index = selectedPartOverheadIndex;
              const bandConfig = getPartOverheadBandConfig(index);
              const subIndex = selectedPartOverheadSubIndex ?? 0;
              const currentWindowTypeId = bandConfig.windowTypes[subIndex] ?? partOverheadWindowTypes[index] ?? 'fixed-sash';
              hasColorOption = currentWindowTypeId === 'fixed-panel';
            } else if (dialogType === 'part-underlight' && selectedPartUnderlightIndex !== null) {
              const index = selectedPartUnderlightIndex;
              const bandConfig = getPartUnderlightBandConfig(index);
              const subIndex = selectedPartUnderlightSubIndex ?? 0;
              const currentWindowTypeId = bandConfig.windowTypes[subIndex] ?? partUnderlightWindowTypes[index] ?? 'fixed-sash';
              hasColorOption = currentWindowTypeId === 'fixed-panel';
            }

            return (
              <DialogContent className="max-w-md" style={{ 
                maxHeight: '85vh', 
                overflowY: 'auto',
                height: 'auto'
              }}>
            <DialogHeader>
              <DialogTitle>
                    {dialogType === 'part-overhead' && selectedPartOverheadIndex !== null
                      ? (() => {
                          const bandConfig = getPartOverheadBandConfig(selectedPartOverheadIndex);
                          const subIndex = selectedPartOverheadSubIndex ?? 0;
                          return bandConfig.count > 1
                            ? t('drawing.overheadSectionSettings', { part: selectedPartOverheadIndex + 1, section: subIndex + 1 })
                            : t('drawing.overheadSettings');
                        })()
                      : dialogType === 'part-underlight' && selectedPartUnderlightIndex !== null
                      ? (() => {
                          const bandConfig = getPartUnderlightBandConfig(selectedPartUnderlightIndex);
                          const subIndex = selectedPartUnderlightSubIndex ?? 0;
                          return bandConfig.count > 1
                            ? t('drawing.underlightSectionSettings', { part: selectedPartUnderlightIndex + 1, section: subIndex + 1 })
                            : t('drawing.underlightSettings');
                        })()
                      : dialogType === 'overhead' && selectedOverheadPartIndex !== null
                      ? t('drawing.overheadPartSettings', { number: selectedOverheadPartIndex + 1 })
                      : dialogType === 'underlight' && selectedUnderlightPartIndex !== null
                        ? t('drawing.underlightPartSettings', { number: selectedUnderlightPartIndex + 1 })
                        : dialogType === 'main' && selectedPartIndex !== null
                          ? t('drawing.partSettings', { number: selectedPartIndex + 1 })
                          : t('drawing.settings')}
              </DialogTitle>
              <DialogDescription>
                    {dialogType === 'part-overhead' && selectedPartOverheadIndex !== null
                      ? t('drawing.configureWindowTypeForOverhead')
                      : dialogType === 'part-underlight' && selectedPartUnderlightIndex !== null
                      ? t('drawing.configureWindowTypeForUnderlight')
                      : dialogType === 'overhead' && selectedOverheadPartIndex !== null
                      ? t('drawing.configureOverheadPart', { number: selectedOverheadPartIndex + 1 })
                      : dialogType === 'underlight' && selectedUnderlightPartIndex !== null
                        ? t('drawing.configureUnderlightPart', { number: selectedUnderlightPartIndex + 1 })
                        : dialogType === 'main' && selectedPartIndex !== null
                          ? t('drawing.configurePart', { number: selectedPartIndex + 1 })
                          : t('drawing.configureWindowType')}
              </DialogDescription>
            </DialogHeader>
                <div className="space-y-3 mt-2">
              {(() => {
                    // Use dialogType to determine which dialog content to show
                    if (dialogType === 'overhead' && selectedOverheadPartIndex !== null) {
                  const index = selectedOverheadPartIndex;
                  const currentWindowTypeId = overheadPartWindowTypes?.[index] || 'fixed-sash';
                  const currentWindowType = raamtypes.find(r => r.id === currentWindowTypeId);
                  
                  return (
                    <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor={`overhead-part-${index}`} className="text-sm font-semibold text-gray-700">
                          {t('drawing.windowType')}
                      </Label>
                      <Select
                        value={currentWindowTypeId}
                        onValueChange={(value) => {
                          onOverheadPartWindowTypeChange(index, value);
                            // Only close modal if not selecting fixed-panel (so user can select color)
                            if (value !== 'fixed-panel') {
                              setSettingsOpen(false);
                            }
                        }}
                      >
                        <SelectTrigger id={`overhead-part-${index}`} className="w-full">
                            <SelectValue placeholder={t('drawing.selectWindowType')}>
                              {currentWindowType ? translateWindowTypeName(currentWindowTypeId) : t('drawing.selectWindowType')}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {raamtypes.map(type => (
                            <SelectItem key={type.id} value={type.id}>
                              <div className="flex items-center space-x-2">
                                {type.image && (
                                  <img 
                                    src={type.image} 
                                      alt={translateWindowTypeName(type.id)}
                                    className="w-6 h-6 object-contain"
                                  />
                                )}
                                  <span>{translateWindowTypeName(type.id)}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      </div>

                      {/* Panel Color option for overhead - only when Fixed Panel is selected */}
                      {currentWindowTypeId === 'fixed-panel' && (
                        <>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-gray-700">
                              {t('drawing.panelColor')}
                            </Label>
                            <ColorPicker
                              selectedColor={panelOverheadColors[index] || 'anthracite'}
                              onColorChange={(colorId) => {
                                updatePanelOverheadColor(index, colorId);
                              }}
                              showSearch
                              inline
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-gray-700">
                              {t('drawing.panelLines')}
                            </Label>
                            <Select
                              value={panelOverheadLineTypes[index] || 'none'}
                              onValueChange={(value) => {
                                updatePanelOverheadLineType(index, value);
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder={t('drawing.selectLineType')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">{t('drawing.noLines')}</SelectItem>
                                <SelectItem value="horizontal">{t('drawing.horizontalLines')}</SelectItem>
                                <SelectItem value="vertical">{t('drawing.verticalLines')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}

                      {/* Ventilator option for overhead - only when Fixed Sash is selected */}
                      {currentWindowTypeId === 'fixed-sash' && (
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-700">
                            {t('drawing.ventilator')}
                          </Label>
                          <Select
                            value={ventilatorOverheadParts[index] ? 'with' : 'none'}
                            onValueChange={(value) => {
                              setVentilatorOverheadParts((prev) => {
                                const next = [...prev];
                                next[index] = value === 'with';
                                return next;
                              });
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('drawing.selectVentilatorOption')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">{t('drawing.noVentilator')}</SelectItem>
                              <SelectItem value="with">{t('drawing.withVentilator')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                } else if (dialogType === 'underlight' && selectedUnderlightPartIndex !== null) {
                  // Underlight has its own window types per part, just like overhead
                  const index = selectedUnderlightPartIndex;
                  const underPartsCount = underlightParts || 1;
                  const effectiveIndex = Math.min(index, underPartsCount - 1);
                  const currentUnderlightTypes = getNormalizedUnderlightPartWindowTypes(underPartsCount);
                  const currentWindowTypeId = currentUnderlightTypes[effectiveIndex] || 'fixed-sash';
                  const currentWindowType = raamtypes.find(r => r.id === currentWindowTypeId);

                  return (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor={`underlight-part-${effectiveIndex}`} className="text-sm font-semibold text-gray-700">
                          {t('drawing.windowType')}
                        </Label>
                        <Select
                          value={currentWindowTypeId}
                          onValueChange={(value) => {
                            onUnderlightPartWindowTypeChange?.(effectiveIndex, value);
                            // Only close modal if not selecting fixed-panel (so user can select color)
                            if (value !== 'fixed-panel') {
                              setSettingsOpen(false);
                            }
                          }}
                        >
                          <SelectTrigger id={`underlight-part-${effectiveIndex}`} className="w-full">
                            <SelectValue placeholder={t('drawing.selectWindowType')}>
                              {currentWindowType ? translateWindowTypeName(currentWindowTypeId) : t('drawing.selectWindowType')}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {raamtypes.map(type => (
                              <SelectItem key={type.id} value={type.id}>
                                <div className="flex items-center space-x-2">
                                  {type.image && (
                                    <img 
                                      src={type.image} 
                                      alt={translateWindowTypeName(type.id)}
                                      className="w-6 h-6 object-contain"
                                    />
                                  )}
                                  <span>{translateWindowTypeName(type.id)}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Panel Color option for underlight - only when Fixed Panel is selected */}
                      {currentWindowTypeId === 'fixed-panel' && (
                        <>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-gray-700">
                              {t('drawing.panelColor')}
                            </Label>
                            <ColorPicker
                              selectedColor={panelUnderlightColors[effectiveIndex] || 'anthracite'}
                              onColorChange={(colorId) => {
                                updatePanelUnderlightColor(effectiveIndex, colorId);
                              }}
                              showSearch
                              inline
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-gray-700">
                              {t('drawing.panelLines')}
                            </Label>
                            <Select
                              value={panelUnderlightLineTypes[effectiveIndex] || 'none'}
                              onValueChange={(value) => {
                                updatePanelUnderlightLineType(effectiveIndex, value);
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder={t('drawing.selectLineType')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">{t('drawing.noLines')}</SelectItem>
                                <SelectItem value="horizontal">{t('drawing.horizontalLines')}</SelectItem>
                                <SelectItem value="vertical">{t('drawing.verticalLines')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}

                      {/* Ventilator option for underlight - only when Fixed Sash is selected */}
                      {currentWindowTypeId === 'fixed-sash' && (
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-700">
                            {t('drawing.ventilator')}
                          </Label>
                          <Select
                            value={ventilatorUnderlightParts[effectiveIndex] ? 'with' : 'none'}
                            onValueChange={(value) => {
                              setVentilatorUnderlightParts((prev) => {
                                const next = [...prev];
                                next[effectiveIndex] = value === 'with';
                                return next;
                              });
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('drawing.selectVentilatorOption')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">{t('drawing.noVentilator')}</SelectItem>
                              <SelectItem value="with">{t('drawing.withVentilator')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                } else if (dialogType === 'part-overhead' && selectedPartOverheadIndex !== null) {
                  const index = selectedPartOverheadIndex;
                  const bandConfig = getPartOverheadBandConfig(index);
                  const subIndex = selectedPartOverheadSubIndex ?? 0;
                  const currentWindowTypeId = bandConfig.windowTypes[subIndex] ?? partOverheadWindowTypes[index] ?? 'fixed-sash';
                  const currentWindowType = raamtypes.find(r => r.id === currentWindowTypeId);

                  return (
                    <div className="space-y-3">
                      {partSectionCountPicker(
                        'overhead',
                        index,
                        bandConfig.count,
                        onPartOverheadSectionCountChange
                      )}
                      <div className="space-y-2">
                        <Label htmlFor={`part-overhead-${index}-${subIndex}`} className="text-sm font-semibold text-gray-700">
                          {bandConfig.count > 1
                            ? t('drawing.windowTypeSection', { section: subIndex + 1 })
                            : t('drawing.windowType')}
                        </Label>
                        <Select
                          value={currentWindowTypeId}
                          onValueChange={(value) => {
                            setPartOverheadSectionWindowType(index, subIndex, value);
                            if (value !== 'fixed-panel') {
                              setSettingsOpen(false);
                            }
                          }}
                        >
                          <SelectTrigger id={`part-overhead-${index}-${subIndex}`} className="w-full">
                            <SelectValue placeholder={t('drawing.selectWindowType')}>
                              {currentWindowType ? translateWindowTypeName(currentWindowTypeId) : t('drawing.selectWindowType')}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {raamtypes.map(type => (
                              <SelectItem key={type.id} value={type.id}>
                                <div className="flex items-center space-x-2">
                                  {type.image && (
                                    <img src={type.image} alt={translateWindowTypeName(type.id)} className="w-6 h-6 object-contain" />
                                  )}
                                  <span>{translateWindowTypeName(type.id)}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {currentWindowTypeId === 'fixed-panel' && (
                        <>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-gray-700">
                              {t('drawing.panelColor')}
                            </Label>
                            <ColorPicker
                              selectedColor={panelOverheadColors[index] || 'anthracite'}
                              onColorChange={(colorId) => {
                                updatePanelOverheadColor(index, colorId);
                              }}
                              showSearch
                              inline
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-gray-700">
                              {t('drawing.panelLines')}
                            </Label>
                            <Select
                              value={panelOverheadLineTypes[index] || 'none'}
                              onValueChange={(value) => {
                                updatePanelOverheadLineType(index, value);
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder={t('drawing.selectLineType')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">{t('drawing.noLines')}</SelectItem>
                                <SelectItem value="horizontal">{t('drawing.horizontalLines')}</SelectItem>
                                <SelectItem value="vertical">{t('drawing.verticalLines')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}

                      {currentWindowTypeId === 'fixed-sash' && bandConfig.count === 1 && (
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-700">
                            {t('drawing.ventilator')}
                          </Label>
                          <Select
                            value={ventilatorPartOverhead[index] ? 'with' : 'none'}
                            onValueChange={(value) => {
                              setVentilatorPartOverhead(index, value === 'with');
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('drawing.selectVentilatorOption')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">{t('drawing.noVentilator')}</SelectItem>
                              <SelectItem value="with">{t('drawing.withVentilator')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                } else if (dialogType === 'part-underlight' && selectedPartUnderlightIndex !== null) {
                  const index = selectedPartUnderlightIndex;
                  const bandConfig = getPartUnderlightBandConfig(index);
                  const subIndex = selectedPartUnderlightSubIndex ?? 0;
                  const currentWindowTypeId = bandConfig.windowTypes[subIndex] ?? partUnderlightWindowTypes[index] ?? 'fixed-sash';
                  const currentWindowType = raamtypes.find(r => r.id === currentWindowTypeId);

                  return (
                    <div className="space-y-3">
                      {partSectionCountPicker(
                        'underlight',
                        index,
                        bandConfig.count,
                        onPartUnderlightSectionCountChange
                      )}
                      <div className="space-y-2">
                        <Label htmlFor={`part-underlight-${index}-${subIndex}`} className="text-sm font-semibold text-gray-700">
                          {bandConfig.count > 1
                            ? t('drawing.windowTypeSection', { section: subIndex + 1 })
                            : t('drawing.windowType')}
                        </Label>
                        <Select
                          value={currentWindowTypeId}
                          onValueChange={(value) => {
                            setPartUnderlightSectionWindowType(index, subIndex, value);
                            if (value !== 'fixed-panel') {
                              setSettingsOpen(false);
                            }
                          }}
                        >
                          <SelectTrigger id={`part-underlight-${index}-${subIndex}`} className="w-full">
                            <SelectValue placeholder={t('drawing.selectWindowType')}>
                              {currentWindowType ? translateWindowTypeName(currentWindowTypeId) : t('drawing.selectWindowType')}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {raamtypes.map(type => (
                              <SelectItem key={type.id} value={type.id}>
                                <div className="flex items-center space-x-2">
                                  {type.image && (
                                    <img src={type.image} alt={translateWindowTypeName(type.id)} className="w-6 h-6 object-contain" />
                                  )}
                                  <span>{translateWindowTypeName(type.id)}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {currentWindowTypeId === 'fixed-panel' && (
                        <>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-gray-700">
                              {t('drawing.panelColor')}
                            </Label>
                            <ColorPicker
                              selectedColor={panelUnderlightColors[index] || 'anthracite'}
                              onColorChange={(colorId) => {
                                updatePanelUnderlightColor(index, colorId);
                              }}
                              showSearch
                              inline
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-gray-700">
                              {t('drawing.panelLines')}
                            </Label>
                            <Select
                              value={panelUnderlightLineTypes[index] || 'none'}
                              onValueChange={(value) => {
                                updatePanelUnderlightLineType(index, value);
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder={t('drawing.selectLineType')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">{t('drawing.noLines')}</SelectItem>
                                <SelectItem value="horizontal">{t('drawing.horizontalLines')}</SelectItem>
                                <SelectItem value="vertical">{t('drawing.verticalLines')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}

                      {currentWindowTypeId === 'fixed-sash' && bandConfig.count === 1 && (
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-700">
                            {t('drawing.ventilator')}
                          </Label>
                          <Select
                            value={ventilatorPartUnderlight[index] ? 'with' : 'none'}
                            onValueChange={(value) => {
                              setVentilatorPartUnderlight(index, value === 'with');
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('drawing.selectVentilatorOption')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">{t('drawing.noVentilator')}</SelectItem>
                              <SelectItem value="with">{t('drawing.withVentilator')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                } else if (dialogType === 'main' && selectedPartIndex !== null) {
                const index = selectedPartIndex;
                  // For single part, check partWindowTypes[0] first, then fall back to windowType?.id
                  const currentWindowTypeId = parts === 1 
                    ? (partWindowTypes?.[index] || windowType?.id || 'fixed-sash')
                    : (partWindowTypes?.[index] || 'fixed-sash');
                const currentWindowType = raamtypes.find(r => r.id === currentWindowTypeId);
                
                return (
                  <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor={`part-${index}`} className="text-sm font-semibold text-gray-700">
                        {t('drawing.windowType')}
                    </Label>
                    <Select
                      value={currentWindowTypeId}
                      onValueChange={(value) => {
                        onPartWindowTypeChange(index, value);
                          // Only close modal if not selecting fixed-panel (so user can select color)
                          if (value !== 'fixed-panel') {
                            setSettingsOpen(false);
                          }
                      }}
                    >
                      <SelectTrigger id={`part-${index}`} className="w-full">
                          <SelectValue placeholder={t('drawing.selectWindowType')}>
                            {currentWindowType ? translateWindowTypeName(currentWindowTypeId) : t('drawing.selectWindowType')}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {raamtypes.map(type => (
                          <SelectItem key={type.id} value={type.id}>
                            <div className="flex items-center space-x-2">
                              {type.image && (
                                <img 
                                  src={type.image} 
                                    alt={translateWindowTypeName(type.id)}
                                  className="w-6 h-6 object-contain"
                                />
                              )}
                                <span>{translateWindowTypeName(type.id)}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    </div>

                    {/* Panel Color option - only when Fixed Panel is selected */}
                    {currentWindowTypeId === 'fixed-panel' && (
                      <>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-700">
                            {t('drawing.panelColor')}
                          </Label>
                          <ColorPicker
                            selectedColor={panelColors[index] || 'anthracite'}
                            onColorChange={(colorId) => {
                              updatePanelColor(index, colorId);
                            }}
                            showSearch
                            inline
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-700">
                            {t('drawing.panelLines')}
                          </Label>
                          <Select
                            value={panelLineTypes[index] || 'none'}
                            onValueChange={(value) => {
                              updatePanelLineType(index, value);
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('drawing.selectLineType')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">{t('drawing.noLines')}</SelectItem>
                              <SelectItem value="horizontal">{t('drawing.horizontalLines')}</SelectItem>
                              <SelectItem value="vertical">{t('drawing.verticalLines')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}

                    {/* Ventilator option - only when Fixed Sash is selected */}
                    {currentWindowTypeId === 'fixed-sash' && (
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-gray-700">
                          {t('drawing.ventilator')}
                        </Label>
                        <Select
                          value={ventilatorParts[index] ? 'with' : 'none'}
                          onValueChange={(value) => {
                            setVentilatorParts((prev) => {
                              const next = [...prev];
                              next[index] = value === 'with';
                              return next;
                            });
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('drawing.selectVentilatorOption')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('drawing.noVentilator')}</SelectItem>
                            <SelectItem value="with">{t('drawing.withVentilator')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Overhead option - per-part (extra band in this column; also when global overhead is on) */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-gray-700">
                        {overhead === 'overhead' ? t('drawing.overheadInThisPart') : t('drawing.overhead')}
                      </Label>
                      <Select
                        value={overheadPerPart[index] ? 'with' : 'without'}
                        onValueChange={(value) => {
                          setOverheadPerPart((prev) => {
                            const next = [...prev];
                            next[index] = value === 'with';
                            return next;
                          });
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('drawing.selectOverheadOption')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="without">{t('drawing.withoutOverhead')}</SelectItem>
                          <SelectItem value="with">{t('drawing.withOverhead')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Per-part overhead window type - when overhead is enabled on this part */}
                    {overheadPerPart[index] && (
                      <>
                        {partSectionCountPicker(
                          'overhead',
                          index,
                          getPartOverheadBandConfig(index).count,
                          onPartOverheadSectionCountChange
                        )}
                        {getPartOverheadBandConfig(index).count === 1 && (
                      <div className="space-y-2">
                        <Label htmlFor={`part-${index}-overhead-type`} className="text-sm font-semibold text-gray-700">
                          {t('drawing.overheadWindowType')}
                        </Label>
                        <Select
                          value={getPartOverheadBandConfig(index).windowTypes[0] ?? partOverheadWindowTypes[index] ?? 'fixed-sash'}
                          onValueChange={(value) => {
                            setPartOverheadSectionWindowType(index, 0, value);
                          }}
                        >
                          <SelectTrigger id={`part-${index}-overhead-type`} className="w-full">
                            <SelectValue placeholder={t('drawing.selectWindowType')} />
                          </SelectTrigger>
                          <SelectContent>
                            {raamtypes.map(type => (
                              <SelectItem key={type.id} value={type.id}>
                                <div className="flex items-center space-x-2">
                                  {type.image && (
                                    <img src={type.image} alt={translateWindowTypeName(type.id)} className="w-6 h-6 object-contain" />
                                  )}
                                  <span>{translateWindowTypeName(type.id)}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                        )}
                        {getPartOverheadBandConfig(index).count > 1 && (
                          <p className="text-xs text-gray-500">{t('drawing.clickSectionToConfigure')}</p>
                        )}
                      </>
                    )}

                    {overheadPerPart[index] && (getPartOverheadBandConfig(index).windowTypes[0] ?? partOverheadWindowTypes[index]) === 'fixed-sash' && getPartOverheadBandConfig(index).count === 1 && (
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-gray-700">
                          {t('drawing.overheadVentilator')}
                        </Label>
                        <Select
                          value={ventilatorPartOverhead[index] ? 'with' : 'none'}
                          onValueChange={(value) => {
                            setVentilatorPartOverhead(index, value === 'with');
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('drawing.selectVentilatorOption')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('drawing.noVentilator')}</SelectItem>
                            <SelectItem value="with">{t('drawing.withVentilator')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Underlight option - per-part only when global underlight is off */}
                    {underlight !== 'underlight' && (
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-gray-700">
                        {t('drawing.underlight')}
                      </Label>
                      <Select
                        value={underlightPerPart[index] ? 'with' : 'without'}
                        onValueChange={(value) => {
                          setUnderlightPerPart((prev) => {
                            const next = [...prev];
                            next[index] = value === 'with';
                            return next;
                          });
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('drawing.selectUnderlightOption')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="without">{t('drawing.withoutUnderlight')}</SelectItem>
                          <SelectItem value="with">{t('drawing.withUnderlight')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    )}

                    {/* Per-part underlight window type - when underlight is enabled on this part (single section only) */}
                    {underlight !== 'underlight' && underlightPerPart[index] && (
                      <>
                        {partSectionCountPicker(
                          'underlight',
                          index,
                          getPartUnderlightBandConfig(index).count,
                          onPartUnderlightSectionCountChange
                        )}
                        {getPartUnderlightBandConfig(index).count === 1 && (
                      <div className="space-y-2">
                        <Label htmlFor={`part-${index}-underlight-type`} className="text-sm font-semibold text-gray-700">
                          {t('drawing.underlightWindowType')}
                        </Label>
                        <Select
                          value={partUnderlightWindowTypes[index] ?? 'fixed-sash'}
                          onValueChange={(value) => {
                            setPartUnderlightWindowType(index, value);
                          }}
                        >
                          <SelectTrigger id={`part-${index}-underlight-type`} className="w-full">
                            <SelectValue placeholder={t('drawing.selectWindowType')} />
                          </SelectTrigger>
                          <SelectContent>
                            {raamtypes.map(type => (
                              <SelectItem key={type.id} value={type.id}>
                                <div className="flex items-center space-x-2">
                                  {type.image && (
                                    <img src={type.image} alt={translateWindowTypeName(type.id)} className="w-6 h-6 object-contain" />
                                  )}
                                  <span>{translateWindowTypeName(type.id)}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                        )}
                        {getPartUnderlightBandConfig(index).count > 1 && (
                          <p className="text-xs text-gray-500">{t('drawing.clickSectionToConfigure')}</p>
                        )}
                      </>
                    )}

                    {underlightPerPart[index] && partUnderlightWindowTypes[index] === 'fixed-sash' && getPartUnderlightBandConfig(index).count === 1 && (
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-gray-700">
                          {t('drawing.underlightVentilator')}
                        </Label>
                        <Select
                          value={ventilatorPartUnderlight[index] ? 'with' : 'none'}
                          onValueChange={(value) => {
                            setVentilatorPartUnderlight(index, value === 'with');
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('drawing.selectVentilatorOption')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('drawing.noVentilator')}</SelectItem>
                            <SelectItem value="with">{t('drawing.withVentilator')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                );
                }
              })()}
            </div>
          </DialogContent>
            );
          })()}
        </Dialog>
      )}
    </div>
  );
};

export default Kozijnen;
